import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Calendar, DollarSign, Users, User, UserPlus, TrendingUp, CheckCircle, XCircle, Clock, MessageCircle, Edit2, Save, X, Wallet, MapPin, PartyPopper, Check, HelpCircle, Shield, Phone, Send, AlertCircle, Eye, ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Mail, Camera, FileText, History, LayoutGrid, List, Menu, CalendarDays, Building2, LogOut, Lock, Bell } from 'lucide-react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, addDoc, deleteDoc, or, onSnapshot, deleteField, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { authService } from '../../services/authService';
import { formatCurrency, formatDate, formatDateTime, formatTimeFromDate, formatTime, getRegistrationFeeLabel } from '../../utils/helpers';
import notificationService from '../../services/notificationService';
const { markNotificationAsReadAndDelete, requestNotificationPermission, processSquadPublishNotifications } = notificationService;
import Header, { ProfileModal, SwitchClubModal, JoinAnotherClubModal } from '../shared/Header';
import LumaBanner from '../shared/LumaBanner';
import SquadSelection from '../admin/SquadSelection';
import WhatsAppGroupMessenger from '../admin/WhatsAppGroupMessenger';
import AddGame from '../admin/AddGame';
import EditGame from '../admin/EditGame';
import MediaGalleryPreview from '../shared/MediaGalleryPreview';
import FeedbackButton from '../shared/FeedbackButton';
import PullToRefresh from 'react-simple-pull-to-refresh';
import PlayerVolunteerSection from './PlayerVolunteerSection';
import PWAInstallPrompt from '../shared/PWAInstallPrompt';
import JoinClubForm from './JoinClubForm';
// import MerchandiseSection from './MerchandiseSection';
import PlayerRegistrationForm from '../auth/PlayerRegistrationForm';
import RegistrationGuard from '../auth/RegistrationGuard';
import WaiverGuard from '../auth/WaiverGuard';
import PlayerMobileBottomNav from './shared/PlayerMobileBottomNav';
import MobileDuesView from './views/MobileDuesView';

// Duty labels for display
const DUTY_LABELS = {
  'ground_setup': '🏟️ Ground Setup',
  'refreshments': '🥤 Drinks & Snacks',
  'scoring': '📝 Scoring',
  'umpiring': '🧑‍⚖️ Umpiring',
  'first_aid': '🩹 First Aid',
  'equipment': '🎒 Equipment',
  'transport': '🚗 Transport',
  'photography': '📸 Photography'
};

// Late arrival fee
const LATE_FEE = 5;

// Expense Categories
const EXPENSE_LEVELS = {
  CLUB: 'club',
  TOURNAMENT: 'tournament',
  MATCH: 'match'
};

const TOURNAMENT_EXPENSE_TYPES = [
  'Tournament Fee',
  'Ground fee',
  'Tournament Balls',
  'Umpiring fee',
  'Umpiring assignment',
  'Lunch',
  'Snacks & Drinks',
  'Other'
];

const MATCH_EXPENSE_TYPES = [
  'Snacks & Drinks',
  'Lunch',
  'Umpiring Fee',
  'Umpiring assignment',
  'Other'
];

// --- Countdown Hook for Next Event Banner ---
const useCountdown = (targetDate, targetTime) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      // Construct target datetime string
      const dateStr = String(targetDate).split('T')[0];
      const timeStr = targetTime || '00:00';
      const targetDateTime = new Date(`${dateStr}T${timeStr}:00`);
      const now = new Date();

      const difference = targetDateTime - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        isPast: false
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, targetTime]);

  return timeLeft;
};

const NextEventBanner = ({ event, eventRsvps, handleEventRsvp, editingEventNote, setEditingEventNote, formatDate, allClubMembers }) => {
  const [showRsvps, setShowRsvps] = useState(false);
  const [showRsvpDetails, setShowRsvpDetails] = useState(false);
  if (!event) return null;

  const myRsvp = eventRsvps[event.id];
  const notRespondedUids = Object.keys(allClubMembers || {}).filter(uid => !event.rsvps?.[uid]);
  const rsvpCounts = {
    yes: Object.values(event.rsvps || {}).filter(r => r.status === 'yes').length,
    no: Object.values(event.rsvps || {}).filter(r => r.status === 'no').length,
    maybe: Object.values(event.rsvps || {}).filter(r => r.status === 'maybe').length,
    pending: notRespondedUids.length,
  };

  const countdown = useCountdown(event.date, event.time);

  // Determine event badge color based on type
  const eventTypeStyles = {
    social: { bg: 'bg-pink-100', text: 'text-pink-800', label: '🎉 Social Event' },
    meeting: { bg: 'bg-blue-100', text: 'text-blue-800', label: '📋 Team Meeting' },
    training: { bg: 'bg-green-100', text: 'text-green-800', label: '🏃 Training Session' },
    fundraiser: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '💰 Fundraiser' },
    poll: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: '📊 Poll' },
    other: { bg: 'bg-white/20', text: 'text-white', label: '📅 Club Event' }
  };
  const badgeStyle = eventTypeStyles[event.eventType] || eventTypeStyles.other;

  return (
    <div className="w-full flex-shrink-0 snap-center">
      <div
        onClick={() => setShowRsvps(!showRsvps)}
        className="bg-gradient-to-r from-[#900B99] to-[#39004D] rounded-2xl shadow-lg p-5 cursor-pointer relative overflow-hidden"
      >
        <div className="flex justify-between items-start">
          {/* Event Info Left Side */}
          <div className="flex-1 pr-4">
            <h3 className="text-xl font-bold text-white tracking-tight mb-1 leading-tight">
              {event.name}
            </h3>
            <div className="text-white/90 font-semibold mb-2 text-[15px]">
              {formatDate(event.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="text-white/70 text-[11px] leading-snug">
              {event.time}{event.endTime ? ` - ${event.endTime}` : ''} {event.venue ? `at ${event.venue}` : ''}
            </div>
          </div>

          {/* Countdown Clock Right Side */}
          <div className="flex-shrink-0">
            {!countdown.isPast ? (
              <div className="flex gap-2.5 bg-black/40 p-2.5 rounded-xl border border-white/10 shadow-inner backdrop-blur-sm">
                <div className="flex flex-col items-center">
                  <div className="text-sm font-bold text-white leading-none mb-1">{String(countdown.days).padStart(2, '0')}</div>
                  <span className="text-[8px] text-white/50 uppercase tracking-widest font-bold">Days</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-sm font-bold text-white leading-none mb-1">{String(countdown.hours).padStart(2, '0')}</div>
                  <span className="text-[8px] text-white/50 uppercase tracking-widest font-bold">Hrs</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-sm font-bold text-white leading-none mb-1">{String(countdown.minutes).padStart(2, '0')}</div>
                  <span className="text-[8px] text-white/50 uppercase tracking-widest font-bold">Min</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-sm font-bold text-white leading-none mb-1">{String(countdown.seconds).padStart(2, '0')}</div>
                  <span className="text-[8px] text-white/50 uppercase tracking-widest font-bold">Sec</span>
                </div>
              </div>
            ) : (
              <div className="bg-black/40 px-3 py-2 rounded-xl border border-white/10 backdrop-blur-sm">
                <p className="text-[11px] font-bold text-white/90 uppercase tracking-wider flex items-center gap-1.5 m-0">
                  <PartyPopper size={14} className="text-pink-300" /> It's Happening!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Expanded RSVP Details (Hidden by default to match clean mockup) */}
        {showRsvps && (
          <div className="mt-4 pt-4 border-t border-white/10 animate-in fade-in slide-in-from-top-2 duration-200">
            {event.eventType === 'poll' && event.pollOptions?.length > 0 ? (
              <div className="flex flex-col gap-2 mb-4">
                {event.pollOptions.map((option, idx) => {
                  const isSelected = myRsvp?.status === option;
                  const voteCount = Object.values(event.rsvps || {}).filter(r => r.status === option).length;
                  const totalVotes = Object.values(event.rsvps || {}).filter(r => event.pollOptions.includes(r.status)).length;
                  const percent = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;

                  return (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, option); }}
                      className={`relative overflow-hidden w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-all shadow-sm group ${isSelected
                        ? 'bg-indigo-500/80 text-white border-2 border-indigo-300'
                        : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                        }`}
                    >
                      {/* Progress Bar Background */}
                      {totalVotes > 0 && (
                        <div
                          className={`absolute left-0 top-0 bottom-0 ${isSelected ? 'bg-indigo-600' : 'bg-white/10'} transition-all -z-10`}
                          style={{ width: `${percent}%` }}
                        />
                      )}
                      <div className="flex justify-between items-center z-10 relative">
                        <span className="drop-shadow-md">{option}</span>
                        {totalVotes > 0 && (
                          <span className="text-xs font-semibold opacity-90 drop-shadow-md">{percent}% ({voteCount})</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                {/* Standard RSVP Section */}
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
                    <span className="text-indigo-100 text-[13px] font-medium mr-1 hidden sm:inline">RSVP:</span>

                    <button
                      onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'yes', 0); }}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-bold transition-all flex items-center gap-1 shadow-sm whitespace-nowrap ${myRsvp?.status === 'yes'
                        ? 'bg-green-500 text-white ring-1 ring-green-300 ring-offset-1 ring-offset-indigo-900 border-none'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                        }`}
                    >
                      <Check size={13} /> Going
                    </button>

                    <button
                      onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'no'); }}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-bold transition-all flex items-center gap-1 shadow-sm whitespace-nowrap ${myRsvp?.status === 'no'
                        ? 'bg-red-500 text-white ring-1 ring-red-300 ring-offset-1 ring-offset-indigo-900 border-none'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                        }`}
                    >
                      <X size={13} /> Can't Go
                    </button>

                    <button
                      onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'maybe'); }}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-bold transition-all flex items-center gap-1 shadow-sm whitespace-nowrap ${myRsvp?.status === 'maybe'
                        ? 'bg-yellow-500 text-white ring-1 ring-yellow-300 ring-offset-1 ring-offset-indigo-900 border-none'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                        }`}
                    >
                      <HelpCircle size={13} /> Maybe
                    </button>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-3">
                    <div className="flex items-center gap-2 bg-black/20 px-2.5 py-1 rounded border border-white/5 text-[11px] font-medium tracking-wide whitespace-nowrap flex-shrink-0">
                      <span className="flex items-center gap-1 text-green-400" title="Going"><CheckCircle size={11} /> {rsvpCounts.yes}</span>
                      <span className="flex items-center gap-1 text-red-400" title="Can't Go"><XCircle size={11} /> {rsvpCounts.no}</span>
                      <span className="flex items-center gap-1 text-yellow-400" title="Maybe"><HelpCircle size={11} /> {rsvpCounts.maybe}</span>
                      <span className="flex items-center gap-1 text-indigo-300" title="Pending"><Clock size={11} /> {rsvpCounts.pending}</span>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); setShowRsvpDetails(!showRsvpDetails); }}
                      className="text-[11px] text-indigo-200 hover:text-white font-medium flex items-center transition-colors bg-white/5 hover:bg-white/10 px-2 py-1.5 rounded gap-1 whitespace-nowrap"
                    >
                      {showRsvpDetails ? 'Hide' : 'Details'}
                      <ChevronDown size={11} className={`transition-transform ${showRsvpDetails ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Note Input */}
                {myRsvp && (
                  <div className="mt-3 flex w-full gap-2 bg-black/10 rounded-md p-1.5 border border-white/5 items-center">
                    <span className="text-[11px] font-bold text-indigo-300/80 uppercase tracking-wider ml-1 hidden sm:block whitespace-nowrap">Note:</span>
                    <input
                      type="text"
                      placeholder="Add an optional note..."
                      className="flex-1 text-[13px] px-2 py-1 bg-white/5 border border-white/10 rounded focus:ring-1 focus:ring-purple-400 focus:border-transparent text-white placeholder-indigo-300/50 outline-none w-full min-w-0 transition-all"
                      value={editingEventNote[event.id] !== undefined ? editingEventNote[event.id] : (myRsvp.note || '')}
                      onChange={(e) => setEditingEventNote(prev => ({ ...prev, [event.id]: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const noteValue = editingEventNote[event.id] !== undefined ? editingEventNote[event.id] : myRsvp.note;
                        handleEventRsvp(event.id, myRsvp.status, { note: noteValue || '' });
                        setEditingEventNote(prev => {
                          const next = { ...prev };
                          delete next[event.id];
                          return next;
                        });
                      }}
                      className="px-3 py-1 bg-purple-500 hover:bg-purple-400 border border-purple-400/50 text-white rounded text-[11px] font-bold transition-colors shadow-sm ml-auto whitespace-nowrap flex-shrink-0"
                    >
                      Save
                    </button>
                  </div>
                )}

                {/* Expanded RSVP Details */}
                {showRsvpDetails && (
                  <div className="mt-3 pt-3 border-t border-white/5 text-white text-[11px] leading-tight">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      {/* Going */}
                      <div>
                        <div className="font-bold text-green-400 mb-1.5 flex items-center justify-between pb-1 border-b border-white/5">
                          <span className="uppercase tracking-wider">Going</span>
                          <span className="bg-green-500/20 px-1 py-0.5 rounded text-[11px]">{rsvpCounts.yes}</span>
                        </div>
                        <div className="text-indigo-50 font-medium">
                          {rsvpCounts.yes > 0 ? (
                            Object.entries(event.rsvps || {})
                              .filter(([_, r]) => r.status === 'yes')
                              .sort((a, b) => {
                                const nameA = allClubMembers[a[0]]?.name || 'Unknown';
                                const nameB = allClubMembers[b[0]]?.name || 'Unknown';
                                return nameA.localeCompare(nameB);
                              })
                              .map(([uid, r]) => {
                                let name = allClubMembers[uid]?.name || 'Unknown';
                                if (r.guests > 0) name += ` (+${r.guests})`;
                                return <div key={uid} className="py-0.5">{name} {r.note && <span className="opacity-60 italic text-[11px] ml-1">"{r.note}"</span>}</div>;
                              })
                          ) : (
                            <span className="italic text-indigo-300/50">None</span>
                          )}
                        </div>
                      </div>

                      {/* Can't Go */}
                      <div>
                        <div className="font-bold text-red-400 mb-1.5 flex items-center justify-between pb-1 border-b border-white/5">
                          <span className="uppercase tracking-wider">Can't Go</span>
                          <span className="bg-red-500/20 px-1 py-0.5 rounded text-[11px]">{rsvpCounts.no}</span>
                        </div>
                        <div className="text-indigo-50 font-medium">
                          {rsvpCounts.no > 0 ? (
                            Object.entries(event.rsvps || {})
                              .filter(([_, r]) => r.status === 'no')
                              .sort((a, b) => {
                                const nameA = allClubMembers[a[0]]?.name || 'Unknown';
                                const nameB = allClubMembers[b[0]]?.name || 'Unknown';
                                return nameA.localeCompare(nameB);
                              })
                              .map(([uid, r]) => {
                                let name = allClubMembers[uid]?.name || 'Unknown';
                                return <div key={uid} className="py-0.5">{name} {r.note && <span className="opacity-60 italic text-[11px] ml-1">"{r.note}"</span>}</div>;
                              })
                          ) : (
                            <span className="italic text-indigo-300/50">None</span>
                          )}
                        </div>
                      </div>

                      {/* Maybe */}
                      <div>
                        <div className="font-bold text-yellow-400 mb-1.5 flex items-center justify-between pb-1 border-b border-white/5">
                          <span className="uppercase tracking-wider">Maybe</span>
                          <span className="bg-yellow-500/20 px-1 py-0.5 rounded text-[11px]">{rsvpCounts.maybe}</span>
                        </div>
                        <div className="text-indigo-50 font-medium">
                          {rsvpCounts.maybe > 0 ? (
                            Object.entries(event.rsvps || {})
                              .filter(([_, r]) => r.status === 'maybe')
                              .sort((a, b) => {
                                const nameA = allClubMembers[a[0]]?.name || 'Unknown';
                                const nameB = allClubMembers[b[0]]?.name || 'Unknown';
                                return nameA.localeCompare(nameB);
                              })
                              .map(([uid, r]) => {
                                let name = allClubMembers[uid]?.name || 'Unknown';
                                return <div key={uid} className="py-0.5">{name} {r.note && <span className="opacity-60 italic text-[11px] ml-1">"{r.note}"</span>}</div>;
                              })
                          ) : (
                            <span className="italic text-indigo-300/50">None</span>
                          )}
                        </div>
                      </div>

                      {/* Pending */}
                      <div>
                        <div className="font-bold text-indigo-300 mb-1.5 flex items-center justify-between pb-1 border-b border-white/5">
                          <span className="uppercase tracking-wider">Pending</span>
                          <span className="bg-indigo-500/20 px-1 py-0.5 rounded text-[11px]">{rsvpCounts.pending}</span>
                        </div>
                        <div className="text-indigo-50 font-medium">
                          {rsvpCounts.pending > 0 ? (
                            [...notRespondedUids]
                              .sort((a, b) => {
                                const nameA = allClubMembers[a]?.name || 'Unknown';
                                const nameB = allClubMembers[b]?.name || 'Unknown';
                                return nameA.localeCompare(nameB);
                              })
                              .map(uid => {
                                const name = allClubMembers[uid]?.name || 'Unknown';
                                return <div key={uid} className="py-0.5 opacity-70 italic">{name}</div>;
                              })
                          ) : (
                            <span className="italic text-indigo-300/50">None</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Umpiring Banner Item ---
const UmpiringBannerItem = ({ assignment, currentUserId, currentUserName, onVolunteer }) => {
  const dateObj = new Date(assignment.date + 'T12:00:00');
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
  const dayNum = dateObj.getDate();
  const year = dateObj.getFullYear();
  const dateStr = `${dayName}, ${monthName} ${dayNum}, ${year}`;
  const timeStr = assignment.time ? `${formatTime(assignment.time)} ` : '';
  const venueStr = assignment.venue ? `at ${assignment.venue}` : '';

  const volunteers = assignment.volunteers || [];
  const hasVolunteered = volunteers.some(v => v.userId === currentUserId);
  const isTakenBySomeoneElse = volunteers.length > 0 && !hasVolunteered;
  const [toggling, setToggling] = React.useState(false);

  const handleToggle = async () => {
    if (toggling || isTakenBySomeoneElse || !onVolunteer) return;
    setToggling(true);
    try { await onVolunteer(assignment, !hasVolunteered); }
    finally { setToggling(false); }
  };

  return (
    <div className="w-[320px] shrink-0 snap-start bg-[#6B1C81] rounded-xl shadow-md p-4 flex flex-col gap-2 overflow-hidden">
      {/* Match info */}
      <div>
        <h3 className="font-bold text-base text-white leading-tight truncate">
          {assignment.homeTeam} vs {assignment.awayTeam}
        </h3>
        <p className="text-sm text-white/90 truncate">{dateStr}</p>
        {(assignment.matchType || timeStr || venueStr) && (
          <p className="text-xs text-white/70 truncate">
            {assignment.matchType ? `${assignment.matchType} ` : ''}
            {timeStr}
            {venueStr}
          </p>
        )}
        {assignment.umpiringTeamName && (
          <p className="text-xs text-white/60 truncate">🧑‍⚖️ {assignment.umpiringTeamName}</p>
        )}
      </div>

      {/* Volunteer section */}
      <div className="border-t border-white/20 pt-2">
        {volunteers.length > 0 ? (
          <div className="mb-2">
            <p className="text-[11px] text-white/60 uppercase tracking-wide mb-1">Umpiring</p>
            <div className="flex flex-wrap gap-1">
              {volunteers.map((v, i) => (
                <span
                  key={i}
                  className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    v.userId === currentUserId
                      ? 'bg-green-400 text-green-900'
                      : 'bg-white/20 text-white'
                  }`}
                >
                  {v.userId === currentUserId ? '✓ You' : v.userName}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-white/40 italic mb-2">No one volunteered yet</p>
        )}

        <button
          onClick={handleToggle}
          disabled={toggling || isTakenBySomeoneElse}
          className={`w-full py-1.5 rounded-lg text-xs font-bold transition-all ${
            hasVolunteered
              ? 'bg-green-400 text-green-900 hover:bg-green-300'
              : isTakenBySomeoneElse
                ? 'bg-white/5 text-white/40 border border-white/10 cursor-not-allowed'
                : 'bg-white/15 text-white hover:bg-white/25 border border-white/30'
          }`}
        >
          {toggling
            ? '…'
            : hasVolunteered
              ? "✓ You're Umpiring"
              : isTakenBySomeoneElse
                ? "Already Taken"
                : "I'll Umpire"}
        </button>
      </div>
    </div>
  );
};

const PlayerDashboard = ({ user, onLogout, hideHeader = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const [allClubGames, setAllClubGames] = useState([]);

  const [expenses, setExpenses] = useState([]);
  const [submittedExpenses, setSubmittedExpenses] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [playerData, setPlayerData] = useState(null);
  const [clubTeams, setClubTeams] = useState([]);
  const [activeTeamFilter, setActiveTeamFilter] = useState('all');



  const availableTeamsForFilter = useMemo(() => {
    // Only return actual teams associated with this club
    if (!clubTeams || clubTeams.length === 0) return [];

    return [...clubTeams].sort((a, b) => {
      const nameA = a.teamName || '';
      const nameB = b.teamName || '';
      return nameA.localeCompare(nameB);
    });
  }, [clubTeams]);

  const getGameStatus = (game) => {
    if (game.status) return game.status;
    const gameDate = new Date(`${game.date}T${game.time || '00:00'}`);
    return gameDate < new Date() ? 'Completed' : 'Scheduled';
  };

  const getComputedGameFormat = (game) => {
    if (game.format) return game.format;
    
    // Check if it's explicitly tied to a tournament
    if (game.tournamentId && tournaments.length > 0) {
      const tourney = tournaments.find(t => t.id === game.tournamentId);
      if (tourney && tourney.ballType) {
        return tourney.ballType === 'tennis' ? 'Tennis Ball' : 'Leather Ball';
      }
    }
    
    // Check team links
    if (clubTeams.length > 0) {
      const gameTeamIds = [game.teamId, game.homeTeamId, game.awayTeamId].filter(id => id);
      for (const id of gameTeamIds) {
        const matchedTeam = clubTeams.find(t => t.teamId === id);
        if (matchedTeam && matchedTeam.ballType) {
          return matchedTeam.ballType === 'tennis' ? 'Tennis Ball' : 'Leather Ball';
        }
      }
    }
    
    return 'Leather Ball'; // Fallback
  };

  const gamesPlayed = useMemo(() => {
    if (!playerData) return [];

    const playerIds = [
      playerData.id,
      playerData.userId,
      playerData.oderId,
      playerData.oderedPlayerId,
      ...(playerData.matchedPlayerIds || [])
    ].filter(Boolean);
    const uniquePlayerIds = [...new Set(playerIds)];

    return allClubGames.map(game => {
      const isInPlaying11 = game.playing11?.some(id => uniquePlayerIds.includes(id));

      let belongsToTeam = false;
      
      if (activeTeamFilter !== 'all') {
         const gameTeams = [game.teamId, game.homeTeamId, game.awayTeamId].filter(Boolean);
         if (gameTeams.length > 0) {
            belongsToTeam = gameTeams.includes(activeTeamFilter);
         } else {
            belongsToTeam = true;
         }
      } else {
         const playerTeams = playerData?.allTeams?.map(t => t.teamId) || (playerData?.teamId ? [playerData.teamId] : []);
         const playerTeamNames = playerData?.allTeams?.map(t => t.teamName?.toLowerCase().trim()) || (playerData?.teamName ? [playerData.teamName.toLowerCase().trim()] : []);

         if (playerTeams.length > 0 || playerTeamNames.length > 0) {
           const gameTeams = [game.teamId, game.homeTeamId, game.awayTeamId].filter(Boolean);
           const gameTeamNames = [game.homeTeam, game.awayTeam].filter(Boolean).map(n => n.toLowerCase().trim());

           if (gameTeams.length > 0 || gameTeamNames.length > 0) {
             const matchesId = gameTeams.some(tId => playerTeams.includes(tId));
             const matchesName = gameTeamNames.some(tName => playerTeamNames.includes(tName));
             belongsToTeam = matchesId || matchesName;
           } else {
             belongsToTeam = true;
           }
         } else {
           const gameTeams = [game.teamId, game.homeTeamId, game.awayTeamId].filter(Boolean);
           belongsToTeam = gameTeams.length === 0;
         }
      }

      const isNotRostered = !(isInPlaying11 || belongsToTeam);

      return {
        ...game,
        isNotRostered
      };
    });
  }, [allClubGames, playerData, activeTeamFilter]);



  const [selectedTournament, setSelectedTournament] = useState('all');
  const [clubInfo, setClubInfo] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileSwitchClub, setShowMobileSwitchClub] = useState(false);
  const [showMobileJoinClub, setShowMobileJoinClub] = useState(false);
  const [availability, setAvailability] = useState({}); // Track player availability for games
  const [showSelectPlaying11, setShowSelectPlaying11] = useState(false);
  const [guestPlayerName, setGuestPlayerName] = useState(''); // NEW
  const [guestPlayers, setGuestPlayers] = useState([]); // NEW
  const [selectedGameForSquad, setSelectedGameForSquad] = useState(null);
  const [squadSelection, setSquadSelection] = useState([]);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [unassignedPlayers, setUnassignedPlayers] = useState([]);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [addingPlayerId, setAddingPlayerId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dataUnsubscribers, setDataUnsubscribers] = useState([]); // Real-time listener cleanup
  
  // Events state
  const [events, setEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false); // Collapsible state for 'All Club Events'
  const [eventRsvps, setEventRsvps] = useState({}); // { eventId: { status: 'yes', guests: 0 } }
  const [expandedEventId, setExpandedEventId] = useState(null); // Track which event has RSVPs expanded
  const [expandedAvailabilityGameId, setExpandedAvailabilityGameId] = useState(null); // Track which game has availability details expanded
  const [allClubMembers, setAllClubMembers] = useState({}); // Map of userId -> { name, email } for resolving RSVPs

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null);

  const [view, setView] = useState('dashboard');

  // Expense Form State
  const [expenseLevel, setExpenseLevel] = useState('match'); // Default to match as it's most common
  const [expenseDescription, setExpenseDescription] = useState(''); // Stores final description string
  const [expenseNote, setExpenseNote] = useState(''); // Stores a new time-series note when submitting/editing
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expenseDate, setExpenseDate] = useState(''); // NEW: For Umpiring assignment date
  const [customDescription, setCustomDescription] = useState('');
  const [expenseTournamentId, setExpenseTournamentId] = useState(''); // For tournament level expenses
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseGameId, setExpenseGameId] = useState('');
  const [otherGameDetails, setOtherGameDetails] = useState('');
  const [expenseReceipt, setExpenseReceipt] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [removeOldReceipt, setRemoveOldReceipt] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftExpenses, setDraftExpenses] = useState([]);

  // Profile states
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileBio, setProfileBio] = useState('');

  // WhatsApp link management (for captains/vice-captains)
  const [editingWhatsApp, setEditingWhatsApp] = useState(false);
  const [whatsAppLink, setWhatsAppLink] = useState('');
  const [showPaymentQR, setShowPaymentQR] = useState(false);

  // Payment submission
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [paymentSubmissions, setPaymentSubmissions] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentTournamentId, setPaymentTournamentId] = useState('');
  const [paymentTransactionId, setPaymentTransactionId] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);

  // Captain's Corner state
  const [captainViewExpanded, setCaptainViewExpanded] = useState(true);
  const [showTeamContacts, setShowTeamContacts] = useState(false);
  const [showWhatsAppMessenger, setShowWhatsAppMessenger] = useState(false);
  const [showAddGame, setShowAddGame] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [expandedGames, setExpandedGames] = useState({}); // Track which games are expanded (collapsed by default)
  const [expandedAvailability, setExpandedAvailability] = useState({}); // Track which games have availability expanded
  const [eventsViewMode, setEventsViewMode] = useState('grid'); // 'grid' | 'list'
  const [mobileViewMode, setMobileViewMode] = useState('list'); // 'list' | 'calendar'
  const [activeFilters, setActiveFilters] = useState({
    quicks: [],
    teams: [],
    tournaments: [],
    formats: []
  }); // Tracks arrays of multi-selected filters

  // Helper to toggle multi-select filters
  const toggleFilter = (category, value) => {
    setActiveFilters(prev => {
      const isSelected = prev[category].includes(value);
      return {
        ...prev,
        [category]: isSelected
          ? prev[category].filter(item => item !== value)
          : [...prev[category], value]
      };
    });
  };

  const clearAllFilters = () => {
    setActiveFilters({ quicks: [], teams: [], tournaments: [], formats: [] });
    setDateRangeFilter({ start: '', end: '' });
  };
  const [formatFilter, setFormatFilter] = useState(null); // 'leather' | 'tennis'
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [showDateRangeDropdown, setShowDateRangeDropdown] = useState(false);

  const hasNoActiveFilters = activeFilters.quicks.length === 0 && 
                             activeFilters.teams.length === 0 && 
                             activeFilters.tournaments.length === 0 && 
                             activeFilters.formats.length === 0 && 
                             !dateRangeFilter.start && 
                             !dateRangeFilter.end;
  const [showAllEventsOnly, setShowAllEventsOnly] = useState(false); // Toggle between games and events

  const filteredAllGames = useMemo(() => {
    let list = gamesPlayed || [];
    if (!showAllEventsOnly) {
      list = list.filter(g => !g.isNotRostered);
    }
    if (activeFilters.quicks.includes('Action on me')) {
      list = list.filter(g => !availability[g.id] && !g.isNotRostered);
    }
    if (activeFilters.quicks.includes('Practice')) {
      list = list.filter(g => !g.tournamentId);
    }
    if (activeFilters.tournaments.length > 0) {
      list = list.filter(g => activeFilters.tournaments.includes(g.tournamentId));
    }
    if (activeFilters.teams.length > 0) {
      const teamIds = availableTeamsForFilter.filter(t => activeFilters.teams.includes(t.teamName)).map(t => t.teamId);
      list = list.filter(g =>
        teamIds.includes(g.teamId) || teamIds.includes(g.homeTeamId) || teamIds.includes(g.awayTeamId) ||
        activeFilters.teams.includes(g.teamName) || activeFilters.teams.includes(g.homeTeam) || activeFilters.teams.includes(g.awayTeam)
      );
    }
    if (activeFilters.formats.length > 0) {
      list = list.filter(g => {
         const computedStr = getComputedGameFormat(g).toLowerCase();
         return activeFilters.formats.some(f => computedStr.includes(f));
      });
    }
    if (dateRangeFilter.start || dateRangeFilter.end) {
      list = list.filter(g => {
        if (!g.date) return false;
        const gDate = new Date(g.date);
        gDate.setHours(0, 0, 0, 0);
        let isValid = true;
        if (dateRangeFilter.start) {
          const sDate = new Date(dateRangeFilter.start);
          sDate.setMinutes(sDate.getMinutes() + sDate.getTimezoneOffset());
          sDate.setHours(0, 0, 0, 0);
          if (gDate < sDate) isValid = false;
        }
        if (dateRangeFilter.end) {
          const eDate = new Date(dateRangeFilter.end);
          eDate.setMinutes(eDate.getMinutes() + eDate.getTimezoneOffset());
          eDate.setHours(23, 59, 59, 999);
          if (gDate > eDate) isValid = false;
        }
        return isValid;
      });
    }
    return list;
  }, [gamesPlayed, showAllEventsOnly, activeFilters, dateRangeFilter, availability, availableTeamsForFilter]);

  const filteredAllEvents = useMemo(() => {
    let list = events || [];
    if (activeFilters.quicks.includes('Practice')) {
      list = list.filter(e => e.eventType === 'training');
    }
    return list;
  }, [events, activeFilters]);
  const [sendingReminderForGame, setSendingReminderForGame] = useState(null); // Track which game is sending a reminder
  const [editingEventNote, setEditingEventNote] = useState({}); // Track temporary note input per event
  const [editingGameNote, setEditingGameNote] = useState({}); // Track temporary note input per game

  // Financial detail modals
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showPendingExpensesModal, setShowPendingExpensesModal] = useState(false);

  const [showTournamentsModal, text] = useState(false); // Kept for reference if needed elsewhere
  const [showTournamentDropdown, setShowTournamentDropdown] = useState(false);
  const [umpiringExpanded, setUmpiringExpanded] = useState(
    () => localStorage.getItem('umpiringExpanded') !== 'false' // default open
  );
  const [showGamesModal, setShowGamesModal] = useState(false);
  const [showBalanceDueModal, setShowBalanceDueModal] = useState(false);


  // Registration form state (for Cascadia Willow Club new users)
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationFormCompleted, setRegistrationFormCompleted] = useState(null); // null = not checked yet

  // PWA Install Prompt state
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);

  // Mobile Availability Bottom Sheet state
  const [selectedGameForAvailability, setSelectedGameForAvailability] = useState(null);

  const upcomingUmpiringAssignments = useMemo(() => {
    if (!tournaments || !playerData) return [];
    
    const captainTeamIds = new Set();
    
    if (playerData.teamRole === 'captain' || playerData.teamRole === 'vice-captain') {
      if (playerData.teamId) captainTeamIds.add(playerData.teamId);
    }
    
    if (playerData.allTeams) {
      playerData.allTeams.forEach(t => {
        if (t.teamRole === 'captain' || t.teamRole === 'vice-captain') {
          captainTeamIds.add(t.teamId);
        }
      });
    }

    // Safely evaluate club admin status using the global logic
    const clubMembership = clubInfo?.id ? user?.joinedClubs?.[clubInfo.id] : null;
    const isClubAdmin = 
      user?.role === 'super-admin' ||
      clubMembership?.role === 'admin' || 
      clubMembership?.role === 'super-admin' ||
      (!clubMembership && (
        clubInfo?.admins?.includes(user?.uid) ||
        user?.permissions?.clubAdminOf?.includes(clubInfo?.id) ||
        user?.role === 'admin' || 
        user?.clubInfo?.role === 'admin'
      ));
      
    // Show for all club members (not just captains/admins)
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const assignments = [];
    
    tournaments.forEach(tournament => {
      if (tournament.umpiringAssignments && Array.isArray(tournament.umpiringAssignments)) {
        tournament.umpiringAssignments.forEach(assignment => {
          let isMyTeam = false;
          
          if (captainTeamIds.has(assignment.umpiringTeamId)) {
            isMyTeam = true;
          } else if (assignment.umpiringTeamName) {
            // Fallback to name matching in case ID is missing or mismatched
            const nameToMatch = assignment.umpiringTeamName.toLowerCase().trim();
            const isPrimaryMatch = (playerData.teamRole === 'captain' || playerData.teamRole === 'vice-captain') && 
               playerData.teamName?.toLowerCase().trim() === nameToMatch;
               
            const isSecondaryMatch = playerData.allTeams?.some(t => 
               (t.teamRole === 'captain' || t.teamRole === 'vice-captain') &&
               t.teamName?.toLowerCase().trim() === nameToMatch
            );
            
            if (isPrimaryMatch || isSecondaryMatch) isMyTeam = true;
          }

          // Show to all club members — only filter by date
          let isPast = false;
          if (assignment.date) {
             const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(assignment.date);
             let assignmentDate;
             if (isDateOnly) {
               const [year, month, day] = assignment.date.split('-');
               assignmentDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
             } else {
               assignmentDate = new Date(assignment.date);
             }
             
             if (!isNaN(assignmentDate.getTime())) {
               assignmentDate.setHours(0, 0, 0, 0);
               if (assignmentDate < today) {
                 isPast = true;
               }
             }
          }
          
          if (!isPast) {
             assignments.push({
               ...assignment,
               tournamentName: tournament.name
             });
          }
        });
      }
    });
    
    // Sort by date ascending
    return assignments.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [tournaments, playerData, clubInfo, user]);

  // --- Volunteer to umpire a specific assignment ---
  const handleVolunteerUmpire = async (assignment, volunteering) => {
    const tournament = tournaments.find(t => t.name === assignment.tournamentName);
    if (!tournament) return;

    const currentAssignments = [...(tournament.umpiringAssignments || [])];
    const idx = currentAssignments.findIndex(a =>
      a.date === assignment.date &&
      a.homeTeam === assignment.homeTeam &&
      a.awayTeam === assignment.awayTeam
    );
    if (idx === -1) return;

    const existing = { ...currentAssignments[idx] };
    const currentVolunteers = existing.volunteers || [];

    if (volunteering) {
      if (!currentVolunteers.some(v => v.userId === user?.uid)) {
        existing.volunteers = [...currentVolunteers, {
          userId: user.uid,
          userName: user.displayName || user.email?.split('@')[0] || 'Player',
          volunteeredAt: new Date().toISOString()
        }];
      }
    } else {
      existing.volunteers = currentVolunteers.filter(v => v.userId !== user?.uid);
    }

    currentAssignments[idx] = existing;

    try {
      await updateDoc(doc(db, 'tournaments', tournament.id), {
        umpiringAssignments: currentAssignments
      });
      // Update local state immediately so UI reflects without re-fetch
      setTournaments(prev => prev.map(t =>
        t.id === tournament.id ? { ...t, umpiringAssignments: currentAssignments } : t
      ));
    } catch (err) {
      console.error('Volunteer umpire failed:', err);
      alert('Could not save. Please try again.');
    }
  };

  // Check if we should show PWA install prompt (first-time user)
  useEffect(() => {
    const checkPWAPrompt = () => {
      // Don't show if already installed as PWA
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return;
      }

      // Check if user has dismissed before
      const dismissed = localStorage.getItem('pwa_install_prompt_dismissed');
      const dismissedDate = localStorage.getItem('pwa_install_prompt_date');

      if (dismissed) {
        // Show again after 7 days
        if (dismissedDate) {
          const daysSinceDismissed = (Date.now() - new Date(dismissedDate).getTime()) / (1000 * 60 * 60 * 24);
          if (daysSinceDismissed < 7) {
            return;
          }
        } else {
          return;
        }
      }

      // Show prompt after a short delay (let the dashboard load first)
      setTimeout(() => {
        setShowPWAPrompt(true);
      }, 2000);
    };

    if (!loading && playerData) {
      checkPWAPrompt();
    }
  }, [loading, playerData]);

  // Auto-refresh when returning to dashboard
  useEffect(() => {
    if (view === 'dashboard' && clubInfo && playerData) {
      handleRefresh();
    }
  }, [view]);

  // Load notifications when user is available
  useEffect(() => {
    if (user?.uid) {
      console.log('🔔 User available, loading notifications for:', user.uid);
      loadNotifications(playerData?.id);
    }
  }, [user?.uid, playerData?.id]);

  // Also refresh notifications when viewing expenses
  useEffect(() => {
    if (view === 'my-expenses' && playerData) {
      loadNotifications(playerData.id);
    }
  }, [view, playerData]);

  // Set up real-time listener for draft expenses when playerData is available
  useEffect(() => {
    if (playerData && playerData.id) {
      console.log('🔵 Setting up draft expenses listener for player:', playerData.id);
      const draftsUnsub = loadDraftExpenses(true);

      // Add to unsubscribers
      if (draftsUnsub) {
        setDataUnsubscribers(prev => [...prev, draftsUnsub]);
      }

      // Cleanup on unmount or when playerData changes
      return () => {
        if (draftsUnsub) {
          draftsUnsub();
        }
      };
    }
  }, [playerData?.id]);

  useEffect(() => {
    loadPlayerData();

    // Check for gameId in URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const gameIdFromUrl = urlParams.get('gameId');

    if (gameIdFromUrl) {
      setView('dashboard');
    }

    // Cleanup listeners on unmount
    return () => {
      dataUnsubscribers.forEach(unsub => unsub && unsub());
      // Cleanup camera stream if active
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [user]);

  // Calculate permissions for view switching
  const canSwitchToAdmin = useMemo(() => {
    if (user?.role === 'super-admin') return true;

    // Check club arrays
    if (clubInfo) {
      if (clubInfo.admins?.includes(user?.uid) || 
          clubInfo.treasurers?.includes(user?.uid) || 
          clubInfo.auditors?.includes(user?.uid)) {
        return true;
      }
    }

    // Check specific club membership
    if (clubInfo && user?.joinedClubs && user.joinedClubs[clubInfo.id]) {
      const role = user.joinedClubs[clubInfo.id].role;
      const additional = user.joinedClubs[clubInfo.id].additionalRoles || [];
      if (role === 'admin' || role === 'treasurer' || additional.includes('treasurer') || additional.includes('auditor')) {
        return true;
      }
    }

    // Check primary role (legacy fallback)
    if (user?.role === 'admin' || user?.role === 'treasurer') {
      return true;
    }

    // Check embedded club info role (legacy fallback)
    if (user?.clubInfo?.role === 'admin' || user?.clubInfo?.role === 'treasurer') {
      return true;
    }

    // Check additional roles (both root and clubInfo)
    const rootAdditionalRoles = user?.additionalRoles || [];
    const clubAdditionalRoles = user?.clubInfo?.additionalRoles || [];
    const allroles = [...rootAdditionalRoles, ...clubAdditionalRoles];
    return allroles.includes('treasurer') || allroles.includes('auditor');
  }, [user, clubInfo]);

  // Separate effect to handle scrolling after games are loaded
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gameIdFromUrl = urlParams.get('gameId');

    if (gameIdFromUrl && gamesPlayed.length > 0 && !loading) {
      console.log('🔍 Attempting to scroll to game:', gameIdFromUrl);
      console.log('📋 Available games:', gamesPlayed.map(g => g.id));

      // Wait a bit for rendering
      setTimeout(() => {
        const gameElement = document.getElementById(`game-${gameIdFromUrl}`);
        console.log('🎯 Game element found:', gameElement ? 'YES' : 'NO');

        if (gameElement) {
          gameElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          gameElement.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
          console.log('✅ Scrolled to game and highlighted');

          setTimeout(() => {
            gameElement.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
          }, 3000);
        } else {
          console.log('❌ Game not found in DOM. Check if game ID is correct and game is visible');
        }
      }, 500);
    }
  }, [gamesPlayed, loading]);

  const loadPlayerData = async () => {
    try {
      setLoading(true);
      console.log('🔵 Loading player data for:', user.email);
      console.log('🔵 User object:', user);

      // Safety check: ensure db is initialized
      if (!db) {
        console.error('❌ Firebase db not initialized yet, retrying in 500ms...');
        setTimeout(() => loadPlayerData(), 500);
        return;
      }

      // Check if user has been rejected - they should see a different screen
      if (user.clubInfo?.status === 'rejected') {
        console.log('⚠️ User was rejected from club');
        setLoading(false);
        return; // Will show rejected screen
      }

      // Get clubId from user.clubInfo or user directly
      const clubId = user.clubInfo?.clubId || user.clubId;

      if (!clubId) {
        console.log('⚠️ No club ID found for player');
        setLoading(false);
        return;
      }

      console.log('🔵 Loading club:', clubId);

      console.log('📍 Step 1: Loading club document');
      const clubRef = doc(db, 'club', clubId);
      
      console.log(`🚨 DEEP DEBUG: path=${clubRef.path}, exactId="${clubId}", type=${typeof clubId}, length=${clubId.length}`);
      
      const clubSnap = await getDoc(clubRef);
      console.log(`🚨 DEEP DEBUG: exists=${clubSnap.exists()}, fromCache=${clubSnap.metadata?.fromCache}`);
      
      if (!clubSnap.exists()) {
        console.log('❌ Club not found (Deep Debug)');
        setLoading(false);
        return;
      }
      
      const club = { id: clubSnap.id, ...clubSnap.data() };
      console.log('✅ Club loaded:', club.name);
      setClubInfo(club);

      // Check if this is Cascadia Willow Club/CWC and user needs to complete registration form
      // Show registration form for APPROVED users who haven't completed it yet
      // (They were approved by admin but need to fill out the form before accessing dashboard)
      const userStatus = user.clubInfo?.status;
      // For registration purposes, only skip if user is actually a club admin or super-admin
      // Regular players in CWC must fill the form even if they have some admin capabilities
      const isAdminUser = user.role === 'admin' || user.role === 'super-admin' ||
        user.clubInfo?.role === 'admin' || user.clubInfo?.role === 'super-admin';
      const skipRegistrationForAdmin = false; // Force everyone to fill registration in CWC clubs

      // Check club code (could be in club document or user's clubInfo)
      const clubCode = club.joinCode || club.clubCode || user.clubInfo?.clubCode;
      console.log('🔍 Club code for registration check:', clubCode);
      console.log('🔍 Club document fields:', Object.keys(club));
      console.log('🔍 User clubInfo:', user.clubInfo);
      console.log('🔍 User status:', userStatus);
      console.log('🔍 Is admin:', isAdminUser);
      console.log('🔍 Skip registration for admin?:', skipRegistrationForAdmin);

      // Show registration form for Cascadia Willow Club ONLY
      const isCascadiaWillowClub =
        club?.name === 'Cascadia Willow Club' ||
        club?.name === 'CWC';

      console.log('🔍 Registration check - Club name:', club?.name);
      console.log('🔍 Registration check - Club code:', clubCode);
      console.log('🔍 Registration check - Is Cascadia club?', isCascadiaWillowClub);

      // For Cascadia clubs, check if registration form exists (everyone must fill it, even admins)
      if (isCascadiaWillowClub) {
        console.log('🔵 Cascadia Willow Club - checking if registration form exists for ALL users');
        try {
          const regFormRef = doc(db, 'registrationForms', user.uid);
          const regFormSnap = await getDoc(regFormRef);

          if (!regFormSnap.exists()) {
            console.log('⚠️ Registration form not completed - marking for display');
            setRegistrationFormCompleted(false);
            setShowRegistrationForm(true);
            // Don't return early - continue loading data so form can pre-fill
          } else {
            console.log('✅ Registration form already completed');
            setRegistrationFormCompleted(true);
          }
        } catch (regFormError) {
          console.error('⚠️ Error checking registration form, skipping:', regFormError);
          setRegistrationFormCompleted(true); // Skip on error
        }
      } else {
        console.log('✅ Skipping registration form check (not Cascadia Willow Club)');
        setRegistrationFormCompleted(true); // Other users don't need form
      }

      // Find player data (from Firestore users collection)
      console.log('📍 Step 2: Loading user document for uid:', user.uid);
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      console.log('📄 User document exists:', userSnap.exists());
      if (userSnap.exists()) {
        console.log('📄 User document data:', userSnap.data());
      }

      let playerInfo = {
        id: user.uid,
        email: user.email,
        name: user.displayName || user.email
      };

      if (userSnap.exists()) {
        const userData = userSnap.data();
        playerInfo = {
          ...playerInfo,
          ...userData,
          name: userData.name || user.displayName
        };
      }

      // IMPROVED: Scan ALL teams in the club to find player's team memberships
      console.log('📍 Step 3: Scanning all teams for player membership');
      const teamsRef = collection(db, 'teams');
      const teamsQuery = query(teamsRef, where('clubId', '==', clubId));
      const teamsSnapshot = await getDocs(teamsQuery);

      const playerTeams = [];
      const allClubTeams = [];
      const matchedPlayerIds = new Set();
      const matchedPlayerEmails = new Set();

      // Seed with basic user details
      matchedPlayerIds.add(user.uid);
      if (playerInfo.id) matchedPlayerIds.add(playerInfo.id);
      if (playerInfo.oderId) matchedPlayerIds.add(playerInfo.oderId);
      if (playerInfo.oderedPlayerId) matchedPlayerIds.add(playerInfo.oderedPlayerId);
      if (user.email) matchedPlayerEmails.add(user.email.toLowerCase());
      if (playerInfo.email) matchedPlayerEmails.add(playerInfo.email.toLowerCase());

      teamsSnapshot.forEach(teamDoc => {
        const teamData = { id: teamDoc.id, ...teamDoc.data() };

        // Map any string players to objects for UI resilience
        const mappedPlayers = (teamData.players || []).map(p => {
          if (typeof p === 'string') {
            return {
              id: p,
              userId: p,
              name: '',
              email: ''
            };
          }
          return p;
        });

        allClubTeams.push({
          teamId: teamDoc.id,
          teamName: teamData.name,
          ballType: teamData.ballType || 'leather',
          players: mappedPlayers
        });

        // Scan players on the team to match current user by UID, email, or name
        const matchingPlayers = mappedPlayers.filter(p => {
          const pId = p.id || p.userId;
          const pName = p.name || '';
          const pEmail = p.email || '';

          const matchesUid = pId === user.uid || (playerInfo.id && pId === playerInfo.id);
          const matchesEmail = pEmail && (
            pEmail.toLowerCase() === user.email?.toLowerCase() ||
            pEmail.toLowerCase() === playerInfo.email?.toLowerCase()
          );
          const matchesName = pName && playerInfo.name && (
            pName.toLowerCase().trim() === playerInfo.name.toLowerCase().trim()
          );

          return matchesUid || matchesEmail || matchesName;
        });

        const isCaptainArr = Array.isArray(teamData.captains) && teamData.captains.includes(user.uid);
        const isViceCaptainArr = Array.isArray(teamData.viceCaptains) && teamData.viceCaptains.includes(user.uid);

        if (matchingPlayers.length > 0 || isCaptainArr || isViceCaptainArr) {
          // Add all matching player IDs and emails to our sets
          matchingPlayers.forEach(p => {
            if (p.id) matchedPlayerIds.add(p.id);
            if (p.userId) matchedPlayerIds.add(p.userId);
            if (p.email) matchedPlayerEmails.add(p.email.toLowerCase());
          });

          const mainMatch = matchingPlayers[0];
          let robustRole = mainMatch?.teamRole;
          if (!robustRole && isCaptainArr) robustRole = 'captain';
          if (!robustRole && isViceCaptainArr) robustRole = 'vice-captain';

          playerTeams.push({
            teamId: teamDoc.id,
            teamName: teamData.name,
            teamRole: robustRole || null,
            whatsappGroupLink: teamData.whatsappGroupLink || '',
            players: mappedPlayers,
            ballType: teamData.ballType || 'leather'
          });
        }
      });

      playerInfo.matchedPlayerIds = Array.from(matchedPlayerIds);
      playerInfo.matchedPlayerEmails = Array.from(matchedPlayerEmails);
      console.log('🔗 Linked Player IDs:', playerInfo.matchedPlayerIds);
      console.log('🔗 Linked Player Emails:', playerInfo.matchedPlayerEmails);

      setClubTeams(allClubTeams);

      // Safely ensure user's explicit profile team is included if data sync missed it
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.teamId && !playerTeams.some(t => t.teamId === userData.teamId)) {
          playerTeams.push({
            teamId: userData.teamId,
            teamName: userData.teamName || 'My Team',
            teamRole: userData.teamRole || 'player',
            whatsappGroupLink: '',
            players: []
          });
          console.log('Added missing team from user profile:', userData.teamName);
        }
      }

      console.log(`✅ Found player in ${playerTeams.length} team(s):`, playerTeams.map(t => t.teamName));

      // Store all teams player belongs to
      playerInfo.allTeams = playerTeams;

      // If player is on at least one team, set the primary team
      if (playerTeams.length > 0) {
        // Prefer team where player is captain/vice-captain, otherwise use first team
        const leadershipTeam = playerTeams.find(t => t.teamRole === 'captain' || t.teamRole === 'vice-captain');
        const primaryTeam = leadershipTeam || playerTeams[0];

        playerInfo.teamId = primaryTeam.teamId;
        playerInfo.teamName = primaryTeam.teamName;
        playerInfo.teamRole = primaryTeam.teamRole;
        playerInfo.teamWhatsAppLink = primaryTeam.whatsappGroupLink;

        // Set initial WhatsApp link state
        setWhatsAppLink(primaryTeam.whatsappGroupLink || '');

        // Load team players for everyone so availability stats work
        console.log('🎖️ Loading team players availability stats');
        setTeamPlayers(primaryTeam.players || []);



        console.log('✅ Primary team set:', primaryTeam.teamName, '- Role:', primaryTeam.teamRole || 'player');
      }

      // Set player data first so draft expenses listener can use it
      setPlayerData(playerInfo);
      console.log('✅ Player data loaded:', playerInfo);
      console.log('  - Team ID:', playerInfo.teamId);
      console.log('  - Team Name:', playerInfo.teamName);
      console.log('  - Team Role:', playerInfo.teamRole);

      // CRITICAL: Check if db is initialized before using it in collection()
      if (!db) {
        console.error('❌ FATAL: Firebase DB instance is missing/undefined in PlayerDashboard!');
        console.error('  - db import:', db);
        setLoading(false);
        return;
      }

      // Clean up any existing listeners
      dataUnsubscribers.forEach(unsub => unsub && unsub());

      // Set up real-time listeners for expenses, wallet transactions, games, tournaments, and draft expenses
      const expensesUnsub = loadExpenses(clubId, user.uid, true, playerInfo.oderId || playerInfo.oderedPlayerId, playerInfo.matchedPlayerIds);
      const walletUnsub = loadWalletTransactions(user.uid, playerInfo.matchedPlayerIds || [playerInfo.id], true, clubId);
      const gamesUnsub = loadGamesForPlayer(clubId, user.uid, true);
      const tournamentsUnsub = loadTournaments(clubId, true);

      // Store unsubscribe functions for cleanup (draft listener will be added after state update)
      setDataUnsubscribers([expensesUnsub, walletUnsub, gamesUnsub, tournamentsUnsub].filter(Boolean));

      // Load other data in parallel (one-time fetches)
      await Promise.all([
        loadPaymentSubmissions(clubId, user.uid),
        loadNotifications(user.uid),
        loadEvents(clubId, user.uid),
        loadClubMembers(clubId), // Load all members for resolving RSVP names
        loadUnassignedPlayers(clubId, playerInfo.teamId) // Required for Captain's Team Roster Management
      ]);

      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading player data:', error);
      setLoading(false);
    }
  };

  // Load notifications
  const loadNotifications = async (playerId) => {
    try {
      // Use the current user's UID as primary identifier
      const primaryUserId = user?.uid;
      if (!primaryUserId) {
        console.log('⚠️ No user UID available, skipping notification load');
        return;
      }

      console.log('🔵 Loading notifications for user:', primaryUserId, 'and playerId:', playerId);

      const notificationsRef = collection(db, 'notifications');

      // Build query - always include user.uid, optionally include playerId if different
      const userIds = [primaryUserId];
      if (playerId && playerId !== primaryUserId) {
        userIds.push(playerId);
      }

      let q;
      if (userIds.length === 1) {
        // Single ID query
        q = query(
          notificationsRef,
          where('userId', '==', userIds[0]),
          where('read', '==', false)
        );
      } else {
        // Multiple IDs with OR query
        q = query(
          notificationsRef,
          or(
            where('userId', '==', userIds[0]),
            where('userId', '==', userIds[1])
          ),
          where('read', '==', false)
        );
      }

      const snapshot = await getDocs(q);

      // Deduplicate by notification ID
      const seen = new Set();
      const notificationsData = [];

      snapshot.forEach((doc) => {
        if (!seen.has(doc.id)) {
          seen.add(doc.id);
          const data = doc.data();
          if (data.clubId === clubInfo?.id || !data.clubId) {
            const notification = { id: doc.id, ...data };
            notificationsData.push(notification);
            console.log('📬 Found notification:', notification.type, notification.message);
          }
        }
      });

      console.log('📊 Found', notificationsData.length, 'unread notifications');
      setNotifications(notificationsData);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
      setNotifications([]);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      // Delete the notification instead of just marking as read
      await markNotificationAsReadAndDelete(notificationId);

      // Remove from local state
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
    }
  };

  const handleDismissAllNotifications = async () => {
    if (notifications.length === 0) return;

    if (!window.confirm('Are you sure you want to dismiss all notifications?')) {
      return;
    }

    try {
      console.log('🔵 Dismissing all notifications...');
      const deletePromises = notifications.map(n => markNotificationAsReadAndDelete(n.id));
      await Promise.all(deletePromises);

      setNotifications([]);
      setShowNotifications(false); // Close the popup
      console.log('✅ All notifications dismissed');
    } catch (error) {
      console.error('❌ Error dismissing all notifications:', error);
      alert('Failed to dismiss some notifications');
    }
  };

  // Enable Push Notifications
  const handleEnableNotifications = async () => {
    if (!user) return;

    // Check if already enabled locally (optional check)
    const result = await requestNotificationPermission(user.uid);
    if (result.success) {
      // Refresh user data to get the token into state if needed
      // alert('Notifications enabled successfully! You will now receive updates on your device.');
      // Use a toast or just log it
      console.log('Push notifications enabled');
    } else {
      console.error('Failed to enable notifications:', result.error);
      alert('Could not enable notifications. Please check your browser settings.');
    }
  };

  const handleTeamSwitch = (newTeamId) => {
    if (!playerData?.allTeams) return;

    const selectedTeam = playerData.allTeams.find(t => t.teamId === newTeamId);
    if (!selectedTeam) return;

    console.log('🔄 Switching to team:', selectedTeam.teamName);

    // Update player data with new team
    setPlayerData(prev => ({
      ...prev,
      teamId: selectedTeam.teamId,
      teamName: selectedTeam.teamName,
      teamRole: selectedTeam.teamRole,
      teamWhatsAppLink: selectedTeam.whatsappGroupLink
    }));

    // Update WhatsApp link state
    setWhatsAppLink(selectedTeam.whatsappGroupLink || '');

    // Update team players for everyone
    setTeamPlayers(selectedTeam.players || []);

    /*
    // Update team players if captain/vice-captain
    if (selectedTeam.teamRole === 'captain' || selectedTeam.teamRole === 'vice-captain') {
      setTeamPlayers(selectedTeam.players || []);
    } else {
      setTeamPlayers([]);
    }
    */

    // Reset tournament selection so useEffect can auto-select the first tournament for new team
    setSelectedTournament('all');

    console.log('✅ Team switched to:', selectedTeam.teamName);
  };

  const handleTeamClick = (teamId) => {
    if (activeTeamFilter === teamId) {
      setActiveTeamFilter('all');
    } else {
      setActiveTeamFilter(teamId);
      handleTeamSwitch(teamId);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!clubInfo || !playerData) {
      console.log('🔄 Refresh skipped - no club or player data');
      return Promise.resolve();
    }

    console.log('🔄 Refreshing data...');
    await Promise.all([
      loadTournaments(clubInfo.id),
      loadGamesForPlayer(clubInfo.id, playerData.id),
      loadExpenses(clubInfo.id, playerData.id, false, playerData.oderId || playerData.oderedPlayerId),
      loadPaymentSubmissions(clubInfo.id, playerData.id),
      loadNotifications(playerData.id),
      loadNotifications(playerData.uid),
      loadEvents(clubInfo.id, playerData.id),
      loadClubMembers(clubInfo.id),
      loadUnassignedPlayers(clubInfo.id, playerData.teamId)
    ]);
    console.log('✅ Data refreshed');
    return Promise.resolve();
  }, [clubInfo, playerData]);

  // Derived state for events
  const upcomingEvents = useMemo(() => {
    return events.filter(event => {
      if (!event.date) return false;
      // Compare dates using local time strings to avoid timezone issues with today's events
      const today = new Date();
      const localTodayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const eventDateStr = String(event.date).split('T')[0];
      return eventDateStr >= localTodayStr;
    });
  }, [events]);

  const urgentUpcomingEvents = useMemo(() => {
    const today = new Date();
    const localTodayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Filter events (already future dated from upcomingEvents)
    const filteredEvents = upcomingEvents.map(e => ({ ...e, type: 'event' }));

    // Sort chronologically
    filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Show up to 10 latest events horizontally scrollable
    return filteredEvents.slice(0, 10);
  }, [upcomingEvents]);

  const urgentUpcomingGames = useMemo(() => {
    const today = new Date();
    const localTodayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Filter games (need to check if future)
    const filteredGames = gamesPlayed.filter(game => {
      if (!game.date) return false;
      const status = getGameStatus(game);
      if (status === 'Completed' || status === 'Abandoned' || status === 'Cancelled') return false;
      
      const gameDateStr = String(game.date).split('T')[0];
      return gameDateStr >= localTodayStr;
    }).map(g => ({ ...g, type: 'game' }));

    // Sort chronologically
    filteredGames.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Return all upcoming games so they can populate 'Up Next' and 'Down the Road' sections completely
    return filteredGames;
  }, [gamesPlayed]);

  const pastEvents = useMemo(() => {
    return events.filter(event => {
      if (!event.date) return false;
      const today = new Date();
      const localTodayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const eventDateStr = String(event.date).split('T')[0];
      return eventDateStr < localTodayStr;
    });
  }, [events]);

  const [showPastEvents, setShowPastEvents] = useState(false);

  const renderSingleEventCard = (event) => {
    const myRsvp = eventRsvps[event.id];
    const rsvpCounts = {
      yes: Object.values(event.rsvps || {}).filter(r => r.status === 'yes').length,
      no: Object.values(event.rsvps || {}).filter(r => r.status === 'no').length,
      maybe: Object.values(event.rsvps || {}).filter(r => r.status === 'maybe').length
    };

    // Get event type styling
    const eventTypeStyles = {
      social: { bg: 'bg-pink-100', text: 'text-pink-700', label: '🎉 Social Event' },
      meeting: { bg: 'bg-blue-100', text: 'text-blue-700', label: '📋 Meeting' },
      training: { bg: 'bg-green-100', text: 'text-green-700', label: '🏃 Training' },
      fundraiser: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '💰 Fundraiser' },
      other: { bg: 'bg-purple-100', text: 'text-purple-700', label: '📅 Event' }
    };
    const typeStyle = eventTypeStyles[event.eventType] || eventTypeStyles.other;

    return (
      <div key={event.id} className="p-4 border-2 rounded-xl hover:border-purple-300 transition-colors">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            {/* Event Type Badge */}
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${typeStyle.bg} ${typeStyle.text}`}>
              {typeStyle.label}
            </span>

            {/* Event Name */}
            <h4 className="font-bold text-lg">{event.name}</h4>

            {/* Event Details */}
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar size={14} className="text-purple-500" />
                <span>{formatDate(event.date, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>

              {event.time && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={14} className="text-purple-500" />
                  <span>{event.time}{event.endTime ? ` - ${event.endTime}` : ''}</span>
                </div>
              )}

              {event.venue && (
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-purple-500" />
                  <span className="text-sm text-gray-600">{event.venue}</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-xs font-semibold"
                  >
                    <MapPin size={12} />
                    Maps
                  </a>
                </div>
              )}

              {event.description && (
                <p className="text-sm text-gray-600 mt-2">{event.description}</p>
              )}
            </div>
          </div>


        </div>

        {/* RSVP Buttons */}
        <div className="pt-3 border-t">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600 mr-2">Your RSVP:</span>

            <button
              onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'yes', 0); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${myRsvp?.status === 'yes'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                }`}
            >
              <Check size={16} />
              Going
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'no'); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${myRsvp?.status === 'no'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                }`}
            >
              <X size={16} />
              Can't Go
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'maybe'); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${myRsvp?.status === 'maybe'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
                }`}
            >
              <HelpCircle size={16} />
              Maybe
            </button>

            {/* Guest count for "Going" */}
            {myRsvp?.status === 'yes' && event.allowGuests && (
              <div className="flex items-center gap-2 ml-4">
                <span className="text-sm text-gray-600">Bringing guests?</span>
                <select
                  value={myRsvp?.guests || 0}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleEventRsvp(event.id, 'yes', { guests: parseInt(e.target.value) })}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="0">Just me</option>
                  <option value="1">+1 guest</option>
                  <option value="2">+2 guests</option>
                  <option value="3">+3 guests</option>
                  <option value="4">+4 guests</option>
                </select>
              </div>
            )}

            {/* Meal Preference Row (Only show if requested by admin and player responded yes/maybe) */}
            {event.askMealPreference && (myRsvp?.status === 'yes' || myRsvp?.status === 'maybe') && (
              <div className="flex items-center gap-2 mt-3 w-full border-t pt-3 border-gray-100">
                <span className="text-sm text-gray-600 mr-2">Meal Preference:</span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, myRsvp.status, { mealPreference: 'veg' }); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${myRsvp?.mealPreference === 'veg'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                    }`}
                >
                  🥗 Veg
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, myRsvp.status, { mealPreference: 'non-veg' }); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${myRsvp?.mealPreference === 'non-veg'
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                    }`}
                >
                  🍗 Non-Veg
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, myRsvp.status, { mealPreference: 'opt-out' }); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${myRsvp?.mealPreference === 'opt-out'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                >
                  ❌ Opt Out
                </button>
              </div>
            )}

            {/* Note Section */}
            {true && (
              <div className="w-full mt-3 bg-gray-50 rounded-lg p-3">
                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                  <FileText size={12} />
                  Note for Organizer (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g. Will be 15 mins late"
                    className="flex-1 text-sm px-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                    value={editingEventNote[event.id] !== undefined ? editingEventNote[event.id] : (myRsvp?.note || '')}
                    onChange={(e) => setEditingEventNote(prev => ({ ...prev, [event.id]: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const noteValue = editingEventNote[event.id] !== undefined ? editingEventNote[event.id] : myRsvp?.note;
                      handleEventRsvp(event.id, myRsvp?.status || 'maybe', { note: noteValue || '' });
                      // clear local shadow state so it resets to saved value
                      setEditingEventNote(prev => {
                        const next = { ...prev };
                        delete next[event.id];
                        return next;
                      });
                    }}
                    className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-sm font-medium hover:bg-purple-200"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>

          {myRsvp && (
            <div className="flex justify-between items-center mt-3 pt-2 text-xs text-gray-400">
              <p>Responded {formatDate(myRsvp.respondedAt)}</p>
              {myRsvp.guests > 0 && <p className="font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">+{myRsvp.guests} guest{myRsvp.guests > 1 ? 's' : ''}</p>}
            </div>
          )}
        </div>

        {/* RSVP Stats Row */}
        <div className="mt-3 flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1 text-green-600 font-medium">
            <CheckCircle size={16} /> {rsvpCounts.yes}
          </span>
          <span className="flex items-center gap-1 text-red-600 font-medium">
            <XCircle size={16} /> {rsvpCounts.no}
          </span>
          <span className="flex items-center gap-1 text-yellow-600 font-medium">
            <HelpCircle size={16} /> {rsvpCounts.maybe}
          </span>
        </div>

        {/* View Details Toggle - Placed below RSVP options */}
        <div className="mt-2 flex">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpandedEventId(expandedEventId === event.id ? null : event.id);
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            {expandedEventId === event.id ? 'Hide responses' : 'View responses'}
            <ChevronDown size={14} className={`transition-transform ${expandedEventId === event.id ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Expanded RSVP Details */}
        {expandedEventId === event.id && (
          <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50 rounded-lg p-4">
            <h5 className="font-semibold text-gray-700 mb-3 text-sm">RSVP List</h5>
            <div className="space-y-2 text-sm">
              {/* Going */}
              <div>
                <span className="font-bold text-green-700">✓ Going ({rsvpCounts.yes}): </span>
                <span className="text-gray-600">
                  {rsvpCounts.yes > 0 ? (
                    Object.entries(event.rsvps || {})
                      .filter(([_, r]) => r.status === 'yes')
                      .map(([uid, r]) => {
                        let name = allClubMembers[uid]?.name || 'Unknown';
                        if (r.guests > 0) name += ` (+${r.guests})`;
                        if (r.note) name += ` - "${r.note}"`;
                        return name;
                      })
                      .join(', ')
                  ) : (
                    <span className="italic text-gray-400">None</span>
                  )}
                </span>
              </div>

              {/* Can't Go */}
              <div>
                <span className="font-bold text-red-700">✗ Can't Go ({rsvpCounts.no}): </span>
                <span className="text-gray-600">
                  {rsvpCounts.no > 0 ? (
                    Object.entries(event.rsvps || {})
                      .filter(([_, r]) => r.status === 'no')
                      .map(([uid, r]) => {
                        let name = allClubMembers[uid]?.name || 'Unknown';
                        if (r.note) name += ` - "${r.note}"`;
                        return name;
                      })
                      .join(', ')
                  ) : (
                    <span className="italic text-gray-400">None</span>
                  )}
                </span>
              </div>

              {/* Maybe */}
              <div>
                <span className="font-bold text-yellow-700">? Maybe ({rsvpCounts.maybe}): </span>
                <span className="text-gray-600">
                  {rsvpCounts.maybe > 0 ? (
                    Object.entries(event.rsvps || {})
                      .filter(([_, r]) => r.status === 'maybe')
                      .map(([uid, r]) => {
                        let name = allClubMembers[uid]?.name || 'Unknown';
                        if (r.note) name += ` - "${r.note}"`;
                        return name;
                      })
                      .join(', ')
                  ) : (
                    <span className="italic text-gray-400">None</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const [expandedGameDetails, setExpandedGameDetails] = useState({});

  const toggleGameDetails = (gameId) => {
    setExpandedGameDetails(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }));
  };

  const renderSingleGameListRow = (game) => {
    const isDateOnly = typeof game.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(game.date);
    let gameDateTime = new Date(game.date);
    if (isDateOnly) {
      gameDateTime.setMinutes(gameDateTime.getMinutes() + gameDateTime.getTimezoneOffset());
    }
    if (game.time) {
      const [hh, mm] = game.time.split(':').map(Number);
      if (!isNaN(hh) && !isNaN(mm)) {
        gameDateTime.setHours(hh, mm, 0, 0);
      }
    } else {
      gameDateTime.setHours(23, 59, 59, 999);
    }

    const playerAvailability = availability[game.id];
    const isPast = gameDateTime < new Date();
    const rsvpCounts = getAvailabilityStats(game);
    const isExpanded = expandedGameDetails[game.id];
    const teamRole = getGameTeamRole(game);
    const isCaptainOrVC = teamRole === 'captain' || teamRole === 'vice-captain';
    const isInPlaying11 = game.playing11?.includes(user?.uid);

    return (
      <React.Fragment key={game.id}>
        <tr className={`hover:bg-gray-50 transition-colors cursor-pointer ${getGameStatus(game) === 'Cancelled' ? 'opacity-75 bg-red-50' :
          getGameStatus(game) === 'Abandoned' ? 'bg-amber-50' :
            getGameStatus(game) === 'Completed' ? 'bg-green-50' : ''
          } ${isExpanded ? 'border-b-0' : 'border-b border-gray-100'} ${game.isNotRostered ? 'opacity-60 saturate-50 hover:opacity-100 hover:saturate-100 grayscale-[20%]' : ''}`}
          onClick={() => toggleGameDetails(game.id)}
        >
          <td className="p-3 text-sm align-middle">
            <div className="flex flex-col">
              <span className={`font-semibold ${getGameStatus(game) === 'Cancelled' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                {formatDate(game.date, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              {getGameStatus(game) !== 'Scheduled' && (
                <span className="text-[10px] uppercase font-bold text-gray-500 mt-0.5">{getGameStatus(game)}</span>
              )}
            </div>
          </td>
          <td className="p-3 text-sm align-middle font-medium text-gray-900">
            <div className="flex items-center gap-2">
              {game.name ? (
                <div className="flex flex-col">
                  <span>{game.name}</span>
                  {game.opponent && !game.name.toLowerCase().includes(game.opponent.toLowerCase()) && (
                    <span className="text-xs text-gray-500 font-normal">vs {game.opponent}</span>
                  )}
                </div>
              ) : (
                <span>{game.opponent ? `vs ${game.opponent}` : <span className="text-gray-400 italic">TBD</span>}</span>
              )}
              {isInPlaying11 && (
                <span className="whitespace-nowrap text-[10px] bg-green-600 text-white px-2 py-0.5 rounded-full font-medium shadow-sm">
                  ✓ In Squad
                </span>
              )}
              {!isInPlaying11 && playerAvailability?.status === 'available' && game.isSquadPublished && (
                <span className="whitespace-nowrap text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full font-medium shadow-sm">
                  ✗ Not in Squad
                </span>
              )}
            </div>
          </td>
          <td className="p-3 text-sm text-gray-600 align-middle">
            {game.time ? formatTime(game.time) : '-'}
          </td>
          <td className="p-3 text-sm text-gray-600 align-middle">
            {game.arrivalTime ? formatTime(game.arrivalTime) : '-'}
          </td>
          <td className="p-3 text-sm text-gray-600 align-middle">
            {game.venue ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.venue)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-purple-600 flex items-center gap-1"
                title={game.venue}
                onClick={(e) => e.stopPropagation()}
              >
                <MapPin size={12} className="shrink-0 text-purple-500" />
                <span className="truncate max-w-[120px] block">{game.venue}</span>
              </a>
            ) : '-'}
          </td>
          <td className="p-3 text-sm align-middle text-center">
            {!game.isNotRostered && !isPast && (
              <div className="flex items-center justify-center gap-1.5 border border-gray-200 rounded-full py-1 px-1.5 w-fit mx-auto bg-white shadow-sm">
                <button
                  onClick={(e) => { e.stopPropagation(); handleAvailability(game.id, 'available'); }}
                  className={`p-1 rounded-full transition-colors ${playerAvailability?.status === 'available' ? 'bg-green-600 text-white' : 'text-gray-400 hover:bg-green-100 hover:text-green-600'}`}
                  title="Available"
                >
                  <Check size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAvailability(game.id, 'unavailable'); }}
                  className={`p-1 rounded-full transition-colors ${playerAvailability?.status === 'unavailable' ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-red-100 hover:text-red-600'}`}
                  title="Unavailable"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAvailability(game.id, 'maybe'); }}
                  className={`p-1 rounded-full transition-colors ${playerAvailability?.status === 'maybe' ? 'bg-yellow-500 text-white' : 'text-gray-400 hover:bg-yellow-100 hover:text-yellow-600'}`}
                  title="Maybe"
                >
                  <HelpCircle size={14} strokeWidth={2.5} />
                </button>
              </div>
            )}
            {!game.isNotRostered && isPast && <span className="text-gray-400 text-xs italic">Closed</span>}
            {game.isNotRostered && <span className="text-gray-400 text-xs">-</span>}
          </td>
          <td className="p-3 text-xs align-middle text-center whitespace-nowrap">
            <div 
              className="inline-flex gap-2 justify-center items-center bg-gray-50 rounded-lg px-2 py-1 border border-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedAvailability(prev => ({ ...prev, [game.id]: !prev[game.id] }));
              }}
            >
              <div className="flex items-center gap-1 text-green-700" title="Available">
                 <Check size={12} strokeWidth={3} />
                 <span className="font-bold text-sm">{rsvpCounts.available}</span>
              </div>
              <div className="flex items-center gap-1 text-red-600" title="Unavailable">
                 <X size={12} strokeWidth={3} />
                 <span className="font-bold text-sm">{rsvpCounts.no}</span>
              </div>
              <div className="flex items-center gap-1 text-yellow-600" title="Maybe">
                 <HelpCircle size={12} strokeWidth={3} />
                 <span className="font-bold text-sm">{rsvpCounts.maybe}</span>
              </div>
              <ChevronDown size={14} className={`ml-1 text-gray-400 transition-transform ${expandedAvailability[game.id] ? 'rotate-180' : ''}`} />
            </div>
          </td>
        </tr>
        
        {isExpanded && (
          <tr className="bg-gray-50/50 border-b border-gray-200">
            <td colSpan={7} className="p-4">
              <div className="flex flex-col gap-4 max-w-4xl">
                
                {/* Actions Row */}
                <div className="flex flex-wrap items-center gap-4">
                  {(isCaptainOrVC || isUserAdmin) && (
                    <div className="flex flex-wrap items-center gap-2 border-r border-gray-300 pr-4 mr-2">
                       <button
                         onClick={(e) => { e.stopPropagation(); handleSelectSquad(game.id); }}
                         className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors bg-purple-600 text-white hover:bg-purple-700"
                       >
                         {game.playing11 && game.playing11.length > 0 ? 'Edit Squad' : 'Select Squad'}
                       </button>
                       <button
                         onClick={(e) => { e.stopPropagation(); setEditingRecapGame(game); }}
                         className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors bg-blue-600 text-white hover:bg-blue-700"
                       >
                         📝 Match Recap
                       </button>
                       <select
                         value={game.status || ''}
                         onChange={(e) => handleUpdateGameStatus(game.id, e.target.value)}
                         className={`px-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-purple-500 font-medium ${!game.status ? 'bg-white text-gray-700' :
                           game.status === 'Cancelled' ? 'bg-red-50 border-red-200 text-red-900' :
                             game.status === 'Abandoned' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                               'bg-green-50 border-green-200 text-green-900'
                           }`}
                       >
                         <option value="">Status: Auto</option>
                         <option value="Completed">Status: Completed</option>
                         <option value="Abandoned">Status: Abandoned</option>
                         <option value="Cancelled">Status: Cancelled</option>
                       </select>
                    </div>
                  )}

                  {/* 
                  <button
                    onClick={(e) => { e.stopPropagation(); setUploadingMediaGame(game); }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm"
                  >
                    <Camera size={14} />
                    Upload Media
                  </button>
                  */}

                  {/* Meal Preference Selection */}
                  {!isPast && game.askMealPreference && playerAvailability?.status !== 'unavailable' && (
                    <div className="flex items-center gap-2 flex-wrap bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                      <span className="text-xs text-gray-600 flex items-center gap-1">🍽️ Meal:</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMealPreference(game.id, 'veg'); }}
                        className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${game.mealPreferences?.[playerData?.id]?.preference === 'veg'
                          ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                      >🥗 Veg</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMealPreference(game.id, 'non-veg'); }}
                        className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${game.mealPreferences?.[playerData?.id]?.preference === 'non-veg'
                          ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                      >🍗 Non-Veg</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMealPreference(game.id, 'opt-out'); }}
                        className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${game.mealPreferences?.[playerData?.id]?.preference === 'opt-out'
                          ? 'bg-gray-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                      >❌ Opt Out</button>
                    </div>
                  )}
                </div>

                {/* Note Section */}
                {!isPast && (
                  <div className="w-full max-w-md bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                      <FileText size={12} />
                      Note for Captain (optional)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g. Will be 15 mins late"
                        className="flex-1 text-sm px-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                        value={editingGameNote[game.id] !== undefined ? editingGameNote[game.id] : (playerAvailability?.note || '')}
                        onChange={(e) => setEditingGameNote(prev => ({ ...prev, [game.id]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const noteValue = editingGameNote[game.id] !== undefined ? editingGameNote[game.id] : playerAvailability?.note;
                          handleAvailability(game.id, playerAvailability?.status || 'maybe', { note: noteValue || '' });
                          setEditingGameNote(prev => {
                            const next = { ...prev };
                            delete next[game.id];
                            return next;
                          });
                        }}
                        className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-sm font-medium hover:bg-purple-200"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Match Recap Section */}
                {(game.matchRecap || (game.memorablePerformances && game.memorablePerformances.length > 0)) && (
                  <div className="mt-2 p-4 bg-blue-50/50 rounded-xl border border-blue-100 w-full">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                      <FileText size={16} className="text-blue-600" />
                      Match Recap
                    </h4>
                    {game.matchRecap && (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">
                        {game.matchRecap}
                      </p>
                    )}

                    {game.memorablePerformances && game.memorablePerformances.length > 0 && (
                      <div className="space-y-2 mt-3 pt-3 border-t border-blue-100/50">
                        <h5 className="font-semibold text-xs text-gray-600 uppercase tracking-wider">Highlights</h5>
                        <ul className="space-y-2">
                          {game.memorablePerformances.map((perf, idx) => (
                            <li key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 flex flex-col gap-1">
                              <span className="font-semibold text-gray-900">
                                {Array.from(new Set(perf.playerIds.map(id => allClubMembers[id]?.name || 'Unknown Player'))).join(', ')}
                              </span>
                              <span className="text-gray-600">{perf.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </td>
          </tr>
        )}

        {/* Dedicated Team Responses Expanded Row */}
        {(() => {
          const isAvailabilityExpanded = expandedAvailability[game.id];
          if (!isAvailabilityExpanded) return null;
          
          const details = getAvailabilityDetails(game);

          return (
            <tr className="bg-gray-50/30 border-b border-gray-200">
              <td colSpan={7} className="p-4">
                <div className="mt-2 p-4 bg-white border border-gray-200 rounded-lg text-sm space-y-3 w-full max-w-4xl shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-2 border-b pb-2">Player Responses</h4>
                  {details.availablePlayers.length > 0 && (
                    <div>
                      <span className="text-green-700 font-semibold flex items-center gap-1 mb-1"><Check size={14}/> Available ({details.availablePlayers.length})</span>
                      <span className="text-gray-600 leading-relaxed block ml-5">{details.availablePlayers.join(', ')}</span>
                    </div>
                  )}
                  {details.unavailablePlayers.length > 0 && (
                    <div>
                      <span className="text-red-700 font-semibold flex items-center gap-1 mb-1"><X size={14}/> Unavailable ({details.unavailablePlayers.length})</span>
                      <span className="text-gray-600 leading-relaxed block ml-5">{details.unavailablePlayers.join(', ')}</span>
                    </div>
                  )}
                  {details.maybePlayers.length > 0 && (
                    <div>
                      <span className="text-yellow-700 font-semibold flex items-center gap-1 mb-1"><HelpCircle size={14}/> Maybe ({details.maybePlayers.length})</span>
                      <span className="text-gray-600 leading-relaxed block ml-5">{details.maybePlayers.join(', ')}</span>
                    </div>
                  )}
                  {details.notRespondedPlayers.length > 0 && (
                    <div>
                      <span className="text-gray-500 font-semibold flex items-center gap-1 mb-1">⏳ Not responded ({details.notRespondedPlayers.length})</span>
                      <span className="text-gray-400 leading-relaxed block ml-5">{details.notRespondedPlayers.join(', ')}</span>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        })()}
      </React.Fragment>
    );
  };

  const renderSingleEventListRow = (event) => {
    const eventDate = new Date(event.date);
    const isPast = eventDate < new Date();
    const playerRsvp = eventRsvps[event.id];

    let rsvpCounts = { attending: 0, no: 0, maybe: 0 };
    if (event.rsvps) {
       Object.values(event.rsvps).forEach(r => {
         if (r.status === 'yes' || r.status === 'attending' || r.status === 'available') rsvpCounts.attending++;
         else if (r.status === 'no' || r.status === 'unavailable') rsvpCounts.no++;
         else if (r.status === 'maybe') rsvpCounts.maybe++;
       });
    }

    return (
      <tr key={event.id} className="hover:bg-gray-50 bg-indigo-50/20 transition-colors">
        <td className="p-3 text-sm align-middle">
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">{formatDate(event.date, { month: 'short', day: 'numeric' })}</span>
            <span className="text-[10px] uppercase font-bold text-indigo-600 mt-0.5">Event</span>
          </div>
        </td>
        <td className="p-3 text-sm align-middle" colSpan={4}>
          <div className="flex flex-col">
             <span className="font-bold text-gray-900">{event.title}</span>
             {event.location && <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin size={10} className="text-purple-400"/> {event.location}</span>}
          </div>
        </td>
        <td className="p-3 text-sm align-middle text-center">
          {!isPast && (
            <div className="flex items-center justify-center gap-1.5 border border-gray-200 rounded-full py-1 px-1.5 w-fit mx-auto bg-white shadow-sm">
              <button
                onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'yes'); }}
                className={`p-1 rounded-full transition-colors ${playerRsvp?.status === 'yes' || playerRsvp?.status === 'attending' ? 'bg-green-600 text-white' : 'text-gray-400 hover:bg-green-100 hover:text-green-600'}`}
                title="Going"
              >
                <Check size={14} strokeWidth={2.5} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'no'); }}
                className={`p-1 rounded-full transition-colors ${playerRsvp?.status === 'no' || playerRsvp?.status === 'unavailable' ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-red-100 hover:text-red-600'}`}
                title="Not Going"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleEventRsvp(event.id, 'maybe'); }}
                className={`p-1 rounded-full transition-colors ${playerRsvp?.status === 'maybe' ? 'bg-yellow-500 text-white' : 'text-gray-400 hover:bg-yellow-100 hover:text-yellow-600'}`}
                title="Maybe"
              >
                <HelpCircle size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}
          {isPast && <span className="text-gray-400 text-xs italic">Closed</span>}
        </td>
        <td className="p-3 text-xs align-middle text-center whitespace-nowrap">
          <div className="inline-flex gap-3 justify-center items-center bg-gray-50 rounded-lg px-2 py-1 border border-gray-100">
            <div className="flex items-center gap-1 text-green-700" title="Going">
               <Check size={12} strokeWidth={3} />
               <span className="font-bold text-sm">{rsvpCounts.attending}</span>
            </div>
            <div className="flex items-center gap-1 text-red-600" title="Not Going">
               <X size={12} strokeWidth={3} />
               <span className="font-bold text-sm">{rsvpCounts.no}</span>
            </div>
            <div className="flex items-center gap-1 text-yellow-600" title="Maybe">
               <HelpCircle size={12} strokeWidth={3} />
               <span className="font-bold text-sm">{rsvpCounts.maybe}</span>
            </div>
          </div>
        </td>
      </tr>
    );
  };


  const renderSingleGameCard = (game) => {
    const isDateOnly = typeof game.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(game.date);
    let gameDateTime = new Date(game.date);
    if (isDateOnly) {
      gameDateTime.setMinutes(gameDateTime.getMinutes() + gameDateTime.getTimezoneOffset());
    }
    if (game.time) {
      const [hh, mm] = game.time.split(':').map(Number);
      if (!isNaN(hh) && !isNaN(mm)) {
        gameDateTime.setHours(hh, mm, 0, 0);
      }
    } else {
      gameDateTime.setHours(23, 59, 59, 999);
    }

    const playerAvailability = availability[game.id];
    const isPast = gameDateTime < new Date();
    // Check if player is selected in playing 11
    const isInPlaying11 = game.playing11?.includes(playerData?.id);
    const gameTeamRole = getGameTeamRole(game);
    const isCaptain = gameTeamRole === 'captain' || gameTeamRole === 'vice-captain';

    // Format date like "Saturday, 10th Jan"
    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[gameDateTime.getDay()];
    const dayOfMonth = gameDateTime.getDate();
    const monthName = months[gameDateTime.getMonth()];

    const formattedMockupDate = `${dayName}, ${getOrdinal(dayOfMonth)} ${monthName}`;

    // Calculate days until game
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const gDateFloor = new Date(gameDateTime);
    gDateFloor.setHours(0, 0, 0, 0);
    const diffTime = gDateFloor - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    let timeText = '';
    if (diffDays > 0) {
      if (diffDays === 1) timeText = '... tomorrow';
      else timeText = `... in ${diffDays} days`;
    } else if (diffDays === 0) {
      timeText = '... today';
    }

    // Determine Status Pill
    let statusPill = null;
    if (game.isNotRostered) {
      statusPill = <span className="text-gray-800 text-[13px] font-medium px-2 py-1 rounded bg-black/5 leading-none">Not in Team Roster</span>;
    } else if (isPast) {
      statusPill = <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-bold tracking-wide">Past Game</span>;
    } else if (isClubArchivedForUser) {
      statusPill = <span className="bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-full text-[12px] font-bold tracking-wide flex items-center gap-1 shadow-sm"><Lock size={12} strokeWidth={3} /> Archived</span>;
    } else if (isInPlaying11) {
      statusPill = <span className="bg-[#00C440] text-white px-3 py-1.5 rounded-full text-[12px] font-bold tracking-wide shadow-sm">Playing</span>;
    } else if (playerAvailability?.status === 'available') {
      statusPill = <span className="bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full text-[12px] font-bold tracking-wide">Available</span>;
    } else if (playerAvailability?.status === 'unavailable') {
      statusPill = <span className="bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-full text-[12px] font-bold tracking-wide">Not Going</span>;
    } else if (playerAvailability?.status === 'maybe') {
      statusPill = <span className="bg-yellow-50 text-yellow-700 border border-yellow-200 px-3 py-1 rounded-full text-[12px] font-bold tracking-wide">Maybe</span>;
    } else {
      statusPill = (
        <button
          className="bg-[#D1D5DB] hover:bg-gray-400 text-gray-800 px-4 py-1.5 rounded-full text-[12px] font-bold tracking-wide transition-colors shadow-sm"
        >
          Provide Availability
        </button>
      );
    }

    return (
      <div
        key={game.id}
        className={`${game.isNotRostered ? 'bg-[#E5E5E5]' : 'bg-white'} rounded-[16px] shadow-sm border border-gray-100 p-3.5 relative overflow-hidden flex flex-col gap-2`}
      >
        {/* Top Section */}
        <div className="flex justify-between items-start">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="font-extrabold text-gray-900 text-[18px] tracking-tight">
              {formattedMockupDate}
            </h4>
            {timeText && <span className="text-[13px] italic text-gray-500 tracking-tight">{timeText}</span>}
          </div>
          <div 
            className={`flex-shrink-0 ml-2 ${(!isPast && !game.isNotRostered && !isClubArchivedForUser) ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isPast && !game.isNotRostered && !isClubArchivedForUser) {
                if (selectedGameForAvailability?.id === game.id) {
                  setSelectedGameForAvailability(null);
                } else {
                  setSelectedGameForAvailability({ id: game.id, currentAvailability: playerAvailability?.status || null, note: playerAvailability?.note || '' });
                }
              }
            }}
          >
            {statusPill}
          </div>
        </div>

        {/* Details Section */}
        <div className="space-y-1.5 font-medium -mt-1">
          {game.name && (
            <div className="text-[15px] font-bold text-gray-800 leading-tight">
              {game.name}
            </div>
          )}
          {!game.name && game.opponent && (
             <div className="text-[15px] font-bold text-gray-800 leading-tight">
               vs {game.opponent}
             </div>
          )}
          <div className="text-[13px] text-gray-600 flex items-center gap-x-4 gap-y-1.5 flex-wrap">
            <span className="flex items-center gap-1 font-semibold">
              <Clock size={14} className="opacity-70 text-gray-500" />
              {game.time ? formatTime(game.time) : 'TBD'}
              {game.endTime ? ` - ${formatTime(game.endTime)}` : ''}
              {/* Cutoff pill moved right beside the time */}
              {game.lateFeeCutoffTime && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-bold tracking-wide">
                  Cutoff: {formatTime(game.lateFeeCutoffTime)}
                </span>
              )}
            </span>
            {game.venue && (
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="flex items-center gap-1">
                  <MapPin size={14} className="opacity-70 text-gray-500" />
                  {game.venue}
                </span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.venue)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded text-[11px] font-bold transition-colors shadow-sm ml-1"
                >
                  Directions
                </a>
              </span>
            )}
          </div>
        </div>

        {/* Bottom Section - Tags */}
        <div className="flex justify-start items-center mt-1 pt-2.5 border-t border-gray-100 overflow-hidden">
          <div className="flex gap-2 flex-nowrap items-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <span className="px-2.5 py-1 bg-[#F6F1E3] text-[#8C7A4A] rounded-md text-[10px] font-bold uppercase tracking-wider whitespace-nowrap flex-shrink-0">
              {getComputedGameFormat(game).toUpperCase()}
            </span>
            {game.tournamentId && (
              <span className="px-2.5 py-1 bg-[#E8F4FD] text-[#02669C] rounded-md text-[10px] font-bold uppercase tracking-wider truncate inline-block max-w-full shrink">
                {(game.tournamentName || '').trim() || 'Tournament'}
              </span>
            )}
          </div>
        </div>

        {/* Expanded Availability Input & Options for Players */}
        {selectedGameForAvailability?.id === game.id && !isClubArchivedForUser && (
          <div className="mt-4 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-2 duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex flex-wrap items-center gap-2 mb-4 justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedGameForAvailability(prev => ({ ...prev, currentAvailability: 'available' }));
                  handleAvailability(game.id, 'available', { note: selectedGameForAvailability?.note || '' });
                  setSelectedGameForAvailability(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm flex-1 ${selectedGameForAvailability?.currentAvailability === 'available' ? 'bg-[#00C440] text-white border-2 border-green-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Available
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedGameForAvailability(prev => ({ ...prev, currentAvailability: 'unavailable' }));
                  handleAvailability(game.id, 'unavailable', { note: selectedGameForAvailability?.note || '' });
                  setSelectedGameForAvailability(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm flex-1 ${selectedGameForAvailability?.currentAvailability === 'unavailable' ? 'bg-red-500 text-white border-2 border-red-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Unavailable
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedGameForAvailability(prev => ({ ...prev, currentAvailability: 'maybe' }));
                }}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm flex-1 ${selectedGameForAvailability?.currentAvailability === 'maybe' ? 'bg-yellow-500 text-white border-2 border-yellow-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Maybe
              </button>
            </div>

            {/* Meal Preference Selection */}
            {!isPast && game.askMealPreference && playerAvailability?.status !== 'unavailable' && (
              <div className="flex items-center gap-2 flex-wrap bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm mt-3 mb-3">
                <span className="text-xs text-gray-600 flex items-center gap-1">🍽️ Meal:</span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleMealPreference(game.id, 'veg'); }}
                  className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${game.mealPreferences?.[playerData?.id]?.preference === 'veg'
                    ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                >🥗 Veg</button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleMealPreference(game.id, 'non-veg'); }}
                  className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${game.mealPreferences?.[playerData?.id]?.preference === 'non-veg'
                    ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                >🍗 Non-Veg</button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleMealPreference(game.id, 'opt-out'); }}
                  className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${game.mealPreferences?.[playerData?.id]?.preference === 'opt-out'
                    ? 'bg-gray-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                >❌ Opt Out</button>
              </div>
            )}

            <div className="flex w-full gap-2 mb-4 mt-2">
              <input
                type="text"
                placeholder={selectedGameForAvailability?.currentAvailability === 'maybe' ? 'Note for Captain (mandatory)' : 'Note for Captain (optional)...'}
                className="flex-1 text-sm px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 outline-none focus:bg-white focus:border-blue-400 transition-all"
                value={selectedGameForAvailability.note || ''}
                onChange={(e) => setSelectedGameForAvailability(prev => ({ ...prev, note: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                disabled={selectedGameForAvailability?.currentAvailability === 'maybe' && (!selectedGameForAvailability.note || selectedGameForAvailability.note.trim() === '')}
                onClick={(e) => {
                  e.stopPropagation();
                  const statusToSave = selectedGameForAvailability?.currentAvailability || playerAvailability?.status || 'maybe';
                  handleAvailability(game.id, statusToSave, { note: selectedGameForAvailability?.note || '' });
                  setSelectedGameForAvailability(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors shadow-sm ${selectedGameForAvailability?.currentAvailability === 'maybe' && (!selectedGameForAvailability.note || selectedGameForAvailability.note.trim() === '') ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#3B82F6] hover:bg-blue-600 text-white'}`}
              >
                Save
              </button>
            </div>

            {/* Note details listed when previously set */}
            {playerAvailability?.note && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mt-2 flex justify-between items-start">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-blue-800 block mb-1">Your Note:</span>
                  {playerAvailability.note}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGameForAvailability(prev => ({
                      ...(prev || {}),
                      id: game.id,
                      note: ''
                    }));
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Edit
                </button>
              </div>
            )}
            
          </div>
        )}

        {/* Media Gallery / Match Recap Public */}
        {(game.memorablePerformances?.length > 0 || game.matchRecap) && (
          <div className="mt-2 flex gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setExpandedGameDetails(prev => ({ ...prev, [game.id]: !prev[game.id] }))}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
            >
              View Recap
            </button>
          </div>
        )}
        <div className="empty:hidden [&:not(:empty)]:mt-2" onClick={e => e.stopPropagation()}>
          <MediaGalleryPreview gameId={game.id} />
        </div>

        {/* View Recap Expansion */}
        {expandedGameDetails[game.id] && ( game.matchRecap || (game.memorablePerformances && game.memorablePerformances.length > 0) ) && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl animate-in fade-in duration-200" onClick={e => e.stopPropagation()}>
             {game.matchRecap && (
               <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                 {game.matchRecap}
               </div>
             )}
             {game.memorablePerformances?.length > 0 && (
               <div className="space-y-2 mt-3 pt-3 border-t border-blue-200/50">
                 <h5 className="font-semibold text-xs text-blue-800 uppercase tracking-wider">Highlights</h5>
                 <ul className="space-y-1">
                   {game.memorablePerformances.map((perf, idx) => (
                     <li key={idx} className="text-[13px] bg-white px-2.5 py-1.5 rounded border border-blue-100">
                       <span className="font-bold text-gray-900 block mb-0.5">
                         {Array.from(new Set(perf.playerIds.map(id => allClubMembers[id]?.name || 'Unknown Player'))).join(', ')}
                       </span>
                       <span className="text-gray-600 block">{perf.description}</span>
                     </li>
                   ))}
                 </ul>
               </div>
             )}
          </div>
        )}

        {/* Action Controls */}
        <div className="mt-2 pt-2.5 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-wrap gap-2 items-center">
            {(isCaptain || isUserAdmin) && (!isPast || isUserAdmin) && (
              <>
                <button
                  onClick={() => handleSelectSquad(game.id)}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-[13px] font-bold hover:bg-purple-700 transition-colors"
                >
                  {game.playing11?.length > 0 ? 'Edit Squad' : 'Select Squad'}
                </button>
                <select
                  value={game.status || ''}
                  onChange={(e) => handleUpdateGameStatus(game.id, e.target.value)}
                  className={`px-2 py-1.5 text-[13px] border rounded-lg focus:ring-2 focus:ring-purple-500 font-semibold ${!game.status ? 'bg-slate-50 text-slate-700 border-slate-200' :
                    game.status === 'Cancelled' ? 'bg-red-50 border-red-200 text-red-900' :
                      game.status === 'Abandoned' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                        'bg-green-50 border-green-200 text-green-900'
                    }`}
                >
                  <option value="">Status: Auto</option>
                  <option value="Completed">Completed</option>
                  <option value="Abandoned">Abandoned</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </>
            )}
            
            {isCaptain && (
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingRecapGame(game); }}
                  className="px-3 py-1.5 rounded-lg text-[13px] font-bold transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                  Match Recap
                </button>
            )}
            
            {/*
            <button
               onClick={(e) => { e.stopPropagation(); setUploadingMediaGame(game); }}
               className="px-3 py-1.5 rounded-lg text-[13px] font-bold transition-colors flex items-center gap-1 bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm"
            >
               <Camera size={14} /> Upload Media
            </button>
            */}
          </div>
          
          {!isPast && (
            <div className="flex mt-2">
              <button
                onClick={() => setExpandedAvailability(prev => ({ ...prev, [game.id]: !prev[game.id] }))}
                className="flex-1 px-3 py-2 bg-slate-50 text-slate-700 rounded-lg text-[13px] font-bold border border-slate-200 hover:bg-slate-100 transition-colors flex items-center justify-center gap-1"
              >
                Team Responses {expandedAvailability[game.id] ? <ChevronDown size={14} className="rotate-180" /> : <ChevronDown size={14} />}
              </button>
            </div>
          )}
        </div>

        {/* Expanded Availability View */}
        {expandedAvailability[game.id] && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs space-y-2 border border-gray-100" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const details = getAvailabilityDetails(game);
              return (
                <>
                  {details.availablePlayers.length > 0 && <div><span className="text-green-700 font-medium">✓ Available ({details.availablePlayers.length}): </span><span className="text-gray-600">{details.availablePlayers.join(', ')}</span></div>}
                  {details.unavailablePlayers.length > 0 && <div><span className="text-red-700 font-medium">✗ Unavailable ({details.unavailablePlayers.length}): </span><span className="text-gray-600">{details.unavailablePlayers.join(', ')}</span></div>}
                  {details.maybePlayers.length > 0 && <div><span className="text-yellow-700 font-medium">? Maybe ({details.maybePlayers.length}): </span><span className="text-gray-600">{details.maybePlayers.join(', ')}</span></div>}
                  {details.notRespondedPlayers.length > 0 && <div><span className="text-gray-500 font-medium">⏳ Pending ({details.notRespondedPlayers.length}): </span><span className="text-gray-400">{details.notRespondedPlayers.join(', ')}</span></div>}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderClubEvents = (eventsList, title = 'Club Events', showIfEmpty = true, isCollapsible = false, isExpanded = true, setExpanded = null, pastEventsList = []) => {
    if (!showIfEmpty && eventsList.length === 0) return null;

    const hasGames = eventsList.some(e => e.type === 'game') || pastEventsList.some(e => e.type === 'game');

    return (
      <div id="club-events" className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div
          className={`flex items-center justify-between gap-2 mb-6 pb-4 border-b ${isCollapsible ? 'cursor-pointer hover:bg-gray-50 -mx-6 px-6 pt-2 rounded-t-xl transition-colors' : ''}`}
          onClick={() => isCollapsible && setExpanded && setExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <PartyPopper className="text-purple-600" size={24} />
            <h3 className="text-xl font-bold">{title}</h3>
            <span className="text-sm text-gray-500">({eventsList.length + pastEventsList.length})</span>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            {hasGames && (
              <div className="flex bg-gray-100 rounded-lg p-1" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setEventsViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${eventsViewMode === 'grid'
                  ? 'bg-white shadow-sm text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="Grid View"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setEventsViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${eventsViewMode === 'list'
                  ? 'bg-white shadow-sm text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="List View"
              >
                <List size={16} />
              </button>
              </div>
            )}

            {isCollapsible && (
              <ChevronDown
                size={20}
                className={`text-gray-400 transition-transform duration-300 ${!isExpanded ? '-rotate-90' : ''}`}
              />
            )}
          </div>
        </div>

        {(!isCollapsible || isExpanded) && (
          <div className="flex flex-col gap-6">
            
            {eventsList.length === 0 && pastEventsList.length === 0 && (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <PartyPopper className="mx-auto mb-2 opacity-50" size={32} />
                <p>No upcoming events or games</p>
              </div>
            )}

            {eventsList.length > 0 && (
              <>
                {!hasGames || eventsViewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {eventsList.map(item => {
                      if (item.type === 'game') return renderSingleGameCard(item);
                      return renderSingleEventCard(item);
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto w-full border border-gray-200 rounded-xl shadow-sm bg-white">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr className="text-xs uppercase tracking-wider text-gray-500 font-semibold text-left">
                          <th className="p-3 whitespace-nowrap">Date</th>
                          <th className="p-3">Opponent</th>
                          <th className="p-3 whitespace-nowrap">Time</th>
                          <th className="p-3 whitespace-nowrap">Arrival</th>
                          <th className="p-3">Address</th>
                          <th className="p-3 text-center">Available</th>
                          <th className="p-3 text-center">Team Responses</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {eventsList.map(item => {
                          if (item.type === 'game') return renderSingleGameListRow(item);
                          return renderSingleEventListRow(item);
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Past Events Section */}
            {pastEventsList.length > 0 && (
              <div className="mt-2 pt-4 border-t border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer py-2 hover:bg-gray-50 rounded-lg px-2 -mx-2 mb-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPastEvents(!showPastEvents);
                  }}
                >
                  <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                    <History size={16} />
                    Past Events ({pastEventsList.length})
                  </h4>
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform duration-300 ${showPastEvents ? 'rotate-180' : ''}`}
                  />
                </div>

                {showPastEvents && (
                  <div className={!hasGames || eventsViewMode === 'grid'
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2"
                    : "overflow-x-auto w-full border border-gray-200 rounded-xl shadow-sm bg-gray-50 opacity-80 mt-2"
                  }>
                    {!hasGames || eventsViewMode === 'grid' ? (
                       pastEventsList.map(item => {
                         if (item.type === 'game') return renderSingleGameCard(item);
                         return renderSingleEventCard(item);
                       })
                    ) : (
                       <table className="w-full text-left border-collapse min-w-[800px]">
                         <tbody className="divide-y divide-gray-100">
                           {pastEventsList.map(item => {
                             if (item.type === 'game') return renderSingleGameListRow(item);
                             return renderSingleEventListRow(item);
                           })}
                         </tbody>
                       </table>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const loadTournaments = (clubId, isRealtime = false) => {
    try {
      console.log('🔵 Loading tournaments for club:', clubId, 'Realtime:', isRealtime);

      const tournamentsRef = collection(db, 'tournaments');
      const q = query(tournamentsRef, where('clubId', '==', clubId));

      if (isRealtime) {
        return onSnapshot(q, (snapshot) => {
          const tournamentsData = [];
          snapshot.forEach((doc) => {
            tournamentsData.push({ id: doc.id, ...doc.data() });
          });
          console.log('✅ Real-time update: Found', tournamentsData.length, 'tournaments');
          setTournaments(tournamentsData);
        }, (error) => {
          console.error('❌ Error loading tournaments real-time:', error);
        });
      } else {
        // Fallback for one-time fetch or manual refresh
        getDocs(q).then((snapshot) => {
          const tournamentsData = [];
          snapshot.forEach((doc) => {
            tournamentsData.push({ id: doc.id, ...doc.data() });
          });
          console.log('✅ One-time fetch: Found', tournamentsData.length, 'tournaments');
          setTournaments(tournamentsData);
        }).catch((error) => {
          console.error('❌ Error loading tournaments one-time:', error);
          setTournaments([]);
        });
      }
    } catch (error) {
      console.error('❌ Error in loadTournaments:', error);
      setTournaments([]);
    }
  };

  const loadClubMembers = async (clubId) => {
    try {
      const usersRef = collection(db, 'users');
      const qLegacy = query(usersRef, where('clubInfo.clubId', '==', clubId));
      const qNew = query(usersRef, where('joinedClubsIds', 'array-contains', clubId));
      
      const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);

      const membersMap = {};
      const processDoc = (doc) => {
        if (membersMap[doc.id]) return;
        const data = doc.data();
        membersMap[doc.id] = {
          name: data.name || data.displayName || 'Unknown',
          email: data.email
        };
      };
      
      snap1.forEach(processDoc);
      snap2.forEach(processDoc);
      
      setAllClubMembers(membersMap);
    } catch (error) {
      console.error('Error loading club members:', error);
    }
  };

  const loadUnassignedPlayers = async (clubId, teamId) => {
    try {
      console.log('🔵 Loading unassigned players for club:', clubId, 'team:', teamId);
      
      // Fetch all users in club
      const usersRef = collection(db, 'users');
      const qLegacy = query(usersRef, where('clubInfo.clubId', '==', clubId));
      const qNew = query(usersRef, where('joinedClubsIds', 'array-contains', clubId));
      
      const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);
      
      // Fetch current team to filter out existing members
      const assignedUserIds = new Set();
      if (teamId) {
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          if (teamData.players && Array.isArray(teamData.players)) {
            teamData.players.forEach(p => {
              if (p.userId) assignedUserIds.add(p.userId);
              if (p.id) assignedUserIds.add(p.id);
            });
          }
        }
      }
      
      const unassigned = [];
      const seenIds = new Set();
      
      const processDoc = (doc) => {
        if (seenIds.has(doc.id)) return;
        seenIds.add(doc.id);
        
        const userData = doc.data();
        const clubInfo = userData.joinedClubs?.[clubId] || userData.clubInfo;
        
        // Only include approved players
        if (clubInfo?.status === 'approved' && !assignedUserIds.has(doc.id)) {
          unassigned.push({
            id: doc.id,
            name: userData.name || userData.displayName || 'Unknown',
            email: userData.email,
            phone: userData.phoneNumber || ''
          });
        }
      };

      snap1.forEach(processDoc);
      snap2.forEach(processDoc);
      
      // Sort alphabetically by name
      unassigned.sort((a, b) => a.name.localeCompare(b.name));
      
      console.log('✅ Found', unassigned.length, 'unassigned players');
      setUnassignedPlayers(unassigned);
    } catch (error) {
      console.error('❌ Error loading unassigned players:', error);
      setUnassignedPlayers([]);
    }
  };

  const handleAddPlayerToTeam = async (playerId) => {
    if (!playerData?.teamId) {
      alert('You must have a primary team to add players.');
      return;
    }
    
    try {
      setAddingPlayerId(playerId);
      const playerToAdd = unassignedPlayers.find(p => p.id === playerId);
      if (!playerToAdd) return;

      const teamRef = doc(db, 'teams', playerData.teamId);
      const teamSnap = await getDoc(teamRef);
      if (!teamSnap.exists()) {
        alert('Team not found.');
        return;
      }
      
      const team = teamSnap.data();
      const newPlayer = {
        id: playerToAdd.id,
        name: playerToAdd.name,
        email: playerToAdd.email,
        phone: playerToAdd.phone || '',
        teamId: playerData.teamId,
        joinedAt: new Date().toISOString()
      };

      const updatedPlayers = [...(team.players || []), newPlayer];
      await updateDoc(teamRef, { players: updatedPlayers });

      // Update the user's document to reflect the team association
      const userRef = doc(db, 'users', playerId);
      
      const newTeamObj = {
        teamId: playerData.teamId,
        teamName: playerData.teamName,
        teamRole: 'player'
      };
      
      const updates = {
        allTeams: arrayUnion(newTeamObj)
      };

      // If they don't have a primary team yet, make this one their primary
      if (!playerToAdd.teamId) {
        updates.teamId = playerData.teamId;
        updates.teamName = playerData.teamName;
        updates.teamRole = 'player';
      }

      await updateDoc(userRef, updates);

      // Refresh unassigned players
      if (clubInfo?.id) {
        await loadUnassignedPlayers(clubInfo.id, playerData.teamId);
      }
      
      // Update local team roster
      setTeamPlayers(updatedPlayers);
      setShowAddPlayerModal(false);
      alert(`${playerToAdd.name} added to the roster!`);
    } catch (error) {
      console.error('Error adding player:', error);
      alert('Failed to add player. Please try again.');
    } finally {
      setAddingPlayerId(null);
    }
  };

  const handleRemovePlayerFromTeam = async (playerId, playerName) => {
    if (!playerData?.teamId) return;
    if (!confirm(`Are you sure you want to remove ${playerName} from the roster?`)) return;

    try {
      const teamRef = doc(db, 'teams', playerData.teamId);
      const teamSnap = await getDoc(teamRef);
      if (!teamSnap.exists()) return;
      
      const team = teamSnap.data();
      const updatedPlayers = (team.players || []).filter(p => (p.id || p.userId) !== playerId);
      
      const newCaptains = updatedPlayers.filter(p => p.teamRole === 'captain').map(p => p.userId || p.id);
      const newViceCaptains = updatedPlayers.filter(p => p.teamRole === 'vice-captain').map(p => p.userId || p.id);
      
      await updateDoc(teamRef, { 
        players: updatedPlayers,
        captains: newCaptains,
        viceCaptains: newViceCaptains
      });

      // Update the user's document to remove them from the team
      const userRef = doc(db, 'users', playerId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const uData = userSnap.data();
        let userUpdates = {};
        
        // Remove from allTeams
        if (uData.allTeams && uData.allTeams.length > 0) {
          const updatedAllTeams = uData.allTeams.filter(t => t.teamId !== playerData.teamId);
          userUpdates.allTeams = updatedAllTeams;
        }

        // If this was their primary team, clear it out
        if (uData.teamId === playerData.teamId) {
          userUpdates.teamId = deleteField();
          userUpdates.teamName = deleteField();
          userUpdates.teamRole = deleteField();
        }

        // Only commit if there's something to update
        if (Object.keys(userUpdates).length > 0) {
          await updateDoc(userRef, userUpdates);
        }
      }

      // Refresh unassigned players
      if (clubInfo?.id) {
        await loadUnassignedPlayers(clubInfo.id, playerData.teamId);
      }
      
      // Update local team roster
      setTeamPlayers(updatedPlayers);
    } catch (error) {
      console.error('Error removing player:', error);
      alert('Failed to remove player. Please try again.');
    }
  };

  const handleLeaveClub = async () => {
    try {
      setIsLeavingClub(true);
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        clubInfo: deleteField(),
        role: 'player',
        additionalRoles: []
      });
      window.location.href = '/'; // Forces a full reload, which routes user to JoinClubForm
    } catch (error) {
      console.error('Error leaving club:', error);
      alert('Failed to leave club. Please try again.');
      setIsLeavingClub(false);
    }
  };

  const loadEvents = async (clubId, playerId) => {
    try {
      console.log('🔵 Loading events for club:', clubId);

      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, where('clubId', '==', clubId));
      const snapshot = await getDocs(q);

      const eventsData = [];
      const rsvpMap = {};

      snapshot.forEach((doc) => {
        const event = { id: doc.id, ...doc.data() };
        eventsData.push(event);

        // Load player's RSVP status
        if (event.rsvps && event.rsvps[playerId]) {
          rsvpMap[event.id] = event.rsvps[playerId];
        }
      });

      // Sort by date (upcoming first)
      eventsData.sort((a, b) => new Date(a.date) - new Date(b.date));

      console.log('✅ Found', eventsData.length, 'events');
      setEvents(eventsData);
      setEventRsvps(rsvpMap);
    } catch (error) {
      console.error('❌ Error loading events:', error);
      setEvents([]);
    }
  };

  const handleEventRsvp = async (eventId, status, options = {}) => {
    if (!playerData) return;

    try {
      console.log('🔵 Updating RSVP for event:', eventId, 'status:', status);

      const eventRef = doc(db, 'events', eventId);
      const eventSnap = await getDoc(eventRef);

      if (!eventSnap.exists()) {
        alert('Event not found');
        return;
      }

      const eventData = eventSnap.data();
      const currentRsvps = eventData.rsvps || {};
      const existingRsvp = currentRsvps[playerData.id] || {};

      const finalStatus = status || existingRsvp.status;
      const guests = options.guests !== undefined ? options.guests : (status === 'yes' && existingRsvp.guests ? existingRsvp.guests : 0);
      let note = options.note !== undefined ? options.note : existingRsvp.note;
      const mealPreference = options.mealPreference !== undefined ? options.mealPreference : existingRsvp.mealPreference;

      if (!finalStatus) return;

      if (finalStatus === 'maybe') {
        const currentEdit = editingEventNote[eventId];
        if (options.note !== undefined && options.note.trim() === '') {
          alert("Please provide a note explaining your availability before selecting Maybe.");
          if (editingEventNote[eventId] === undefined) {
            setEditingEventNote(prev => ({ ...prev, [eventId]: '' }));
          }
          return;
        } else if (options.note === undefined) {
          if ((!note || note.trim() === '') && (!currentEdit || currentEdit.trim() === '')) {
            alert("Please provide a note explaining your availability before selecting Maybe.");
            if (editingEventNote[eventId] === undefined) {
              setEditingEventNote(prev => ({ ...prev, [eventId]: '' }));
            }
            return;
          }
          if (!note || note.trim() === '') {
            note = currentEdit?.trim() || '';
          }
        }
      }

      const updatePayload = {
        ...existingRsvp,
        status: finalStatus,
        guests,
        respondedAt: new Date().toISOString(),
        playerName: playerData.name,
        playerEmail: playerData.email
      };

      if (note !== undefined) updatePayload.note = note;
      if (mealPreference !== undefined) updatePayload.mealPreference = mealPreference;

      currentRsvps[playerData.id] = updatePayload;

      await updateDoc(eventRef, { rsvps: currentRsvps });

      // Update local state
      setEventRsvps(prev => ({
        ...prev,
        [eventId]: currentRsvps[playerData.id]
      }));

      console.log('✅ RSVP updated');
    } catch (error) {
      console.error('❌ Error updating RSVP:', error);
      alert('Error updating RSVP');
    }
  };

  const loadGamesForPlayer = (clubId, playerId, setupListener = false) => {
    if (!clubId) return;

    console.log('🔵 Loading games for club:', clubId);

    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, where('clubId', '==', clubId));

    const processGames = (snapshot) => {
      const allGames = [];
      const availabilityMap = {};

      snapshot.forEach((docSnap) => {
        const game = { id: docSnap.id, ...docSnap.data() };
        allGames.push(game);

        // Load player's availability status (check primary playerId and any additional/matched IDs)
        const matchIds = [playerId, ...(playerData?.matchedPlayerIds || [])].filter(Boolean);
        const uniqueMatchIds = [...new Set(matchIds)];
        
        let foundStatus = null;
        for (const id of uniqueMatchIds) {
          if (game.availability?.[id]) {
            foundStatus = game.availability[id];
            break;
          }
        }
        if (foundStatus) {
          availabilityMap[game.id] = foundStatus;
        }
      });

      console.log('✅ Found', allGames.length, 'games for club');
      console.log('📊 Games with squads:', allGames.filter(g => g.playing11?.length > 0).length);

      // Sort games by date (ascending)
      allGames.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Debug: Log each game's playing11 status
      allGames.forEach(g => {
        console.log(`  📋 Game "${g.name}" (${g.date}): playing11 =`, g.playing11?.length || 0, 'players');
      });
      setAllClubGames(allGames);
      setAvailability(availabilityMap);
    };

    if (setupListener) {
      console.log('🔵 Setting up real-time games listener for club:', clubId);
      const unsubscribe = onSnapshot(q, processGames, (error) => {
        console.error('❌ Error in games listener:', error);
        setAllClubGames([]);
      });
      return unsubscribe;
    } else {
      // One-time fetch for backwards compatibility
      getDocs(q).then(processGames).catch((error) => {
        console.error('❌ Error loading games:', error);
        setAllClubGames([]);
      });
    }
  };

  const handleAvailability = async (gameId, status, options = {}) => {
    if (!playerData) return;

    try {
      console.log('🔵 Updating availability for game:', gameId, 'status:', status);

      // Get current availability to track if this is a change
      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);
      const currentAvailability = gameSnap.data()?.availability?.[playerData.id];

      const timestamp = new Date().toISOString();
      const finalStatus = status || currentAvailability?.status;
      let note = options.note !== undefined ? options.note : currentAvailability?.note;

      if (!finalStatus) return;

      const isTogglingOff = currentAvailability && currentAvailability.status === status && options.note === undefined && options.guests === undefined && options.mealPreference === undefined;

      if (finalStatus === 'maybe' && !isTogglingOff) {
        const currentEdit = editingGameNote[gameId];
        if (options.note !== undefined && options.note.trim() === '') {
          alert("Please provide a note explaining your availability before selecting Maybe.");
          if (editingGameNote[gameId] === undefined) setEditingGameNote(prev => ({ ...prev, [gameId]: '' }));
          return;
        } else if (options.note === undefined) {
          if ((!note || note.trim() === '') && (!currentEdit || currentEdit.trim() === '')) {
            alert("Please provide a note explaining your availability before selecting Maybe.");
            if (editingGameNote[gameId] === undefined) setEditingGameNote(prev => ({ ...prev, [gameId]: '' }));
            return;
          }
          if (!note || note.trim() === '') {
            note = currentEdit?.trim() || '';
          }
        }
      }

      // Check if clicking the same status -> toggle off (unless they are specifically saving a note)
      if (isTogglingOff) {
        // Build history entry for un-voting
        const historyEntry = {
          status: null, // Indicates un-vote
          changedAt: timestamp,
          playerName: playerData.name
        };

        await updateDoc(gameRef, {
          [`availability.${playerData.id}`]: deleteField()
        });

        // Update local state by removing the game from availability
        setAvailability(prev => {
          const newState = { ...prev };
          delete newState[gameId];
          return newState;
        });

        console.log('✅ Availability removed (toggled off)');
        return;
      }

      // Build history entry if status changed
      const historyEntry = {
        status: status,
        changedAt: timestamp,
        playerName: playerData.name
      };

      // If this is a change (not first response), add to history
      const isChange = !!(currentAvailability && currentAvailability.status !== status);

      await updateDoc(gameRef, {
        [`availability.${playerData.id}`]: {
          status: finalStatus,
          note: note || '',
          updatedAt: timestamp,
          playerName: playerData.name,
          previousStatus: currentAvailability?.status || null,
          isChanged: isChange,
          history: currentAvailability?.history
            ? [...(currentAvailability.history || []), historyEntry]
            : [historyEntry]
        }
      });

      // Update local state
      setAvailability(prev => ({
        ...prev,
        [gameId]: {
          status: status,
          updatedAt: timestamp,
          playerName: playerData.name
        }
      }));

      console.log('✅ Availability updated', isChange ? '(CHANGED)' : '(initial)');
    } catch (error) {
      console.error('❌ Error updating availability:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      alert(`Failed to update availability: ${error.message}`);
    }
  };

  const handleMealPreference = async (gameId, preference) => {
    if (!playerData) return;

    try {
      console.log('🔵 Updating meal preference for game:', gameId, 'preference:', preference);

      const gameRef = doc(db, 'games', gameId);
      const timestamp = new Date().toISOString();

      await updateDoc(gameRef, {
        [`mealPreferences.${playerData.id}`]: {
          preference: preference,
          updatedAt: timestamp,
          playerName: playerData.name
        }
      });

      console.log('✅ Meal preference updated');
    } catch (error) {
      console.error('❌ Error updating meal preference:', error);
      alert(`Failed to update meal preference: ${error.message}`);
    }
  };

  // Helper to check if game date is today or in the past
  const isGameDayOrPast = (gameDate) => {
    if (!gameDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const gDate = new Date(gameDate);
    gDate.setHours(0, 0, 0, 0);
    return gDate <= today;
  };

  // Handle marking a player as late arrival for a game (Captain/VC only)
  const handleMarkLateArrival = async (gameId, playerId, isLate) => {
    try {
      console.log('🔵 Marking late arrival:', { gameId, playerId, isLate });

      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);

      if (!gameSnap.exists()) {
        alert('Game not found');
        return;
      }

      const currentLateArrivals = gameSnap.data().lateArrivals || {};

      if (isLate) {
        currentLateArrivals[playerId] = true;
      } else {
        delete currentLateArrivals[playerId];
      }

      await updateDoc(gameRef, {
        lateArrivals: currentLateArrivals
      });

      // Update local state
      setAllClubGames(prev => prev.map(g =>
        g.id === gameId
          ? { ...g, lateArrivals: currentLateArrivals }
          : g
      ));

      console.log('✅ Late arrival updated');
    } catch (error) {
      console.error('❌ Error marking late arrival:', error);
      alert('Error updating late arrival status');
    }
  };

  // Toggle game card expansion (for captain's view)
  const toggleGameExpanded = (gameId) => {
    setExpandedGames(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }));
  };

  const handleSelectSquad = async (gameId) => {
    try {
      console.log('🔵 Opening squad selection for game:', gameId);

      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);

      if (!gameSnap.exists()) {
        alert('Game not found');
        return;
      }

      const game = { id: gameSnap.id, ...gameSnap.data() };

      if (!playerData.teamId) {
        alert('You are not assigned to a team');
        return;
      }

      const gameTeamPlayers = getGameTeamPlayers(game);
      const allSelectablePlayers = new Map();

      // 1. Add all game team roster players
      (gameTeamPlayers || []).forEach(p => {
        const uid = p.id || p.userId;
        const memberInfo = allClubMembers[uid];
        allSelectablePlayers.set(uid, {
          id: uid,
          userId: p.userId || p.id,
          name: p.name || (memberInfo ? memberInfo.name : 'Unknown Player'),
          email: p.email || (memberInfo ? memberInfo.email : 'Unknown Email'),
          registrationStatus: memberInfo ? memberInfo.registrationStatus : 'approved',
          isGuest: false
        });
      });

      // 2. Add anyone who explicitly RSVP'd (might be subs from other teams)
      const gameAvailability = game.availability || {};
      Object.entries(gameAvailability).forEach(([uid, resp]) => {
        if (!allSelectablePlayers.has(uid)) {
          const memberInfo = allClubMembers[uid];
          allSelectablePlayers.set(uid, {
            id: uid,
            userId: uid,
            name: memberInfo ? memberInfo.name : (resp.playerName || 'Unknown Player'),
            email: memberInfo ? memberInfo.email : 'External/Guest',
            registrationStatus: 'approved',
            isGuest: false
          });
        }
      });

      // 3. Ensure anyone already in playing11 is included
      (game.playing11 || []).forEach(uid => {
        if (!allSelectablePlayers.has(uid)) {
          const memberInfo = allClubMembers[uid];
          if (memberInfo) {
            allSelectablePlayers.set(uid, {
              id: uid,
              userId: uid,
              name: memberInfo.name,
              email: memberInfo.email,
              registrationStatus: 'approved',
              isGuest: false
            });
          }
        }
      });

      setTeamPlayers(Array.from(allSelectablePlayers.values()));
      setSelectedGameForSquad(game);
      setSquadSelection(game.playing11 || []);
      setGuestPlayers(game.guestPlayers || []); // Load existing guest players
      setGuestPlayerName(''); // Reset input
      setShowSelectPlaying11(true);
    } catch (error) {
      console.error('❌ Error loading squad selection:', error);
      alert('Error loading squad selection');
    }
  };

  const togglePlayerInSquad = (playerId) => {
    if (squadSelection.includes(playerId)) {
      setSquadSelection(squadSelection.filter(id => id !== playerId));
    } else if (squadSelection.length < 12) {
      setSquadSelection([...squadSelection, playerId]);
    }
  };

  const handleAddGuestPlayer = () => {
    if (!guestPlayerName.trim()) {
      alert('Please enter a player name');
      return;
    }

    // Generate a temporary ID for the guest player
    const guestId = `guest_${Date.now()}`;
    const newGuest = {
      id: guestId,
      name: guestPlayerName.trim(),
      isGuest: true
    };

    setGuestPlayers([...guestPlayers, newGuest]);
    setSquadSelection([...squadSelection, guestId]);
    setGuestPlayerName('');
    console.log('✅ Added guest player:', newGuest.name);
  };

  const handleRemoveGuestPlayer = (guestId) => {
    setGuestPlayers(guestPlayers.filter(g => g.id !== guestId));
    setSquadSelection(squadSelection.filter(id => id !== guestId));
    console.log('✅ Removed guest player');
  };

  const handleSaveSquad = async ({ playing11, superSubs, guestPlayers: guests, playerDuties, isPublish, notificationOption }) => {
    // Note: Validation is handled by SquadSelection component
    if (!selectedGameForSquad) {
      alert('No game selected');
      return;
    }

    // Duty labels for notifications
    const DUTY_LABELS = {
      'ground_setup': '🏟️ Ground Setup',
      'refreshments': '🥤 Drinks & Snacks',
      'scoring': '📝 Scoring',
      'umpiring': '🧑‍⚖️ Umpiring',
      'first_aid': '🩹 First Aid',
      'equipment': '🎒 Equipment',
      'transport': '🚗 Transport',
      'photography': '📸 Photography'
    };

    try {
      console.log('🔵 Saving squad for game:', selectedGameForSquad.id);
      console.log('  - Players:', playing11.length);
      console.log('  - Super subs:', superSubs?.length || 0);
      console.log('  - Player duties:', Object.keys(playerDuties || {}).length);

      const gameRef = doc(db, 'games', selectedGameForSquad.id);
      const previousSquad = selectedGameForSquad.playing11 || [];

      const mappedResponsibilities = {};
      if (playerDuties && Object.keys(playerDuties).length > 0) {
        Object.keys(playerDuties).forEach(playerId => {
          mappedResponsibilities[playerId] = [playerDuties[playerId]];
        });
      }

      // Update game with new squad, super subs, guest players, duties, and responsibilities
      const updateData = {
        playing11: playing11,
        superSubs: superSubs || [],
        guestPlayers: guests || [],
        playerDuties: playerDuties || {},
        playerResponsibilities: mappedResponsibilities
      };

      const previousNotified = selectedGameForSquad.notifiedSquad || [];

      let newlySelected = [];
      if (isPublish) {
        updateData.isSquadPublished = true;
        newlySelected = playing11.filter(playerId =>
          !previousNotified.includes(playerId) && !playerId.startsWith('guest')
        );
        updateData.notifiedSquad = Array.from(new Set([...previousNotified, ...newlySelected]));
      }

      await updateDoc(gameRef, updateData);

      // Format game date nicely
      const formattedDate = formatDate(selectedGameForSquad.date, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });

      let totalNotified = 0;

      if (isPublish) {
        // Use centralized notification service for squad selection
        const notificationResult = await processSquadPublishNotifications({
          game: selectedGameForSquad,
          newSquad: playing11,
          previousSquad: previousNotified,
          superSubs: superSubs || [],
          newDuties: playerDuties || {},
          previousDuties: selectedGameForSquad.playerDuties || {},
          notificationOption: notificationOption || 'impacted_only',
          currentUserName: playerData?.name,
          clubId: clubInfo?.id || selectedGameForSquad.clubId
        });

        if (notificationResult.success) {
          totalNotified = notificationResult.count || 0;
          console.log(`✅ Notified ${totalNotified} squad members`);
        }
      }

      console.log('✅ Squad saved with super subs, duties, and optional notifications');

      if (isPublish) {
        alert(`Squad published successfully! ${totalNotified} player${totalNotified !== 1 ? 's' : ''} notified.`);
      } else {
        alert(`Squad draft saved successfully!`);
      }

      // Reload games
      await loadGamesForPlayer(clubInfo.id, playerData.id);
      setShowSelectPlaying11(false);
    } catch (error) {
      console.error('❌ Error saving squad:', error);
      alert('Error saving squad');
    }
  };

  // EXPENSE MANAGEMENT FUNCTIONS
  const handleAddExpense = async () => {
    if (!expenseDescription || !expenseAmount) {
      alert('Please fill in description and amount');
      return;
    }

    if (expenseGameId === 'other' && (!otherGameDetails || !otherGameDetails.trim())) {
      alert('Please provide the match details (Teams, Date) for the unlisted game.');
      return;
    }

    if (expenseLevel === 'tournament' && selectedCategory === 'Umpiring assignment') {
      if (!expenseTournamentId) {
        alert('Please select a tournament for the umpiring assignment.');
        return;
      }
      if (!expenseDate) {
        alert('Please select the date for the umpiring assignment.');
        return;
      }
    }

    // Check if receipt is required for this expense type
    const receiptNotRequired = ['umpiring fee', 'umpiring assignment'];
    const requiresReceipt = !receiptNotRequired.some(type => expenseDescription.toLowerCase().startsWith(type));
    if (requiresReceipt && !expenseReceipt) {
      alert('Receipt is required for this expense type. Please upload a receipt.');
      return;
    }

    try {
      console.log('🔵 Adding expense');
      setUploadingReceipt(true);

      let gameData = null;
      let playing11 = [];
      let amountPerPlayer = 0;
      let tournamentId = null;
      let tournamentName = null;

      // Get game details if game is selected
      if (expenseGameId && expenseGameId !== 'other') {
        const gameRef = doc(db, 'games', expenseGameId);
        const gameSnap = await getDoc(gameRef);

        if (gameSnap.exists()) {
          gameData = gameSnap.data();
          playing11 = gameData.playing11 || [];
          amountPerPlayer = playing11.length > 0 ? parseFloat(expenseAmount) / playing11.length : 0;

          // Get tournament info from game
          tournamentId = gameData.tournamentId || null;
          tournamentName = tournamentId ? tournaments.find(t => t.id === tournamentId)?.name : null;
        }
      } else if (expenseLevel === 'tournament') {
        tournamentId = expenseTournamentId || null;
        tournamentName = tournamentId ? tournaments.find(t => t.id === tournamentId)?.name : null;
      }

      // Check if tournament is settled
      if (tournamentId) {
        const t = tournaments.find(t => t.id === tournamentId);
        if (t?.isSettled) {
          alert('This tournament has been settled. You can no longer submit new expenses for it or its games.');
          setUploadingReceipt(false);
          return;
        }
      }

      let receiptURL = null;

      // Upload receipt if provided
      if (expenseReceipt) {
        console.log('🔵 Uploading receipt...');
        const receiptRef = ref(storage, `receipts/${clubInfo.id}/${Date.now()}_${expenseReceipt.name}`);
        await uploadBytes(receiptRef, expenseReceipt);
        receiptURL = await getDownloadURL(receiptRef);
        console.log('✅ Receipt uploaded:', receiptURL);
      }

      // Create expense
      const expensesRef = collection(db, 'expenses');
      await addDoc(expensesRef, {
        description: expenseDescription,
        amount: parseFloat(expenseAmount),
        amountPerPlayer: amountPerPlayer,
        gameId: expenseGameId === 'other' ? null : (expenseGameId || null),
        gameName: expenseGameId === 'other' ? otherGameDetails : (gameData?.name || null),
        tournamentId: tournamentId,
        tournamentName: tournamentName,
        clubId: clubInfo.id,
        splitAmong: playing11,
        isDividable: expenseLevel !== 'tournament',
        isTournamentFee: expenseLevel === 'tournament',
        clubCovered: false,
        receiptURL: receiptURL,
        createdAt: new Date().toISOString(),
        createdBy: user.email,
        createdByName: playerData.name || user.displayName || user.email.split('@')[0],
        addedByPlayer: playerData.name,
        addedByPlayerId: user.uid,
        addedByPlayerName: playerData.name || user.displayName || user.email.split('@')[0],
        approvalStatus: 'pending',
        approvedBy: null,
        approvedAt: null,
        expenseDate: expenseDate || null
      });

      console.log('✅ Expense added');
      alert('Expense submitted successfully! Waiting for admin approval.');

      // Reset form
      setExpenseDescription('');
      setExpenseAmount('');
      setExpenseGameId('');
      setOtherGameDetails('');
      setExpenseNote('');
      setExpenseReceipt(null);
      setCapturedImage(null);
      setExpenseDate('');
      setUploadingReceipt(false);

      await loadExpenses(clubInfo.id, playerData.id, false, playerData.oderId || playerData.oderedPlayerId);
      setView('dashboard');
    } catch (error) {
      console.error('❌ Error adding expense:', error);
      alert('Error adding expense: ' + error.message);
      setUploadingReceipt(false);
    }
  };

  const handleEditExpense = async () => {
    if (!editingExpense || !expenseDescription || !expenseAmount) {
      alert('Please fill in description and amount');
      return;
    }

    if (expenseGameId === 'other' && (!otherGameDetails || !otherGameDetails.trim())) {
      alert('Please provide the match details (Teams, Date) for the unlisted game.');
      return;
    }

    if (expenseLevel === 'tournament' && selectedCategory === 'Umpiring assignment') {
      if (!expenseTournamentId) {
        alert('Please select a tournament for the umpiring assignment.');
        return;
      }
      if (!expenseDate) {
        alert('Please select the date for the umpiring assignment.');
        return;
      }
    }

    // Security check: Only allow editing of pending or denied expenses
    if (editingExpense.approvalStatus === 'approved') {
      alert('Cannot edit approved expenses. Please contact an admin if you need to modify this expense.');
      return;
    }

    try {
      console.log('🔵 Updating expense:', editingExpense.id);
      setUploadingReceipt(true);

      let receiptURL = editingExpense.receiptURL;

      // If user wants to remove old receipt
      if (removeOldReceipt) {
        receiptURL = null;
      }

      // Upload new receipt if provided
      if (expenseReceipt) {
        console.log('🔵 Uploading new receipt...');
        const receiptRef = ref(storage, `receipts/${clubInfo.id}/${Date.now()}_${expenseReceipt.name}`);
        await uploadBytes(receiptRef, expenseReceipt);
        receiptURL = await getDownloadURL(receiptRef);
        console.log('✅ New receipt uploaded:', receiptURL);
      }

      // Check if receipt is required for this expense type
      const receiptNotRequired = ['umpiring fee', 'umpiring assignment'];
      const requiresReceipt = !receiptNotRequired.some(type => expenseDescription.toLowerCase().startsWith(type));
      if (requiresReceipt && !receiptURL) {
        alert('Receipt is required for this expense type. Please upload a receipt.');
        setUploadingReceipt(false);
        return;
      }

      let gameData = null;
      let playing11 = [];
      let amountPerPlayer = 0;

      // Get game details if game is selected
      if (expenseGameId && expenseGameId !== 'other') {
        const gameRef = doc(db, 'games', expenseGameId);
        const gameSnap = await getDoc(gameRef);

        if (gameSnap.exists()) {
          gameData = gameSnap.data();
          playing11 = gameData.playing11 || [];
          amountPerPlayer = playing11.length > 0 ? parseFloat(expenseAmount) / playing11.length : 0;
        }
      }

      // Update expense - reset to pending if it was denied
      const expenseRef = doc(db, 'expenses', editingExpense.id);
      const updateData = {
        description: expenseDescription,
        amount: parseFloat(expenseAmount),
        amountPerPlayer: amountPerPlayer,
        gameId: expenseGameId === 'other' ? null : (expenseGameId || null),
        gameName: expenseGameId === 'other' ? otherGameDetails : (gameData?.name || null),
        tournamentId: expenseTournamentId || null,
        splitAmong: playing11,
        isDividable: expenseLevel !== 'tournament',
        isTournamentFee: expenseLevel === 'tournament',
        clubCovered: false,
        expenseDate: expenseDate || null,
        receiptURL: receiptURL,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email
      };

      if (typeof expenseNote !== 'undefined' && expenseNote.trim() !== '') {
        updateData.notes = arrayUnion({
          text: expenseNote.trim(),
          addedBy: playerData?.name || user.email,
          addedAt: new Date().toISOString()
        });
      }

      // If expense was denied, reset it to pending and clear denial info
      if (editingExpense.approvalStatus === 'denied') {
        updateData.approvalStatus = 'pending';
        updateData.denialReason = null;
        updateData.deniedBy = null;
        updateData.deniedAt = null;
      }

      await updateDoc(expenseRef, updateData);

      console.log('🔵 Expense updated, now cleaning up notifications...');

      // Delete any existing notifications for this expense
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', playerData.id),
        where('expenseId', '==', editingExpense.id)
      );
      const notificationSnapshot = await getDocs(q);

      console.log(`🔵 Found ${notificationSnapshot.size} notifications to delete for expense ${editingExpense.id}`);

      // Delete all notifications related to this expense
      const deletePromises = [];
      notificationSnapshot.forEach((docSnap) => {
        console.log(`  - Deleting notification: ${docSnap.id}`);
        deletePromises.push(deleteDoc(docSnap.ref));
      });
      await Promise.all(deletePromises);

      console.log('✅ Expense updated and notifications cleared');

      // Reset form
      setEditingExpense(null);
      setExpenseDescription('');
      setExpenseAmount('');
      setExpenseGameId('');
      setExpenseNote('');
      setExpenseReceipt(null);
      setRemoveOldReceipt(false);
      setUploadingReceipt(false);

      // Reload data to refresh notifications and expenses
      await Promise.all([
        loadExpenses(clubInfo.id, playerData.id, false, playerData.oderId || playerData.oderedPlayerId),
        loadNotifications(playerData.id)
      ]);

      console.log('✅ Data reloaded, current notifications:', notifications.length);

      alert('Expense updated and resubmitted for approval!');
      setView('my-expenses');
    } catch (error) {
      console.error('❌ Error updating expense:', error);
      alert('Error updating expense: ' + error.message);
      setUploadingReceipt(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      console.log('🔵 Attempting to delete expense:', expenseId);

      // Get the expense first to check its status
      const expenseRef = doc(db, 'expenses', expenseId);
      const expenseSnap = await getDoc(expenseRef);

      if (!expenseSnap.exists()) {
        alert('Expense not found');
        return;
      }

      const expense = expenseSnap.data();

      // Security check: Only allow deletion of pending or denied expenses
      if (expense.approvalStatus === 'approved') {
        alert('Cannot delete approved expenses. Please contact an admin if you need to modify this expense.');
        return;
      }

      if (!confirm('Are you sure you want to delete this expense?')) {
        return;
      }

      await deleteDoc(expenseRef);

      console.log('✅ Expense deleted');
      alert('Expense deleted successfully!');
      await loadExpenses(clubInfo.id, playerData.id, false, playerData.oderId || playerData.oderedPlayerId);
    } catch (error) {
      console.error('❌ Error deleting expense:', error);
      alert('Error deleting expense: ' + error.message);
    }
  };

  // CAMERA CAPTURE FUNCTIONS
  const startCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      setCameraStream(stream);
      setShowCameraCapture(true);
    } catch (error) {
      console.error('❌ Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions or use file upload instead.');
    }
  };

  const stopCameraCapture = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraCapture(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      const file = new File([blob], `receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setCapturedImage(URL.createObjectURL(blob));
      setExpenseReceipt(file);
      stopCameraCapture();
    }, 'image/jpeg', 0.9);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setExpenseReceipt(null);
    startCameraCapture();
  };

  // DRAFT MANAGEMENT FUNCTIONS
  const loadDraftExpenses = (setupListener = false) => {
    if (!playerData) return;

    const draftsRef = collection(db, 'draftExpenses');
    const q = query(draftsRef, where('userId', '==', playerData.id));

    const processDrafts = (snapshot) => {
      const drafts = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.clubId === clubInfo?.id || !data.clubId) {
          drafts.push({ id: doc.id, ...data });
        }
      });

      console.log('✅ Draft expenses updated:', drafts.length, 'drafts');
      setDraftExpenses(drafts);
    };

    if (setupListener) {
      console.log('🔵 Setting up real-time draft expenses listener for user:', playerData.id);
      const unsubscribe = onSnapshot(q, processDrafts, (error) => {
        console.error('❌ Error in draft expenses listener:', error);
        setDraftExpenses([]);
      });
      return unsubscribe;
    } else {
      // One-time fetch for backwards compatibility
      getDocs(q).then(processDrafts).catch((error) => {
        console.error('❌ Error loading drafts:', error);
        setDraftExpenses([]);
      });
    }
  };

  const handleSaveAsDraft = async () => {
    if (!expenseDescription && !expenseAmount) {
      alert('Please enter at least a description or amount to save a draft');
      return;
    }

    if (expenseGameId === 'other' && (!otherGameDetails || !otherGameDetails.trim())) {
      alert('Please provide the match details (Teams, Date) for the unlisted game.');
      return;
    }
  
    try {
      setSavingDraft(true);
      console.log('🔵 Saving expense as draft');

      let receiptURL = null;

      // Upload receipt if provided
      if (expenseReceipt) {
        console.log('🔵 Uploading receipt for draft...');
        const receiptRef = ref(storage, `receipts/drafts/${clubInfo.id}/${Date.now()}_${expenseReceipt.name}`);
        await uploadBytes(receiptRef, expenseReceipt);
        receiptURL = await getDownloadURL(receiptRef);
        console.log('✅ Receipt uploaded for draft');
      }

      let gameData = null;
      // Get game details if game is selected
      if (expenseGameId && expenseGameId !== 'other') {
        const gameRef = doc(db, 'games', expenseGameId);
        const gameSnap = await getDoc(gameRef);
        gameData = gameSnap.exists() ? gameSnap.data() : null;
      }

      const draftData = {
        description: expenseDescription,
        amount: parseFloat(expenseAmount),
        gameId: expenseGameId === 'other' ? null : (expenseGameId || null),
        gameName: expenseGameId === 'other' ? otherGameDetails : (gameData?.name || null),
        receiptURL: receiptURL,
        clubId: clubInfo.id,
        userId: playerData.id,
        userName: playerData.name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingExpense?.isDraft) {
        // Update existing draft
        const draftRef = doc(db, 'draftExpenses', editingExpense.id);
        await updateDoc(draftRef, {
          ...draftData,
          updatedAt: new Date().toISOString()
        });
        console.log('✅ Draft updated');
        alert('Draft updated successfully!');
      } else {
        // Create new draft
        await addDoc(collection(db, 'draftExpenses'), draftData);
        console.log('✅ Draft saved');
        alert('Draft saved successfully!');
      }

      // Reset form
      setExpenseDescription('');
      setExpenseAmount('');
      setExpenseGameId('');
      setExpenseNote('');
      setExpenseReceipt(null);
      setCapturedImage(null);
      setEditingExpense(null);
      setSavingDraft(false);

      // Real-time listener will update draftExpenses automatically
      setView('my-expenses');
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      alert('Error saving draft: ' + error.message);
      setSavingDraft(false);
    }
  };

  const handleSubmitFromDraft = async (draftId) => {
    try {
      const draftRef = doc(db, 'draftExpenses', draftId);
      const draftSnap = await getDoc(draftRef);

      if (!draftSnap.exists()) {
        alert('Draft not found');
        return;
      }

      const draft = draftSnap.data();

      // Check if receipt is required
      const receiptNotRequired = ['umpiring fee', 'umpiring assignment'];
      const requiresReceipt = !receiptNotRequired.some(type => draft.description?.toLowerCase().startsWith(type));
      if (requiresReceipt && !draft.receiptURL) {
        alert('Receipt is required for this expense type. Please edit the draft and add a receipt.');
        return;
      }

      console.log('🔵 Submitting expense from draft');

      let gameData = null;
      let playing11 = [];
      let amountPerPlayer = 0;
      let tournamentId = null;
      let tournamentName = null;

      // Get game details if game is selected
      if (draft.gameId) {
        const gameRef = doc(db, 'games', draft.gameId);
        const gameSnap = await getDoc(gameRef);

        if (gameSnap.exists()) {
          gameData = gameSnap.data();
          playing11 = gameData.playing11 || [];
          amountPerPlayer = playing11.length > 0 ? draft.amount / playing11.length : 0;

          // Get tournament info
          tournamentId = gameData.tournamentId || null;
          tournamentName = tournamentId ? tournaments.find(t => t.id === tournamentId)?.name : null;
        }
      }

      // Create expense
      await addDoc(collection(db, 'expenses'), {
        description: draft.description,
        amount: draft.amount,
        amountPerPlayer: amountPerPlayer,
        gameId: draft.gameId || null,
        gameName: draft.gameName || null,
        tournamentId: tournamentId,
        tournamentName: tournamentName,
        clubId: clubInfo.id,
        splitAmong: playing11,
        receiptURL: draft.receiptURL,
        createdAt: new Date().toISOString(),
        createdBy: user.email,
        createdByName: playerData.name || user.displayName || user.email.split('@')[0],
        addedByPlayer: playerData.name,
        addedByPlayerId: user.uid,
        addedByPlayerName: playerData.name || user.displayName || user.email.split('@')[0],
        approvalStatus: 'pending',
        approvedBy: null,
        approvedAt: null
      });

      // Delete draft
      await deleteDoc(draftRef);

      console.log('✅ Expense submitted from draft');
      alert('Expense submitted successfully! Waiting for admin approval.');

      // Real-time listeners will update expenses and drafts automatically
    } catch (error) {
      console.error('❌ Error submitting from draft:', error);
      alert('Error submitting expense: ' + error.message);
    }
  };

  const handleEditDraft = (draft) => {
    setEditingExpense({ ...draft, isDraft: true });
    setExpenseDescription(draft.description);
    setExpenseAmount(draft.amount.toString());
    
    if (!draft.gameId && draft.gameName) {
      setExpenseGameId('other');
      setOtherGameDetails(draft.gameName);
    } else {
      setExpenseGameId(draft.gameId || '');
      setOtherGameDetails('');
    }
    if (draft.receiptURL) {
      setCapturedImage(draft.receiptURL);
    }
    setView('add-expense');
  };

  const handleDeleteDraft = async (draftId) => {
    if (!confirm('Are you sure you want to delete this draft?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'draftExpenses', draftId));
      console.log('✅ Draft deleted');
      alert('Draft deleted successfully!');
      // Real-time listener will update draftExpenses automatically
    } catch (error) {
      console.error('❌ Error deleting draft:', error);
      alert('Error deleting draft: ' + error.message);
    }
  };

  // PROFILE MANAGEMENT
  const handleUploadProfilePicture = async (file) => {
    if (!file) return;

    try {
      console.log('🔵 Uploading profile picture');
      setUploadingProfilePic(true);

      // Upload to Firebase Storage
      const profilePicRef = ref(storage, `profile-pictures/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(profilePicRef, file);
      const profilePicURL = await getDownloadURL(profilePicRef);

      // Update user document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        profilePicture: profilePicURL,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Profile picture uploaded');
      alert('Profile picture updated successfully!');

      // Reload player data
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setPlayerData({ ...playerData, profilePicture: profilePicURL });
      }

      setUploadingProfilePic(false);
    } catch (error) {
      console.error('❌ Error uploading profile picture:', error);
      alert('Error uploading profile picture');
      setUploadingProfilePic(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      console.log('🔵 Updating profile');

      // Update user document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        name: profileName,
        email: profileEmail,
        phone: profilePhone || '',
        bio: profileBio || '',
        updatedAt: new Date().toISOString()
      });

      // Also update player info in team's players array
      if (playerData.teamId) {
        const teamRef = doc(db, 'teams', playerData.teamId);
        const teamSnap = await getDoc(teamRef);

        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          const updatedPlayers = (teamData.players || []).map(player => {
            if (player.userId === user.uid || player.id === playerData.id) {
              return {
                ...player,
                name: profileName,
                email: profileEmail,
                phone: profilePhone || '',
                bio: profileBio || ''
              };
            }
            return player;
          });

          await updateDoc(teamRef, { players: updatedPlayers });
          console.log('✅ Updated player in team array');
        }
      }

      console.log('✅ Profile updated');
      alert('Profile updated successfully!');

      // Update local state
      setPlayerData({
        ...playerData,
        name: profileName,
        email: profileEmail,
        phone: profilePhone,
        bio: profileBio
      });

      setEditingProfile(false);
      setView('profile');
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      alert('Error updating profile');
    }
  };

  const loadExpenses = (clubId, playerId, setupListener = false, playerOderId = null, additionalPlayerIds = []) => {
    if (!clubId) {
      console.log('⚠️ loadExpenses called without clubId, returning');
      return;
    }

    console.log(`📊 Loading expenses for club: ${clubId}, player: ${playerId}, oderId: ${playerOderId}, listener: ${setupListener}`);

    const expensesRef = collection(db, 'expenses');
    const q = query(expensesRef, where('clubId', '==', clubId));

    const processExpenses = (snapshot) => {
      const expensesData = [];
      const submittedExpensesData = [];
      let initialDonationFound = false;

      // Ensure we have all potential player IDs
      const targetOderId = playerOderId || playerData?.oderId || playerData?.oderedPlayerId;
      const idsToMatch = [
        ...new Set([
          playerId,
          targetOderId,
          ...(additionalPlayerIds || []),
          ...(playerData?.matchedPlayerIds || [])
        ].filter(Boolean))
      ];

      snapshot.forEach((docSnap) => {
        const expense = { id: docSnap.id, ...docSnap.data() };

        // Track Initial Donations specifically for debugging
        if (expense.isInitialDonation || expense.isRegistrationFee) {
          console.log('💰 Found Initial Donation:', {
            id: expense.id,
            amount: expense.amount,
            splitAmong: expense.splitAmong,
            playerEmail: expense.playerEmail,
            includesPlayer: expense.splitAmong?.some(id => idsToMatch.includes(id))
          });
          if (expense.splitAmong?.some(id => idsToMatch.includes(id))) {
            initialDonationFound = true;
          }
        }

        // Track expenses MANUALLY SUBMITTED by this player
        // Excludes auto-created initial donations and club-assigned expenses
        if (idsToMatch.includes(expense.addedByPlayerId) &&
          !expense.isInitialDonation &&
          !expense.isRegistrationFee &&
          !expense.autoCreated) {
          submittedExpensesData.push(expense);
        }

        // Include expenses where player is in splitAmong (for payment tracking)
        // OR player is the one who submitted/paid for it (so they see it pending/approved)
        // OR include ALL initial donations (for squad selection fee status checking by captains)
        // OR include ALL tournament expenses (so they can be dynamically split on player dashboard)
        if (!expense.splitAmong || 
            (Array.isArray(expense.splitAmong) && expense.splitAmong.length === 0) ||
            expense.splitAmong.some(id => idsToMatch.includes(id)) || 
            idsToMatch.includes(expense.addedByPlayerId) || 
            idsToMatch.includes(expense.paidBy) ||
            expense.isInitialDonation || 
            expense.isRegistrationFee ||
            expense.isTournamentFee ||
            expense.splitType === 'tournament' ||
            expense.level === 'tournament' ||
            expense.tournamentId) {
          expensesData.push(expense);
        }
      });

      setExpenses(expensesData);
      setSubmittedExpenses(submittedExpensesData);
    };

    if (setupListener) {
      console.log('🔵 Setting up real-time expenses listener for club:', clubId);
      const unsubscribe = onSnapshot(q, processExpenses, (error) => {
        console.error('❌ Error in expenses listener:', error);
        setExpenses([]);
        setSubmittedExpenses([]);
      });
      return unsubscribe;
    } else {
      // One-time fetch for backwards compatibility
      getDocs(q).then(processExpenses).catch((error) => {
        console.error('❌ Error loading expenses:', error);
        setExpenses([]);
        setSubmittedExpenses([]);
      });
    }
  };

  const loadWalletTransactions = (playerId, additionalIds = [], setupListener = false, clubIdToMatch = null) => {
    if (!playerId) return;

    // Use passed clubId or fallback to global state
    const targetClubId = clubIdToMatch || clubInfo?.id;

    const walletRef = collection(db, 'walletTransactions');

    // Collect all possible IDs (Firebase UID, player ID from team)
    const allIds = [playerId, ...additionalIds].filter(Boolean);
    const uniqueIds = [...new Set(allIds)];

    console.log('🔍 Wallet transactions for userIds:', uniqueIds);

    // Query for all possible user IDs
    const q = query(
      walletRef,
      where('userId', 'in', uniqueIds.length > 0 ? uniqueIds : [playerId])
    );

    const processTransactions = (snapshot) => {
      const transactions = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Support legacy schema missing clubId, or explicitly matching active club
        if (data.clubId === targetClubId || !data.clubId) {
          transactions.push({ id: docSnap.id, ...data });
        }
      });
      console.log('✅ Wallet transactions updated:', transactions.length);
      setWalletTransactions(transactions);
    };

    if (setupListener) {
      console.log('🔵 Setting up real-time wallet transactions listener');
      const unsubscribe = onSnapshot(q, processTransactions, (error) => {
        console.error('❌ Error in wallet transactions listener:', error);
        setWalletTransactions([]);
      });
      return unsubscribe;
    } else {
      // One-time fetch for backwards compatibility
      getDocs(q).then(processTransactions).catch((error) => {
        console.error('❌ Error loading wallet transactions:', error);
        setWalletTransactions([]);
      });
    }
  };

  const loadPaymentSubmissions = async (clubId, playerId) => {
    try {
      console.log('🔵 Loading payment submissions for player:', playerId);

      const paymentsRef = collection(db, 'paymentSubmissions');
      const q = query(
        paymentsRef,
        where('clubId', '==', clubId),
        where('playerId', '==', playerId)
      );
      const snapshot = await getDocs(q);

      const submissions = [];
      snapshot.forEach((doc) => {
        submissions.push({ id: doc.id, ...doc.data() });
      });

      console.log('✅ Found', submissions.length, 'payment submissions');
      setPaymentSubmissions(submissions);
    } catch (error) {
      console.error('❌ Error loading payment submissions:', error);
      setPaymentSubmissions([]);
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentAmount || !paymentMethod || !paymentDate || !paymentNotes) {
      alert('Please fill in all required fields');
      return;
    }

    if (parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }



    const isDonation = !paymentTournamentId && netBalance <= 0;

    // Warn if payment amount exceeds balance due (ONLY if not already a donation scenario)
    if (!isDonation && parseFloat(paymentAmount) > netBalance) {
      const confirmOverpay = confirm(
        `Your current balance is ${formatCurrency(netBalance)}, but you're submitting ${formatCurrency(parseFloat(paymentAmount))}. Do you want to continue?`
      );
      if (!confirmOverpay) return;
    }

    // Receipt is mandatory only for Benevity
    if (paymentMethod === 'Benevity' && !paymentReceipt) {
      alert('Please upload a payment receipt for Benevity donations');
      return;
    }

    try {
      setUploadingPayment(true);

      // Upload receipt to Firebase Storage (if provided)
      let receiptUrl = null;
      if (paymentReceipt) {
        const receiptRef = ref(storage, `payment-receipts/${clubInfo.id}/${playerData.id}/${Date.now()}_${paymentReceipt.name}`);
        await uploadBytes(receiptRef, paymentReceipt);
        receiptUrl = await getDownloadURL(receiptRef);
      }

      // Create payment submission
      const submissionsRef = collection(db, 'paymentSubmissions');
      const paymentDoc = await addDoc(submissionsRef, {
        clubId: clubInfo.id,
        playerId: playerData.id,
        playerName: playerData.name,
        playerEmail: user.email,
        amount: parseFloat(paymentAmount),
        paymentMethod: paymentMethod,
        paymentDate: paymentDate,
        transactionId: paymentTransactionId || null,
        notes: paymentNotes || null,
        receiptURL: receiptUrl,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        submittedBy: user.email,
        isDonation: isDonation, // Flag as donation if balance was <= 0
        // Tag the tournament so the approved wallet transaction can be scoped correctly
        tournamentId: paymentTournamentId || null,
        tournamentName: paymentTournamentId
          ? (tournaments.find(t => t.id === paymentTournamentId)?.name || null)
          : null
      });

      // Send notification to all club admins
      try {
        // Get the club document to find the admins array (same source as Firestore rules)
        const clubRef = doc(db, 'club', clubInfo.id);
        const clubSnap = await getDoc(clubRef);

        if (clubSnap.exists()) {
          const clubData = clubSnap.data();
          const adminUids = clubData.admins || [];

          console.log('🔵 Sending payment notifications to admins:', adminUids);

          const notificationsRef = collection(db, 'notifications');

          for (const adminUid of adminUids) {
            // Don't notify the submitter if they're an admin
            if (adminUid !== user.uid) {
              await addDoc(notificationsRef, {
                userId: adminUid,
                type: 'payment_submitted',
                message: `💰 ${playerData.name} submitted a payment of ${formatCurrency(parseFloat(paymentAmount))} - pending approval`,
                playerName: playerData.name,
                amount: parseFloat(paymentAmount),
                paymentMethod: paymentMethod,
                paymentId: paymentDoc.id,
                receiptURL: receiptURL,
                read: false,
                createdAt: new Date().toISOString(),
                clubId: clubInfo.id
              });
              console.log('✅ Notification sent to admin:', adminUid);
            }
          }
        }
        console.log('✅ Notifications sent to club admins');
      } catch (notifError) {
        console.error('Error sending admin notifications:', notifError);
        // Don't fail the whole operation if notifications fail
      }

      alert('Payment submitted successfully! Admins have been notified and will review it.');

      // Reset form
      setShowRecordPayment(false);
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentDate('');
      setPaymentTournamentId('');
      setPaymentTransactionId('');
      setPaymentNotes('');
      setPaymentReceipt(null);

      // Reload payment submissions
      await loadPaymentSubmissions(clubInfo.id, playerData.id);

    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('Error submitting payment. Please try again.');
    } finally {
      setUploadingPayment(false);
    }
  };

  const handleCaptainCreateGame = async (gameData) => {
    try {
      const docRef = await addDoc(collection(db, 'games'), gameData);
      alert('Game created successfully!');
      setShowAddGame(false);
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert('Error creating game');
    }
  };

  const handleCaptainUpdateGame = async (gameId, updatedData) => {
    try {
      await updateDoc(doc(db, 'games', gameId), updatedData);
      alert('Game updated successfully!');
      setEditingGame(null);
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert('Error updating game');
    }
  };

  // CAPTAIN'S CORNER FUNCTIONS
  const getUpcomingGamesForCaptain = () => {
    if (!playerData?.teamId && (!playerData?.allTeams || playerData.allTeams.length === 0)) return [];

    const now = new Date();
    const fourteenDaysFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

    // Get all team IDs where the player is a captain or vice-captain
    const leadershipTeamIds = (playerData.allTeams || [])
      .filter(t => t.teamRole === 'captain' || t.teamRole === 'vice-captain')
      .map(t => t.teamId);
      
    // Always include primary team if they are captain/vc there
    if (playerData.teamId && (playerData.teamRole === 'captain' || playerData.teamRole === 'vice-captain')) {
        if (!leadershipTeamIds.includes(playerData.teamId)) {
            leadershipTeamIds.push(playerData.teamId);
        }
    }

    const allUpcomingGames = gamesPlayed.filter(game => {
      const status = getGameStatus(game);
      if (status === 'Completed' || status === 'Abandoned' || status === 'Cancelled') return false;
      
      const gameDate = new Date(game.date);
      
      const gameTeamIds = [game.teamId, game.homeTeamId, game.awayTeamId].filter(Boolean);
      const isLeadershipGame = gameTeamIds.some(id => leadershipTeamIds.includes(id));
      
      return isLeadershipGame && gameDate >= now;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));

    const gamesIn14Days = allUpcomingGames.filter(game => {
      const gameDate = new Date(game.date);
      return gameDate <= fourteenDaysFromNow;
    });

    if (gamesIn14Days.length > 0) {
      return gamesIn14Days;
    } else if (allUpcomingGames.length > 0) {
      return [allUpcomingGames[0]];
    }

    return [];
  };

  const getGameTeamRole = (game) => {
    if (!game || !playerData) return 'player';
    
    // If player has multiple teams, check which one this game belongs to
    if (playerData.allTeams && playerData.allTeams.length > 0) {
      const possibleTeamIds = [game.teamId, game.homeTeamId, game.awayTeamId].filter(Boolean);
      if (possibleTeamIds.length > 0) {
        const matchTeam = playerData.allTeams.find(t => possibleTeamIds.includes(t.teamId));
        if (matchTeam) {
          return matchTeam.teamRole || 'player';
        }
      }
    }
    
    // Fallback to primary team role if the game is for their primary team
    const isPrimaryTeamGame = game.teamId === playerData.teamId || game.homeTeamId === playerData.teamId || game.awayTeamId === playerData.teamId;
    if (isPrimaryTeamGame) {
      return playerData.teamRole || 'player';
    }
    
    return 'player';
  };

  // Helper to get the correct roster for a specific game
  const getGameTeamPlayers = (game) => {
    if (!game) return teamPlayers;

    const possibleTeamIds = [game.teamId, game.homeTeamId, game.awayTeamId].filter(Boolean);
    const possibleTeamNames = [game.teamName, game.homeTeamName, game.awayTeamName].filter(Boolean).map(n => n.toLowerCase());

    if (possibleTeamIds.length > 0 || possibleTeamNames.length > 0) {
      // Check if it's the primary team
      if (possibleTeamIds.includes(playerData?.teamId) || possibleTeamNames.includes((playerData?.teamName || '').toLowerCase())) {
        return teamPlayers;
      }

      // If it's a different team, try to find it in clubTeams which has the full roster
      if (clubTeams && clubTeams.length > 0) {
        const matchTeam = clubTeams.find(t => possibleTeamIds.includes(t.teamId) || possibleTeamIds.includes(t.id) || possibleTeamNames.includes((t.teamName || '').toLowerCase()));
        if (matchTeam && matchTeam.players) {
          return matchTeam.players;
        }
      }

      // Fallback to playerData.allTeams
      if (playerData?.allTeams) {
        const matchTeam = playerData.allTeams.find(t => possibleTeamIds.includes(t.teamId) || possibleTeamNames.includes((t.teamName || '').toLowerCase()));
        if (matchTeam && matchTeam.players) {
          return matchTeam.players;
        }
      }
      
      // If we couldn't find the secondary team's players, return an empty array 
      // instead of incorrectly showing the primary team's roster
      return [];
    }
    return teamPlayers;
  };

  const getAvailabilityStats = (game) => {
    const gameAvailability = game.availability || {};
    const gameTeamPlayers = getGameTeamPlayers(game);

    let available = 0;
    let unavailable = 0;
    let maybe = 0;

    // Count all explicit responses from anyone
    Object.values(gameAvailability).forEach(response => {
      if (response.status === 'available') available++;
      else if (response.status === 'unavailable') unavailable++;
      else if (response.status === 'maybe') maybe++;
    });

    // Calculate non-responders strictly from the game's team roster
    let notResponded = 0;
    const nonResponders = [];

    gameTeamPlayers.forEach(player => {
      const playerId = player.userId || player.id;
      if (!gameAvailability[playerId]?.status) {
        notResponded++;
        nonResponders.push(player);
      }
    });

    const total = available + unavailable + maybe + notResponded;

    return { available, unavailable, maybe, notResponded, nonResponders, total };
  };

  const handleSendAvailabilityReminder = async (game) => {
    if (sendingReminderForGame === game.id) return;
    
    const stats = getAvailabilityStats(game);
    if (stats.notResponded === 0) return;

    setSendingReminderForGame(game.id);
    try {
      const notificationsRef = collection(db, 'notifications');
      const promises = stats.nonResponders.map(player => {
        const playerId = player.userId || player.id;
        if (!playerId || playerId.startsWith('guest')) return Promise.resolve();

        const notificationData = {
          userId: playerId,
          type: 'availability_reminder',
          read: false,
          createdAt: new Date().toISOString(),
          clubId: clubInfo?.id,
          gameId: game.id,
          gameName: game.name,
          gameDate: game.date,
          title: 'Availability Reminder',
          message: `Please update your availability for ${game.name}.`,
          createdBy: playerData?.name || 'Captain'
        };
        return addDoc(notificationsRef, notificationData);
      });

      await Promise.all(promises);
      
      setTimeout(() => {
        setSendingReminderForGame(null);
      }, 1000);
      
    } catch (error) {
      console.error('Error sending availability reminders:', error);
      setSendingReminderForGame(null);
    }
  };

  // Get detailed availability breakdown with player names
  const getAvailabilityDetails = (game) => {
    const gameAvailability = game.availability || {};
    const gameTeamPlayers = getGameTeamPlayers(game);

    const availablePlayers = [];
    const unavailablePlayers = [];
    const maybePlayers = [];
    const notRespondedPlayers = [];

    const respondedPlayerIds = new Set();

    // Process all explicit responses
    Object.entries(gameAvailability).forEach(([playerId, response]) => {
      const status = response.status;
      const note = response.note;

      respondedPlayerIds.add(playerId);

      const teamPlayer = gameTeamPlayers.find(p => (p.userId || p.id) === playerId);
      let playerName = teamPlayer?.name || response.playerName || 'Unknown Player';

      if (note) {
        playerName = `${playerName} - "${note}"`;
      }

      if (status === 'available') availablePlayers.push(playerName);
      else if (status === 'unavailable') unavailablePlayers.push(playerName);
      else if (status === 'maybe') maybePlayers.push(playerName);
    });

    // Process non-responders from the game's team roster
    gameTeamPlayers.forEach(player => {
      const playerId = player.userId || player.id;
      if (!respondedPlayerIds.has(playerId)) {
        notRespondedPlayers.push(player.name || 'Unknown');
      }
    });

    // Sort alphabetically by name
    availablePlayers.sort((a, b) => a.localeCompare(b));
    unavailablePlayers.sort((a, b) => a.localeCompare(b));
    maybePlayers.sort((a, b) => a.localeCompare(b));
    notRespondedPlayers.sort((a, b) => a.localeCompare(b));

    return { availablePlayers, unavailablePlayers, maybePlayers, notRespondedPlayers };
  };

  const generateWhatsAppReminderLink = (game, nonResponders) => {
    if (nonResponders.length === 0) return null;

    const gameDate = formatDate(game.date, {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });

    const names = nonResponders.map(p => p.name.split(' ')[0]).join(', ');
    const message = `🏏 Availability Reminder\n\nHey ${names}!\n\nPlease confirm your availability for:\n📅 ${game.name}\n🗓️ ${gameDate}\n${game.venue ? `📍 ${game.venue}\n` : ''}\nRespond in the app or reply here. Thanks!`;

    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  };

  const generateWhatsAppTeamMessage = (game) => {
    const gameDate = formatDate(game.date, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });

    const stats = getAvailabilityStats(game);
    const message = `🏏 ${game.name}\n📅 ${gameDate}\n${game.venue ? `📍 ${game.venue}\n` : ''}\n✅ Available: ${stats.available}\n❌ Unavailable: ${stats.unavailable}\n❓ Maybe: ${stats.maybe}\n⏳ Pending: ${stats.notResponded}\n\nPlease update your availability in the app!`;

    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  };

  // CALENDAR HELPER FUNCTIONS
  const getCalendarDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ day, date });
    }

    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return { games: [], events: [] };

    // Format local date directly to YYYY-MM-DD without UTC conversion
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // Filter games for this date
    const gamesOnDate = filteredAllGames.filter(game => {
      // If game.date is a string like YYYY-MM-DD, we can compare directly
      if (typeof game.date === 'string' && game.date.includes('-')) {
        return game.date.split('T')[0] === dateStr;
      }
      return false;
    });

    // Filter events for this date
    const eventsOnDate = filteredAllEvents.filter(event => {
      if (typeof event.date === 'string' && event.date.includes('-')) {
        return event.date.split('T')[0] === dateStr;
      }
      return false;
    });

    return { games: gamesOnDate, events: eventsOnDate };
  };

  const hasEventsOnDate = (date) => {
    if (!date) return { hasGames: false, hasEvents: false };
    const { games, events: evts } = getEventsForDate(date);
    return { hasGames: games.length > 0, hasEvents: evts.length > 0 };
  };

  const navigateMonth = (direction) => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedCalendarEvent(null);
  };

  const goToToday = () => {
    setCalendarDate(new Date());
    setSelectedCalendarEvent(null);
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const { games, events: evts } = getEventsForDate(date);
    if (games.length > 0 || evts.length > 0) {
      setSelectedCalendarEvent({ date, games, events: evts });
    } else {
      setSelectedCalendarEvent(null);
    }
  };

  const calculateAmountOwed = () => {
    return getBalanceDueBreakdown().total;
  };

  const calculateAmountPaid = () => {
    // 1. Approved payments from walletTransactions (type='payment' or 'waiver')
    // Negative amounts = money paid by player OR waived (reduces what they owe)
    // 1. Approved payments from walletTransactions (type='payment' or 'waiver')
    // Negative amounts = money paid by player OR waived (reduces what they owe)
    const approvedPaid = walletTransactions
      .filter(t => (t.type === 'payment' || t.type === 'waiver') && t.status === 'approved' && t.amount < 0 && !t.isDonation) // Exclude donations
      .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

    // 2. Pending Benevity payments
    // Requirement: Benevity payments should reduce dues immediately ("Awaited" status)
    const benevityPending = paymentSubmissions
      .filter(p => p.status === 'pending' && p.paymentMethod?.toLowerCase().includes('benevity'))
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    return approvedPaid + benevityPending;
  };

  const getPlayerReimbursementDetails = () => {
    if (!playerData) return { approvedExpenses: [], reimbursementsReceived: [], totalReimbursementsOwed: 0 };

    const playerIds = [
      playerData.id,
      playerData.userId,
      playerData.oderId,
      playerData.oderedPlayerId,
      ...(playerData.matchedPlayerIds || [])
    ].filter(Boolean);
    const uniquePlayerIds = [...new Set(playerIds)];

    // Wallet approved expenses (payouts or positive adjustments)
    const walletApprovedExpenses = walletTransactions.filter(t =>
      uniquePlayerIds.includes(t.userId) &&
      t.status === 'approved' &&
      (t.type === 'expense_reimbursement' || (t.type === 'reimbursement' && t.amount > 0))
    ).map(t => {
      const matchingExp = t.expenseId ? expenses.find(e => e.id === t.expenseId) : null;
      return {
        ...t,
        gameId: t.gameId || matchingExp?.gameId,
        gameName: t.gameName || matchingExp?.gameName || matchingExp?.game?.name,
        tournamentName: t.tournamentName || matchingExp?.tournamentName
      };
    });

    // Direct approved expenses that do not have a wallet transaction
    const seenExpenseIds = new Set(walletApprovedExpenses.map(t => t.expenseId).filter(Boolean));
    const directExpenses = expenses.filter(e =>
      e.addedByPlayerId && uniquePlayerIds.includes(e.addedByPlayerId) &&
      e.approvalStatus === 'approved' &&
      !e.isRegistrationFee && !e.isTournamentFee && !e.isInitialDonation && !e.isClubCovered && e.paidBy !== 'club' &&
      !seenExpenseIds.has(e.id)
    );

    const syntheticTxns = directExpenses.map(e => ({
      id: `synthetic-${e.id}`,
      description: `Reimbursement: ${e.description || 'Approved Expense'}`,
      amount: e.amount,
      createdAt: e.createdAt,
      expenseId: e.id,
      type: 'expense_reimbursement',
      status: 'approved',
      gameId: e.gameId,
      gameName: e.gameName || e.game?.name,
      tournamentName: e.tournamentName
    }));

    const approvedExpenses = [...walletApprovedExpenses, ...syntheticTxns];

    const reimbursementsReceived = walletTransactions.filter(t =>
      uniquePlayerIds.includes(t.userId) &&
      t.status === 'approved' &&
      (t.type === 'reimbursement_paid' || (t.type === 'reimbursement' && t.amount < 0))
    );

    const totalApprovedExpenses = approvedExpenses.reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalReimbursementsReceived = reimbursementsReceived.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

    return {
      approvedExpenses,
      reimbursementsReceived,
      totalReimbursementsOwed: totalApprovedExpenses - totalReimbursementsReceived
    };
  };

  const calculateReimbursementsOwed = () => {
    return getPlayerReimbursementDetails().totalReimbursementsOwed;
  };

  // Calculate NET balance: What player owes minus what they've paid minus what club owes them
  const calculateNetBalance = () => {
    // Use breakdown.total to ensure consistency with what's displayed
    const breakdown = getBalanceDueBreakdown();
    const owed = breakdown.total;              // Fees player owes (all tournaments)
    const paid = calculateAmountPaid();         // Payments player made (all tournaments)
    const reimbursements = calculateReimbursementsOwed(); // Club owes player (all tournaments)

    // Net = owed - paid - reimbursements
    // Positive = player owes money
    // Negative = club owes player (credit)
    // Zero = all settled
    return owed - paid - reimbursements;
  };

  // Get detailed breakdown of what player owes
  const getBalanceDueBreakdown = () => {
    if (!playerData) return { items: [], total: 0 };

    const playerIds = [
      playerData.id,
      playerData.userId,
      playerData.oderId,
      playerData.oderedPlayerId,
      ...(playerData.matchedPlayerIds || [])
    ].filter(Boolean);
    const uniquePlayerIds = [...new Set(playerIds)];

    const playerEmails = [
      playerData.email,
      ...(playerData.matchedPlayerEmails || [])
    ].filter(Boolean).map(e => e.toLowerCase());

    const items = [];
    let total = 0;

    // Initial donations (handling both field names for compatibility)
    const initialDonationAmounts = expenses.filter(e =>
      (e.isInitialDonation || e.isRegistrationFee) &&
      (e.splitAmong?.some(id => uniquePlayerIds.includes(id)) ||
        (e.playerEmail && playerEmails.includes(e.playerEmail.toLowerCase()))) &&
      (e.approvalStatus === 'approved' || !e.approvalStatus) &&
      !e.isDonation // Exclude voluntary donations (informational only)
    );

    initialDonationAmounts.forEach(fee => {
      // Use amountPerPlayer if available (for bulk donations), otherwise amount (for individual initial donations)
      const amount = fee.amountPerPlayer !== undefined ? fee.amountPerPlayer : fee.amount;

      // Determine correct label: Only force "Initial Donation" for the main registration fee
      // For additional donations (which have isRegistrationFee=true but isInitialDonation=false), use their description
      const label = fee.isInitialDonation ? getRegistrationFeeLabel(clubInfo) : (fee.description || 'Registration Fee');

      // Deduplicate: If an entry with the same label and amount already exists, skip it
      // This handles cases where double-entry occurred in the database
      const isDuplicate = items.some(item =>
        item.type === 'registration' &&
        item.description === label &&
        Math.abs(item.amount - amount) < 0.01 // Float comparison
      );

      if (isDuplicate) return;

      items.push({
        id: fee.id,
        type: 'registration',
        description: label,
        amount: amount,
        date: fee.createdAt,
        gameId: null,
        gameName: null
      });
      total += amount;
    });

    // Merchandise Expenses
    const merchandiseExpenses = expenses.filter(e =>
      e.isMerchandise &&
      uniquePlayerIds.includes(e.userId) &&
      (e.approvalStatus === 'approved' || !e.approvalStatus)
    );

    merchandiseExpenses.forEach(exp => {
      items.push({
        id: exp.id,
        type: 'merchandise',
        description: exp.description || 'Merchandise Purchase',
        amount: exp.amount,
        date: exp.createdAt,
        gameId: null,
        gameName: null
      });
      total += exp.amount;
    });

    // Game-based expenses (excludes tournament fees)
    gamesPlayed.forEach(game => {
      if (game.playing11?.some(id => uniquePlayerIds.includes(id))) {
        // Check if tournament is settled
        if (game.tournamentId) {
          const tournament = tournaments.find(t => t.id === game.tournamentId);
          if (tournament && !tournament.isSettled) {
            return; // Skip game expenses and late fees until tournament is settled
          }
        }

        const gameExpenses = expenses.filter(e =>
          e.gameId === game.id &&
          (e.approvalStatus === 'approved' || !e.approvalStatus) &&
          !e.isInitialDonation &&
          !e.isTournamentFee // Exclude tournament fees - handled separately
        );

        const isSuperSub = game.superSubs?.some(id => uniquePlayerIds.includes(id));

        gameExpenses.forEach(expense => {
          // Use actual squad size from playing11, no hardcoded fallback
          const squadSize = game.playing11?.length || 0;
          const superSubCount = game.superSubs?.length || 0;
          const regularPlayerCount = squadSize - superSubCount;

          let playerShare = 0;
          if (squadSize > 0) {
            const totalShares = regularPlayerCount + (superSubCount * 0.5);
            const perPlayerCost = expense.amount / totalShares;
            playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;
          }

          const roundedShare = Math.round(playerShare * 100) / 100;
          if (roundedShare > 0) {
            items.push({
              id: expense.id,
              type: 'game_expense',
              description: expense.description || 'Game Expense',
              amount: roundedShare,
              date: expense.createdAt,
              gameId: game.id,
              gameName: game.name,
              isSuperSub: isSuperSub
            });
            total += roundedShare;
          }
        });

        // Add Late Fees
        const hasLateArrival = uniquePlayerIds.some(id => game.lateArrivals?.[id]);
        if (game.lateArrivals && hasLateArrival) {
          const lateFee = game.lateFeeAmount ?? 5;
          items.push({
            id: `latefee-${game.id}`,
            type: 'late_fee',
            description: 'Late Fee',
            amount: lateFee,
            date: game.date,
            gameId: game.id,
            gameName: game.name
          });
          total += lateFee;
        }
      }
    });

    // Tournament fees from settled tournament fee expenses OR settled tournaments
    const tournamentWideExpenses = expenses.filter(e =>
      (e.isTournamentFee || !e.gameId || e.level === 'tournament') &&
      e.tournamentId &&
      (e.approvalStatus === 'approved' || !e.approvalStatus)
    );

    // No longer filter by selected tournament for global dues/ledger
    const activeTournamentsForBreakdown = tournaments || [];

    activeTournamentsForBreakdown.forEach(t => {
      const tExpenses = tournamentWideExpenses.filter(e => e.tournamentId === t.id);

      tExpenses.forEach(fee => {
        if (fee.settled && fee.splitAmongDetails?.length > 0) {
          const playerShare = fee.splitAmongDetails.find(p =>
            uniquePlayerIds.includes(p.playerId) ||
            uniquePlayerIds.includes(p.oderId) ||
            (p.email && playerEmails.includes(p.email.toLowerCase())) ||
            (p.name && playerData.name && p.name.toLowerCase().trim() === playerData.name.toLowerCase().trim())
          );

          if (playerShare && playerShare.gamesPlayed > 0) {
            const amount = playerShare.amount || 0;
            const perGameShare = amount / playerShare.gamesPlayed;
            const roundedShare = Math.round(perGameShare * 100) / 100;

            // Find valid games player participated in for this tournament
            const tournamentGames = allClubGames.filter(g => g.tournamentId === t.id);
            const playerGames = tournamentGames.filter(g => g.playing11?.some(id => uniquePlayerIds.includes(id)));

            playerGames.forEach(g => {
              items.push({
                id: `${fee.id}-${g.id}`,
                type: 'tournament_fee',
                description: `Tournament Expenses share`,
                amount: roundedShare,
                date: fee.settledAt || fee.createdAt,
                gameId: g.id,
                gameName: g.name || `${g.homeTeam} vs ${g.awayTeam}`,
                tournamentId: fee.tournamentId,
                tournamentName: t.name,
                gamesPlayed: 1
              });
              total += roundedShare;
            });
          }
        } else if (t.isSettled) {
          // Compute dynamically if tournament is settled (mirroring TournamentDetailView)
          const tournamentGames = allClubGames.filter(g => g.tournamentId === t.id);
          const validGames = tournamentGames.filter(g => g.playing11 && g.playing11.length > 0);
          const numGames = validGames.length;

          if (numGames > 0) {
            const expensePerGame = fee.amount / numGames;

            validGames.forEach(g => {
              if (g.playing11?.some(id => uniquePlayerIds.includes(id))) {
                const squadSize = g.playing11.length;
                const superSubCount = g.superSubs?.length || 0;
                const regularCount = squadSize - superSubCount;
                const totalShares = regularCount + (superSubCount * 0.5);

                if (totalShares > 0) {
                  const isSuperSub = g.superSubs?.some(id => uniquePlayerIds.includes(id));
                  const share = isSuperSub
                    ? (expensePerGame / totalShares) * 0.5
                    : expensePerGame / totalShares;
                  
                  const roundedShare = Math.round(share * 100) / 100;
                  items.push({
                    id: `${fee.id}-${g.id}`,
                    type: 'tournament_fee',
                    description: `Tournament Expenses share`,
                    amount: roundedShare,
                    date: fee.createdAt,
                    gameId: g.id,
                    gameName: g.name || `${g.homeTeam} vs ${g.awayTeam}`,
                    tournamentId: fee.tournamentId,
                    tournamentName: t.name,
                    gamesPlayed: 1
                  });
                  total += roundedShare;
                }
              }
            });
          }
        }
      });
    });

    return { items, total: Math.round(total * 100) / 100 };
  };

  // Get approved expenses (wallet transactions) submitted by player
  const getApprovedExpenses = () => {
    return submittedExpenses.filter(e => e.approvalStatus === 'approved');
  };

  // Get pending expenses submitted by player
  const getPendingExpenses = () => {
    return submittedExpenses.filter(e => e.approvalStatus === 'pending');
  };

  // Get denied expenses submitted by player
  const getDeniedExpenses = () => {
    return submittedExpenses.filter(e => e.approvalStatus === 'denied');
  };

  const getPaymentStatus = () => {
    const owed = calculateAmountOwed();
    const paid = calculateAmountPaid();
    const balance = owed - paid;

    if (owed === 0) return { status: 'none', text: 'No payments due', color: 'gray' };
    if (balance <= 0) return { status: 'paid', text: 'Fully Paid', color: 'green' };
    if (paid > 0) return { status: 'partial', text: 'Partially Paid', color: 'yellow' };
    return { status: 'pending', text: 'Payment Due', color: 'red' };
  };

  // Filter games by tournament
  const filteredGames = selectedTournament === 'all'
    ? gamesPlayed
    : gamesPlayed.filter(g => g.tournamentId === selectedTournament);

  const totalGamesPlayed = filteredGames.filter(g => g.playing11?.includes(playerData?.id)).length;
  const totalGamesInClub = filteredGames.length;
  const amountOwed = calculateAmountOwed();
  const amountPaid = calculateAmountPaid();
  const reimbursementsOwed = calculateReimbursementsOwed();
  const netBalance = calculateNetBalance(); // Positive = player owes, Negative = club owes player, Zero = settled
  const balance = netBalance; // Keep 'balance' for backward compatibility
  const totalTournaments = tournaments.length; // Total tournaments in club
  const tournamentsParticipated = new Set(gamesPlayed.filter(g => g.playing11?.includes(playerData?.id)).map(g => g.tournamentId).filter(Boolean)).size; // Tournaments player participated in
  const paymentStatus = getPaymentStatus();

  const gamesByTournament = tournaments.reduce((acc, tournament) => {
    // Show games where player's team is involved (home or away)
    const games = gamesPlayed.filter(game => {
      if (game.tournamentId !== tournament.id) return false;

      // Show if player's team is home or away team
      const playerTeamId = playerData?.teamId;
      return playerTeamId && (game.homeTeamId === playerTeamId || game.awayTeamId === playerTeamId || game.teamId === playerTeamId);
    });

    if (games.length > 0) {
      acc[tournament.id] = {
        tournament,
        games,
        expenses: calculateTournamentExpenses(tournament.id)
      };
    }
    return acc;
  }, {});

  // Auto-select first tournament when tournaments load
  useEffect(() => {
    const tournamentsList = Object.values(gamesByTournament);
    if (tournamentsList.length > 0 && selectedTournament === 'all') {
      setSelectedTournament(tournamentsList[0].tournament.id);
    }
  }, [gamesByTournament, selectedTournament]);

  function calculateTournamentExpenses(tournamentId) {
    const tournamentGames = gamesPlayed.filter(game => game.tournamentId === tournamentId);
    let total = 0;

    // 1. Calculate regular game expenses (Snacks & Drinks, etc.)
    tournamentGames.forEach(game => {
      const gameExpenses = expenses.filter(e =>
        e.gameId === game.id &&
        !e.isTournamentFee &&
        !e.isInitialDonation &&
        e.approvalStatus === 'approved'
      );
      gameExpenses.forEach(expense => {
        const squadSize = game.playing11?.length || 11;
        total += expense.amountPerPlayer || (expense.amount / squadSize);
      });
    });

    // 2. Add tournament fee share (if settled)
    if (playerData?.id) {
      const tournamentFee = expenses.find(e =>
        e.isTournamentFee &&
        e.tournamentId === tournamentId &&
        e.settled &&
        e.splitAmongDetails?.length > 0
      );

      if (tournamentFee) {
        const playerShare = tournamentFee.splitAmongDetails.find(p =>
          p.oderId === playerData.id ||
          p.oderId === playerData.oderId ||
          p.oderId === playerData.oderedPlayerId
        );

        if (playerShare) {
          total += playerShare.amount;
        }
      }
    }

    return total;
  }

  const getGameExpenses = (gameId) => {
    // Find the game to get tournament info
    const game = gamesPlayed.find(g => g.id === gameId);

    // 1. Calculate regular game expenses (Snacks & Drinks, etc.)
    const gameExpenses = expenses.filter(e =>
      e.gameId === gameId &&
      !e.isTournamentFee &&
      !e.isInitialDonation &&
      e.approvalStatus === 'approved'
    );

    let playerShare = gameExpenses.reduce((sum, expense) => {
      // Use amountPerPlayer if available, otherwise divide by squad size or 11
      const squadSize = game?.playing11?.length || 11;
      return sum + (expense.amountPerPlayer || (expense.amount / squadSize));
    }, 0);

    // 2. Add tournament fee share for this game (if tournament is settled)
    if (game?.tournamentId && playerData?.id) {
      // Find settled tournament fee expense
      const tournamentFee = expenses.find(e =>
        e.isTournamentFee &&
        e.tournamentId === game.tournamentId &&
        e.settled &&
        e.splitAmongDetails?.length > 0
      );

      if (tournamentFee) {
        // Find this player's share in the tournament
        const playerTournamentShare = tournamentFee.splitAmongDetails.find(p =>
          p.oderId === playerData.id ||
          p.oderId === playerData.oderId ||
          p.oderId === playerData.oderedPlayerId
        );

        if (playerTournamentShare && playerTournamentShare.gamesPlayed > 0) {
          // Divide their total tournament share by games played to get per-game amount
          const perGameTournamentShare = playerTournamentShare.amount / playerTournamentShare.gamesPlayed;
          playerShare += perGameTournamentShare;
        }
      }
    }

    return playerShare;
  };

  const handleSaveWhatsAppLink = async () => {
    if (!playerData?.teamId) return;

    setSavingWhatsApp(true);
    try {
      const teamRef = doc(db, 'teams', playerData.teamId);
      await updateDoc(teamRef, {
        whatsappGroupLink: whatsAppLink || '',
        updatedAt: new Date().toISOString()
      });

      // Update local playerData
      setPlayerData({
        ...playerData,
        teamWhatsAppLink: whatsAppLink || ''
      });

      setEditingWhatsApp(false);
      alert('WhatsApp group link updated successfully!');
    } catch (error) {
      console.error('Error updating WhatsApp link:', error);
      alert('Error updating WhatsApp link. Please try again.');
    } finally {
      setSavingWhatsApp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Evaluate club-specific roles
  const clubMembership = clubInfo?.id ? user?.joinedClubs?.[clubInfo.id] : null;

  // Check if user is an admin for the ACTIVE club
  const isUserAdmin = 
    user?.role === 'super-admin' ||
    clubMembership?.role === 'admin' || 
    clubMembership?.role === 'super-admin' ||
    // Legacy fallback ONLY if there is no granular membership defined:
    (!clubMembership && (
      clubInfo?.admins?.includes(user?.uid) ||
      user?.permissions?.clubAdminOf?.includes(clubInfo?.id) ||
      user?.role === 'admin' || 
      user?.clubInfo?.role === 'admin'
    ));

  const isUserTreasurer = 
    clubInfo?.treasurers?.includes(user?.uid) ||
    clubMembership?.role === 'treasurer' || 
    clubMembership?.additionalRoles?.includes('treasurer') ||
    // Legacy fallback
    (!clubMembership && (user?.role === 'treasurer' || user?.clubInfo?.role === 'treasurer' || 
     user?.additionalRoles?.includes('treasurer') || user?.clubInfo?.additionalRoles?.includes('treasurer')));
    
  // Check if current active club has the archival lock physically engaged and user is not an admin bypasser
  const isClubArchivedForUser = clubInfo?.isArchived === true && !isUserAdmin;

  // Check if email is verified - for NEW users who haven't verified their email yet
  // Show verification screen if:
  // 1. User's email is not verified
  // 2. User is a NEW user (status is 'pending' or just signed up)
  // Existing users (no status or status 'approved') are NOT blocked
  const userStatus = user?.clubInfo?.status;
  const isNewPendingUser = userStatus === 'pending';
  const isNewUserNeedingVerification = user &&
    !user.emailVerified &&
    isNewPendingUser;

  if (isNewUserNeedingVerification) {
    const EmailVerificationScreen = () => {
      const [resending, setResending] = useState(false);
      const [checking, setChecking] = useState(false);
      const [message, setMessage] = useState('');
      const [messageType, setMessageType] = useState(''); // 'success' or 'error'

      const handleResendEmail = async () => {
        setResending(true);
        setMessage('');
        try {
          const result = await authService.sendVerificationEmail();
          if (result.success) {
            setMessage('Verification email sent! Please check your inbox.');
            setMessageType('success');
          } else {
            setMessage(result.error);
            setMessageType('error');
          }
        } catch (error) {
          setMessage('Failed to send verification email. Please try again.');
          setMessageType('error');
        } finally {
          setResending(false);
        }
      };

      const handleCheckVerification = async () => {
        setChecking(true);
        setMessage('');
        try {
          const result = await authService.reloadUser();
          if (result.success && result.user.emailVerified) {
            window.location.reload();
          } else {
            setMessage('Email not yet verified. Please check your inbox and click the verification link.');
            setMessageType('error');
          }
        } catch (error) {
          setMessage('Failed to check verification status. Please try again.');
          setMessageType('error');
        } finally {
          setChecking(false);
        }
      };

      return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
          <Header user={user} role="player" onLogout={onLogout} playerData={playerData} notifications={notifications} onNotificationClick={() => setShowNotifications(!showNotifications)} isUserAdmin={isUserAdmin} canSwitchToAdmin={canSwitchToAdmin} />
          <div className="max-w-md mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="text-blue-600" size={40} />
              </div>
              <h2 className="text-2xl font-bold mb-2">Verify Your Email 📧</h2>
              <p className="text-gray-600 mb-4">
                Please verify your email address to continue.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Check your inbox for a verification link sent to <strong>{user.email}</strong>
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  The verification link expires in 24 hours. If you don't see the email, check your spam folder.
                </p>
              </div>

              {message && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${messageType === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                  {message}
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleCheckVerification}
                  disabled={checking}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {checking ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      Checking...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      I've Verified My Email
                    </>
                  )}
                </button>
                <button
                  onClick={handleResendEmail}
                  disabled={resending}
                  className="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {resending ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={18} />
                      Resend Verification Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return <EmailVerificationScreen />;
  }

  // NOTE: Registration form handling has been moved to RegistrationGuard component
  // RegistrationGuard will handle showing the registration form when needed

  // Check if player was rejected
  if (user?.clubInfo?.status === 'rejected') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <Header user={user} role="player" onLogout={onLogout} playerData={playerData} notifications={notifications} onNotificationClick={() => setShowNotifications(!showNotifications)} isUserAdmin={isUserAdmin} canSwitchToAdmin={canSwitchToAdmin} />
        <div className="max-w-xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-red-600" size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Request Declined</h2>
            <p className="text-gray-600 mb-4">
              Your request to join <span className="font-semibold">{user?.clubInfo?.clubName}</span> was not approved.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                If you believe this was a mistake, please contact your club admin directly.
              </p>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Want to join a different club?
              </p>
              <button
                onClick={async () => {
                  // Clear club info so they can join a new club
                  try {
                    const userRef = doc(db, 'users', user.uid);
                    await updateDoc(userRef, {
                      clubInfo: null,
                      clubId: null
                    });
                    window.location.reload();
                  } catch (error) {
                    console.error('Error clearing club info:', error);
                    alert('Error. Please try logging out and back in.');
                  }
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
              >
                Join Another Club
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if player is pending approval
  if (user?.clubInfo?.status === 'pending' || clubInfo?.status === 'pending') {
    const isNewClubAdmin = user?.role === 'admin';
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <Header user={user} role="player" onLogout={onLogout} playerData={playerData} notifications={notifications} onNotificationClick={() => setShowNotifications(!showNotifications)} isUserAdmin={isUserAdmin} canSwitchToAdmin={canSwitchToAdmin} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="text-yellow-600" size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Approval Pending ⏳</h2>
            <p className="text-gray-600 mb-4">
              {isNewClubAdmin 
                ? `Your request to register ` 
                : `Your request to join `}
              <span className="font-semibold">{user?.clubInfo?.clubName || clubInfo?.name}</span> has been submitted.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <p className="text-sm text-yellow-800">
                {isNewClubAdmin
                  ? "The Super Admin will review your registration and approve your club. You'll receive an email once approved."
                  : "A club admin will review your request and approve your access. You'll be able to access your dashboard once approved."}
              </p>
            </div>
            <div className="text-sm text-gray-500 mb-6">
              <p>Submitted on: {formatDate(user?.clubInfo?.joinedAt || clubInfo?.joinedAt, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
            >
              🔄 Check Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show JoinClubForm ONLY if user has NO club association at all
  // Users with clubInfo (even if clubInfo failed to load) should NOT see this
  const userHasNoClub = !user?.clubInfo?.clubId && !user?.clubId;

  if (userHasNoClub && (!playerData || !clubInfo)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <Header user={user} role="player" onLogout={onLogout} playerData={playerData} notifications={notifications} onNotificationClick={() => setShowNotifications(!showNotifications)} isUserAdmin={isUserAdmin} canSwitchToAdmin={canSwitchToAdmin} />
        <div className="max-w-xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-green-600" size={40} />
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome to ClubHudl! 🏆</h2>
              <p className="text-gray-600">
                Enter your club's invite code to join as a player.
              </p>
            </div>

            <JoinClubForm
              user={user}
              onJoinSuccess={() => {
                window.location.reload();
              }}
            />

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                Don't have a club code? Ask your club admin for the invite code, or check your email for a registration link.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user has club but data failed to load, show error screen
  if (!playerData || !clubInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <Header user={user} role="player" onLogout={onLogout} playerData={playerData} notifications={notifications} onNotificationClick={() => setShowNotifications(!showNotifications)} isUserAdmin={isUserAdmin} canSwitchToAdmin={canSwitchToAdmin} />
        <div className="max-w-xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-red-600" size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Unable to Load Data</h2>
            <p className="text-gray-600 mb-4">
              We couldn't load your club information. This might be a temporary issue.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
              >
                🔄 Try Again
              </button>
              <button
                onClick={onLogout}
                className="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't wrap with RegistrationGuard until we have club info
  if (!clubInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading club information...</p>
        </div>
      </div>
    );
  }

  // --- Extract Captain's Corner Render Function ---
  const renderCaptainsCorner = () => {
    const isGloballyCaptainOrVC = playerData && (
      playerData.teamRole === 'captain' || 
      playerData.teamRole === 'vice-captain' || 
      playerData.allTeams?.some(t => t.teamRole === 'captain' || t.teamRole === 'vice-captain')
    );
    if (!isGloballyCaptainOrVC) return null;

    return (
      <div id="captains-corner" className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl shadow-sm mb-8 overflow-hidden">
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-yellow-100/50 transition-colors"
          onClick={() => setCaptainViewExpanded(!captainViewExpanded)}
        >
          <div className="flex items-center gap-3">
            <div className="bg-yellow-500 text-white p-2 rounded-lg">
              <Shield size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-yellow-900">Captain's Corner</h3>
              <p className="text-sm text-yellow-700">
                {(() => {
                   const teamsWhereCaptain = (playerData?.allTeams || []).filter(t => t.teamRole === 'captain' || t.teamRole === 'vice-captain');
                   if (teamsWhereCaptain.length > 1) {
                     return '⭐ Captain/Vice Captain • Multiple Teams';
                   }
                   const activeTeam = teamsWhereCaptain[0] || playerData;
                   return `${activeTeam.teamRole === 'captain' ? '⭐ Captain' : '🔷 Vice Captain'} • ${activeTeam.teamName}`;
                })()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
              {teamPlayers.length} players
            </span>
            <svg
              className={`w-5 h-5 text-yellow-700 transition-transform ${captainViewExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Expanded Content */}
        {captainViewExpanded && (
          <div className="p-4 pt-0 space-y-4">
            {/* Quick Actions */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowTeamContacts(!showTeamContacts)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-yellow-300 rounded-lg text-yellow-800 hover:bg-yellow-50 font-medium text-sm"
              >
                <Phone size={16} />
                {showTeamContacts ? 'Hide' : 'Show'} Team Roster
              </button>
              <button
                onClick={() => setShowWhatsAppMessenger(true)}
                className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                title="Open WhatsApp Messenger"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                <span className="text-sm font-medium">WhatsApp</span>
              </button>
              <button
                onClick={() => setShowAddGame(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm transition-all"
                title="Add New Game"
              >
                <Calendar size={16} />
                Add Game
              </button>
            </div>

            {/* Team Contacts Panel */}
            {showTeamContacts && (
              <div className="bg-white rounded-lg border border-yellow-200 p-4">
                <div className="flex items-center justify-between mb-3 border-b border-yellow-100 pb-2">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Users size={18} />
                    Team Roster
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowAddPlayerModal(true)}
                      className="text-xs bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-lg hover:bg-yellow-200 font-medium flex items-center gap-1 transition-colors"
                    >
                      <UserPlus size={14} /> Add Player
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {teamPlayers.map(player => (
                    <div key={player.userId || player.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-semibold">
                          {player.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{player.name}</p>
                          {player.teamRole && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${player.teamRole === 'captain' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                              {player.teamRole === 'captain' ? 'Captain' : 'Vice Captain'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {player.phone && (
                          <a
                            href={`tel:${player.phone}`}
                            className="text-blue-600 hover:text-blue-800"
                            title={player.phone}
                          >
                            <Phone size={16} />
                          </a>
                        )}
                        {player.id !== user.uid && player.userId !== user.uid && player.teamRole !== 'captain' && (
                          <button
                            onClick={() => handleRemovePlayerFromTeam(player.id || player.userId, player.name)}
                            className="text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                            title={`Remove ${player.name} from team`}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Games Availability */}
            {(() => {
              let upcomingGames = getUpcomingGamesForCaptain();
              
              // Apply active dashboard filters
              // Apply active dashboard filters
              if (activeFilters.quicks.includes('Action on me')) {
                upcomingGames = upcomingGames.filter(g => !availability[g.id] && !g.isNotRostered);
              }
              if (activeFilters.quicks.includes('Practice')) {
                upcomingGames = upcomingGames.filter(g => !g.tournamentId);
              }
              if (activeFilters.tournaments.length > 0) {
                upcomingGames = upcomingGames.filter(g => activeFilters.tournaments.includes(g.tournamentId));
              }
              if (activeFilters.teams.length > 0) {
                const teamIds = availableTeamsForFilter.filter(t => activeFilters.teams.includes(t.teamName)).map(t => t.teamId);
                upcomingGames = upcomingGames.filter(g =>
                  teamIds.includes(g.teamId) || teamIds.includes(g.homeTeamId) || teamIds.includes(g.awayTeamId) ||
                  activeFilters.teams.includes(g.teamName) || activeFilters.teams.includes(g.homeTeam) || activeFilters.teams.includes(g.awayTeam)
                );
              }
              if (activeFilters.formats.length > 0) {
                upcomingGames = upcomingGames.filter(g => {
                   const computedStr = getComputedGameFormat(g).toLowerCase();
                   return activeFilters.formats.some(f => computedStr.includes(f));
                });
              }
              if (dateRangeFilter.start || dateRangeFilter.end) {
                upcomingGames = upcomingGames.filter(g => {
                  const gDate = new Date(g.date);
                  gDate.setHours(0, 0, 0, 0);
                  
                  let isValid = true;
                  if (dateRangeFilter.start) {
                    const sDate = new Date(dateRangeFilter.start);
                    sDate.setMinutes(sDate.getMinutes() + sDate.getTimezoneOffset());
                    sDate.setHours(0, 0, 0, 0);
                    if (gDate < sDate) isValid = false;
                  }
                  if (dateRangeFilter.end) {
                    const eDate = new Date(dateRangeFilter.end);
                    eDate.setMinutes(eDate.getMinutes() + eDate.getTimezoneOffset());
                    eDate.setHours(23, 59, 59, 999);
                    if (gDate > eDate) isValid = false;
                  }
                  return isValid;
                });
              }

              if (upcomingGames.length === 0) {
                return (
                  <div className="bg-white rounded-lg border border-yellow-200 p-4 text-center">
                    <Calendar className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-gray-600 text-sm">No upcoming games scheduled.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  <h4 className="font-semibold text-yellow-900 flex items-center gap-2">
                    <Calendar size={18} />
                    Upcoming Games - Availability Overview
                  </h4>

                  {upcomingGames.map(game => {
                    const stats = getAvailabilityStats(game);
                    let gameDate;
                    if (game.date && typeof game.date === 'string' && game.date.includes('-')) {
                      const [y, m, d] = game.date.split('T')[0].split('-');
                      gameDate = new Date(y, m - 1, d);
                    } else {
                      gameDate = new Date(game.date);
                      gameDate.setHours(0, 0, 0, 0);
                    }

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const tomorrow = new Date(today);
                    tomorrow.setDate(tomorrow.getDate() + 1);

                    const isToday = gameDate.getTime() === today.getTime();
                    const isTomorrow = gameDate.getTime() === tomorrow.getTime();
                    const daysUntil = Math.round((gameDate - today) / (1000 * 60 * 60 * 24));

                    return (
                      <div key={game.id} className="bg-white rounded-lg border border-yellow-200 p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h5 className="font-semibold text-gray-800">{game.name}</h5>
                              <span className={`text-xs px-2 py-1 rounded-full font-semibold ${isToday ? 'bg-red-100 text-red-700' :
                                isTomorrow ? 'bg-orange-100 text-orange-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                {isToday ? 'TODAY' : isTomorrow ? 'TOMORROW' : `${daysUntil} days`}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {formatDate(game.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                              {game.venue && ` • ${game.venue}`}
                            </p>
                          </div>

                          {/* Quick Stats */}
                          <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <CheckCircle size={16} /> {stats.available}
                            </span>
                            <span className="flex items-center gap-1 text-red-600 font-medium">
                              <XCircle size={16} /> {stats.unavailable}
                            </span>
                            <span className="flex items-center gap-1 text-yellow-600 font-medium">
                              <HelpCircle size={16} /> {stats.maybe}
                            </span>
                            {stats.notResponded > 0 && (
                              <span className="flex items-center gap-1 text-gray-500 font-medium">
                                <Clock size={16} /> {stats.notResponded}
                              </span>
                            )}
                            <button
                              onClick={() => setExpandedAvailabilityGameId(expandedAvailabilityGameId === game.id ? null : game.id)}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1 ml-2"
                            >
                              {expandedAvailabilityGameId === game.id ? 'Hide Details' : 'View Details'}
                              <ChevronDown size={12} className={`transition-transform ${expandedAvailabilityGameId === game.id ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
                            <div
                              className="bg-green-500 transition-all"
                              style={{ width: `${(stats.available / stats.total) * 100}%` }}
                            />
                            <div
                              className="bg-red-500 transition-all"
                              style={{ width: `${(stats.unavailable / stats.total) * 100}%` }}
                            />
                            <div
                              className="bg-yellow-500 transition-all"
                              style={{ width: `${(stats.maybe / stats.total) * 100}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {stats.available + stats.maybe} potential players • {stats.notResponded} pending responses
                          </p>
                        </div>

                        {/* Expanded Game Availability Details */}
                        {expandedAvailabilityGameId === game.id && (
                          <div className="mb-4 bg-gray-50 rounded-lg p-3 border border-gray-100">
                            {(() => {
                              const details = getAvailabilityDetails(game);
                              return (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  {/* Available */}
                                  <div>
                                    <p className="text-xs font-bold text-green-700 mb-1 flex items-center gap-1">
                                      <CheckCircle size={10} /> Available ({stats.available})
                                    </p>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      {details.availablePlayers.length > 0 ? (
                                        <ul className="list-disc list-inside">
                                          {details.availablePlayers.map((name, i) => (
                                            <li key={i} className="break-words pr-2">{name}</li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <span className="italic text-gray-400">None</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Unavailable */}
                                  <div>
                                    <p className="text-xs font-bold text-red-700 mb-1 flex items-center gap-1">
                                      <XCircle size={10} /> Unavailable ({stats.unavailable})
                                    </p>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      {details.unavailablePlayers.length > 0 ? (
                                        <ul className="list-disc list-inside">
                                          {details.unavailablePlayers.map((name, i) => (
                                            <li key={i} className="break-words pr-2">{name}</li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <span className="italic text-gray-400">None</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Maybe */}
                                  <div>
                                    <p className="text-xs font-bold text-yellow-700 mb-1 flex items-center gap-1">
                                      <HelpCircle size={10} /> Maybe ({stats.maybe})
                                    </p>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      {details.maybePlayers.length > 0 ? (
                                        <ul className="list-disc list-inside">
                                          {details.maybePlayers.map((name, i) => (
                                            <li key={i} className="break-words pr-2">{name}</li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <span className="italic text-gray-400">None</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Not Responded */}
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-bold text-gray-600 flex items-center gap-1">
                                        <Clock size={10} /> Pending ({stats.notResponded})
                                      </p>
                                      {stats.notResponded > 0 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSendAvailabilityReminder(game);
                                          }}
                                          disabled={sendingReminderForGame === game.id}
                                          className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                                        >
                                          {sendingReminderForGame === game.id ? 'Sending...' : 'Send Reminder'}
                                        </button>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      {details.notRespondedPlayers.length > 0 ? (
                                        <ul className="list-disc list-inside">
                                          {details.notRespondedPlayers.map((name, i) => (
                                            <li key={i} className="break-words pr-2">{name}</li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <span className="italic text-gray-400">None</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Captain Meal Preferences Summary */}
                            {game.askMealPreference && game.mealPreferences && Object.keys(game.mealPreferences).length > 0 && (
                              <div className="mt-4 pt-3 border-t border-gray-200">
                                <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1">
                                  🍽️ Meal Preferences Summary
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                  {/* Veg */}
                                  <div>
                                    <p className="text-xs font-semibold text-green-700 mb-1">🥗 Veg ({Object.values(game.mealPreferences).filter(p => p.preference === 'veg').length})</p>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      <ul className="list-disc list-inside">
                                        {Object.entries(game.mealPreferences)
                                          .filter(([_, pref]) => pref.preference === 'veg')
                                          .map(([pId, pref]) => {
                                            const p = teamPlayers.find(tp => tp.id === pId || tp.oderId === pId || tp.userId === pId);
                                            return <li key={pId} className="truncate">{p?.name || pref.playerName || 'Unknown'}</li>;
                                          })}
                                      </ul>
                                    </div>
                                  </div>
                                  {/* Non-Veg */}
                                  <div>
                                    <p className="text-xs font-semibold text-orange-700 mb-1">🍗 Non-Veg ({Object.values(game.mealPreferences).filter(p => p.preference === 'non-veg').length})</p>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      <ul className="list-disc list-inside">
                                        {Object.entries(game.mealPreferences)
                                          .filter(([_, pref]) => pref.preference === 'non-veg')
                                          .map(([pId, pref]) => {
                                            const p = teamPlayers.find(tp => tp.id === pId || tp.oderId === pId || tp.userId === pId);
                                            return <li key={pId} className="truncate">{p?.name || pref.playerName || 'Unknown'}</li>;
                                          })}
                                      </ul>
                                    </div>
                                  </div>
                                  {/* Opt-Out */}
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">❌ Opt Out ({Object.values(game.mealPreferences).filter(p => p.preference === 'opt-out').length})</p>
                                    <div className="text-xs text-gray-600 max-h-24 overflow-y-auto pl-1">
                                      <ul className="list-disc list-inside">
                                        {Object.entries(game.mealPreferences)
                                          .filter(([_, pref]) => pref.preference === 'opt-out')
                                          .map(([pId, pref]) => {
                                            const p = teamPlayers.find(tp => tp.id === pId || tp.oderId === pId || tp.userId === pId);
                                            return <li key={pId} className="truncate">{p?.name || pref.playerName || 'Unknown'}</li>;
                                          })}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Non-Responders Alert */}
                        {stats.notResponded > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800">
                                  {stats.notResponded} player{stats.notResponded > 1 ? 's haven\'t' : ' hasn\'t'} responded
                                </p>
                                <p className="text-xs text-amber-700 mt-1">
                                  {stats.nonResponders.map(p => p.name.split(' ')[0]).join(', ')}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2 flex-wrap">
                          {stats.notResponded > 0 && (
                            <>
                              <button
                                onClick={() => handleSendAvailabilityReminder(game)}
                                disabled={sendingReminderForGame === game.id}
                                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                              >
                                <Bell size={14} />
                                {sendingReminderForGame === game.id ? 'Sending...' : 'Notify in App'}
                              </button>
                              <a
                                href={generateWhatsAppReminderLink(game, stats.nonResponders)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                              >
                                <Send size={14} />
                                WhatsApp Reminder
                              </a>
                            </>
                          )}
                          <a
                            href={generateWhatsAppTeamMessage(game)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                          >
                            <MessageCircle size={14} />
                            Share Status
                          </a>
                          <button
                            onClick={() => setEditingGame(game)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                          >
                            <Edit2 size={14} />
                            Edit Game
                          </button>
                          <button
                            onClick={() => handleSelectSquad(game.id)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                          >
                            <Users size={14} />
                            Select Squad
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Add Game Modal for Captains */}
        {showAddGame && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60] overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden max-h-[90vh] overflow-y-auto my-8 relative">
              <div className="p-4 bg-gray-50 border-b border-gray-100 sticky top-0 z-10 flex justify-between items-center">
                <h3 className="font-bold text-lg text-gray-800">Add New Game</h3>
                <button onClick={() => setShowAddGame(false)} className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 p-2 rounded-full">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6">
                <AddGame
                  teams={clubTeams?.map(t => ({ id: t.teamId, name: t.teamName })) || []}
                  tournaments={tournaments}
                  selectedClub={clubInfo}
                  onBack={() => setShowAddGame(false)}
                  onSave={handleCaptainCreateGame}
                />
              </div>
            </div>
          </div>
        )}

        {/* Edit Game Modal for Captains */}
        {editingGame && (
           <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60] overflow-y-auto">
             <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden max-h-[90vh] overflow-y-auto my-8 relative">
               <div className="p-4 bg-gray-50 border-b border-gray-100 sticky top-0 z-10 flex justify-between items-center">
                 <h3 className="font-bold text-lg text-gray-800">Edit Game</h3>
                 <button onClick={() => setEditingGame(null)} className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 p-2 rounded-full">
                   <X size={20} />
                 </button>
               </div>
               <div className="p-6">
                 <EditGame
                   game={editingGame}
                   teams={clubTeams?.map(t => ({ id: t.teamId, name: t.teamName })) || []}
                   tournaments={tournaments}
                   selectedClub={clubInfo}
                   onBack={() => setEditingGame(null)}
                   onSave={handleCaptainUpdateGame}
                   isAdmin={isUserAdmin}
                 />
               </div>
             </div>
           </div>
        )}
      </div>
    );
  };

  const renderFilterPills = () => (
    <>
                          {/* Filter Pills */}
                          <div className="mb-4">
                            <div className="flex gap-2.5 overflow-x-auto md:overflow-visible md:flex-wrap pb-2 scrollbar-none md:mx-0 px-1 md:px-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                              {(() => {
                                const filterOptions = [
                                  'All',
                                  'Action on me',
                                  'Team',
                                  'Tournament',
                                  'Practice',
                                  'Format',
                                  'Date Range'
                                ];
                                return filterOptions.map((filter) => {
                                  let isActive = false;
                                  if (filter === 'All') isActive = hasNoActiveFilters;
                                  else if (filter === 'Action on me') isActive = activeFilters.quicks.includes('Action on me');
                                  else if (filter === 'Practice') isActive = activeFilters.quicks.includes('Practice');
                                  else if (filter === 'Tournament') isActive = activeFilters.tournaments.length > 0;
                                  else if (filter === 'Team') isActive = activeFilters.teams.length > 0;
                                  else if (filter === 'Format') isActive = activeFilters.formats.length > 0;
                                  else if (filter === 'Date Range') isActive = (dateRangeFilter.start || dateRangeFilter.end);

                                  return (
                                    <div key={filter} className="relative flex-shrink-0">
                                      <button
                                        onClick={(e) => {
                                          if (filter === 'All') {
                                            clearAllFilters();
                                            setShowFormatDropdown(false);
                                            setShowDateRangeDropdown(false);
                                            setShowTournamentDropdown(false);
                                            setShowTeamDropdown(false);
                                          } else if (filter === 'Action on me') {
                                            toggleFilter('quicks', 'Action on me');
                                          } else if (filter === 'Practice') {
                                            toggleFilter('quicks', 'Practice');
                                          } else if (filter === 'Team') {
                                            setShowTeamDropdown(!showTeamDropdown);
                                            setShowTournamentDropdown(false);
                                            setShowFormatDropdown(false);
                                            setShowDateRangeDropdown(false);
                                          } else if (filter === 'Tournament') {
                                            setShowTournamentDropdown(!showTournamentDropdown);
                                            setShowTeamDropdown(false);
                                            setShowFormatDropdown(false);
                                            setShowDateRangeDropdown(false);
                                          } else if (filter === 'Format') {
                                            setShowFormatDropdown(!showFormatDropdown);
                                            setShowTeamDropdown(false);
                                            setShowTournamentDropdown(false);
                                            setShowDateRangeDropdown(false);
                                          } else if (filter === 'Date Range') {
                                            setShowDateRangeDropdown(!showDateRangeDropdown);
                                            setShowTeamDropdown(false);
                                            setShowFormatDropdown(false);
                                            setShowTournamentDropdown(false);
                                          }
                                        }}
                                        className={`whitespace-nowrap px-4 py-1.5 rounded-full text-[13px] font-bold border transition-colors flex items-center gap-1 ${(isActive && filter !== 'Tournament' && filter !== 'Team' && filter !== 'Format' && filter !== 'Date Range') ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 shadow-sm'}`}>
                                        {filter === 'Tournament' ? (
                                          <>
                                            {activeFilters.tournaments.length === 1 ? (tournaments.find(t => t.id === activeFilters.tournaments[0])?.name || 'Tournament') : activeFilters.tournaments.length > 1 ? `${activeFilters.tournaments.length} Selected` : 'Tournament'}
                                            <ChevronDown size={14} className="ml-0.5 opacity-70" />
                                          </>
                                        ) : filter === 'Team' ? (
                                          <>
                                            {activeFilters.teams.length === 1 ? (availableTeamsForFilter.find(t => t.teamName === activeFilters.teams[0])?.teamName || 'Team') : activeFilters.teams.length > 1 ? `${activeFilters.teams.length} Selected` : 'Team'}
                                            <ChevronDown size={14} className="ml-0.5 opacity-70" />
                                          </>
                                        ) : filter === 'Date Range' ? (
                                          <>
                                            {filter}
                                            <ChevronDown size={14} className="ml-0.5 opacity-70" />
                                          </>
                                        ) : filter === 'Format' ? (
                                          <>
                                            {activeFilters.formats.length === 1 ? (activeFilters.formats[0] === 'leather' ? 'Leather Ball' : 'Tennis Ball') : activeFilters.formats.length > 1 ? `${activeFilters.formats.length} Selected` : 'Format'}
                                            <ChevronDown size={14} className="ml-0.5 opacity-70" />
                                          </>
                                        ) : (
                                          filter
                                        )}
                                      </button>
                                      

                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          {/* Invisible Overlay to close dropdowns when clicking outside */}
                          {(showTeamDropdown || showTournamentDropdown || showFormatDropdown || showDateRangeDropdown) && (
                            <div 
                              className="fixed inset-0 z-30" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowTeamDropdown(false);
                                setShowTournamentDropdown(false);
                                setShowFormatDropdown(false);
                                setShowDateRangeDropdown(false);
                              }}
                            />
                          )}

                          {/* Dropdowns Rendered Outside Overflow */}
                          {showTeamDropdown && availableTeamsForFilter.length > 0 && (
                            <div className="mb-4 bg-white shadow-xl rounded-xl border border-slate-200 overflow-hidden relative z-40 animate-in fade-in slide-in-from-top-2">
                              <div className="flex flex-col max-h-64 overflow-y-auto divide-y divide-slate-100">
                                <button
                                  className={`flex items-center gap-3 px-4 py-3 text-sm font-semibold text-left transition-colors hover:bg-slate-50 ${activeFilters.teams.length === 0 ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilters(prev => ({ ...prev, teams: [] }));
                                  }}
                                >
                                  <input 
                                    type="checkbox" 
                                    checked={activeFilters.teams.length === 0}
                                    readOnly
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 pointer-events-none"
                                  />
                                  All Teams
                                </button>
                                {availableTeamsForFilter.map(t => (
                                  <button
                                    key={t.teamId}
                                    className={`flex items-center gap-3 px-4 py-3 text-sm font-semibold text-left transition-colors hover:bg-slate-50 ${activeFilters.teams.includes(t.teamName) ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFilter('teams', t.teamName);
                                    }}
                                  >
                                    <input 
                                      type="checkbox" 
                                      checked={activeFilters.teams.includes(t.teamName)}
                                      readOnly
                                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 pointer-events-none"
                                    />
                                    {t.teamName}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {showTournamentDropdown && tournaments.length > 0 && (
                            <div className="mb-4 bg-white shadow-xl rounded-xl border border-slate-200 overflow-hidden relative z-40 animate-in fade-in slide-in-from-top-2">
                              <div className="flex flex-col max-h-64 overflow-y-auto divide-y divide-slate-100">
                                <button
                                  className={`px-4 py-3 text-sm font-semibold text-left transition-colors ${activeFilters.tournaments.length === 0 ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700 hover:bg-slate-50'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilters(prev => ({ ...prev, tournaments: [] }));
                                  }}
                                >
                                  All Games
                                </button>
                                {tournaments.map(t => (
                                  <button
                                    key={t.id}
                                    className={`px-4 py-3 text-sm font-semibold text-left transition-colors ${activeFilters.tournaments.includes(t.id) ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700 hover:bg-slate-50'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFilter('tournaments', t.id);
                                    }}
                                  >
                                    {t.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {showFormatDropdown && (
                            <div className="mb-4 bg-white shadow-xl rounded-xl border border-slate-200 overflow-hidden relative z-40 animate-in fade-in slide-in-from-top-2">
                              <div className="flex divide-x divide-slate-100">
                                <button
                                  className={`flex-1 px-4 py-3 text-sm font-semibold hover:bg-slate-50 transition-colors ${activeFilters.formats.length === 0 ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilters(prev => ({ ...prev, formats: [] }));
                                  }}
                                >
                                  All Formats
                                </button>
                                <button
                                  className={`flex-1 px-4 py-3 text-sm font-semibold hover:bg-slate-50 transition-colors ${activeFilters.formats.includes('leather') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFilter('formats', 'leather');
                                  }}
                                >
                                  Leather
                                </button>
                                <button
                                  className={`flex-1 px-4 py-3 text-sm font-semibold hover:bg-slate-50 transition-colors ${activeFilters.formats.includes('tennis') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFilter('formats', 'tennis');
                                  }}
                                >
                                  Tennis
                                </button>
                              </div>
                            </div>
                          )}

                          {showDateRangeDropdown && (
                            <div className="mb-4 bg-white shadow-xl rounded-xl border border-slate-200 relative z-40 overflow-hidden animate-in fade-in slide-in-from-top-2">
                              <div className="p-4 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">From Date</label>
                                    <input 
                                      type="date"
                                      value={dateRangeFilter.start}
                                      onChange={(e) => {
                                        setDateRangeFilter(prev => ({ ...prev, start: e.target.value }));
                                      }}
                                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-medium text-slate-700"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">To Date</label>
                                    <input 
                                      type="date"
                                      value={dateRangeFilter.end}
                                      min={dateRangeFilter.start}
                                      onChange={(e) => {
                                        setDateRangeFilter(prev => ({ ...prev, end: e.target.value }));
                                      }}
                                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-medium text-slate-700"
                                    />
                                  </div>
                                </div>
                                <div className="pt-2 flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDateRangeFilter({ start: '', end: '' });
                                      setShowDateRangeDropdown(false);
                                    }}
                                    className="flex-1 px-3 py-2 rounded-lg text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  >
                                    Clear
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowDateRangeDropdown(false);
                                    }}
                                    disabled={!dateRangeFilter.start && !dateRangeFilter.end}
                                    className="flex-1 px-3 py-2 rounded-lg text-sm font-bold bg-slate-800 text-white hover:bg-black transition-colors disabled:opacity-50"
                                  >
                                    Apply Filter
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          </div>

                          {/* Events Toggle */}
                          <div className="mb-6 px-1">
                            <label className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-5 h-5 rounded-sm border-2 border-gray-800 text-slate-800 focus:ring-slate-800 cursor-pointer appearance-none checked:bg-slate-800 checked:border-slate-800 relative after:content-[''] after:absolute after:hidden checked:after:block after:left-[6px] after:top-[2px] after:w-[6px] after:h-[10px] after:border-solid after:border-white after:border-r-2 after:border-b-2 after:rotate-45"
                                checked={showAllEventsOnly}
                                onChange={(e) => setShowAllEventsOnly(e.target.checked)}
                              />
                              Show all club games & events
                            </label>
                          </div>

    </>
  );

  const renderCalendarContent = () => (
    <div className="p-6 pt-2 border-t md:p-4 md:border-t-0">
      {/* Navigation Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Today
          </button>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <h4 className="text-lg font-semibold text-gray-800">
          {formatDate(calendarDate, { month: 'long', year: 'numeric' })}
        </h4>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day Headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {getCalendarDays().map((dayInfo, index) => {
          const { day, date } = dayInfo;
          const { games, events: evts } = getEventsForDate(date);
          const hasGames = games.length > 0;
          const hasEvents = evts.length > 0;
          const isToday = date && date.toDateString() === new Date().toDateString();
          const isSelected = selectedCalendarEvent?.date?.toDateString() === date?.toDateString();
          const isPast = date && date < new Date().setHours(0, 0, 0, 0);

          return (
            <div
              key={index}
              onClick={() => handleDateClick(date)}
              className={`
      relative min-h-[100px] p-1.5 border rounded-lg transition-all overflow-hidden
      ${!day ? 'bg-gray-50' : 'cursor-pointer hover:shadow-md'}
      ${isToday ? 'border-blue-500 border-2 bg-blue-50/30' : 'border-gray-200'}
      ${isSelected ? 'bg-blue-50 ring-2 ring-blue-400' : ''}
      ${isPast && day ? 'opacity-50' : ''}
    `}
            >
              {day && (
                <>
                  <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {games.slice(0, 2).map(game => (
                      <div
                        key={game.id}
                        className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded truncate font-medium"
                        title={game.name}
                      >
                        🏏 {game.name.length > 12 ? game.name.substring(0, 12) + '...' : game.name}
                      </div>
                    ))}
                    {games.length > 2 && (
                      <div className="text-xs text-green-600 font-medium">+{games.length - 2} more</div>
                    )}
                    {evts.slice(0, 2 - Math.min(games.length, 2)).map(event => (
                      <div
                        key={event.id}
                        className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded truncate font-medium"
                        title={event.name}
                      >
                        {event.eventType === 'social' ? '🎉' : event.eventType === 'meeting' ? '📋' : event.eventType === 'training' ? '🏃' : event.eventType === 'fundraiser' ? '💰' : '📅'} {event.name.length > 10 ? event.name.substring(0, 10) + '...' : event.name}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-xs text-gray-600">Games</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
          <span className="text-xs text-gray-600">Events</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded border-2 border-blue-500"></div>
          <span className="text-xs text-gray-600">Today</span>
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedCalendarEvent && (
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-semibold text-gray-800 mb-4">
            {formatDate(selectedCalendarEvent.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </h4>

          {/* Games on selected date */}
          {selectedCalendarEvent.games.length > 0 && (
            <div className="space-y-3 mb-4">
              <h5 className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                Games ({selectedCalendarEvent.games.length})
              </h5>
              {selectedCalendarEvent.games.map(game => (
                <div key={game.id} className="mb-4">
                  {renderSingleGameCard(game)}
                </div>
              ))}
            </div>
          )}

          {/* Events on selected date */}
          {selectedCalendarEvent.events.length > 0 && (
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                Events ({selectedCalendarEvent.events.length})
              </h5>
              {selectedCalendarEvent.events.map(event => {
                const myRsvp = eventRsvps[event.id];
                const eventTypeStyles = {
                  social: { bg: 'bg-pink-50', border: 'border-pink-200', label: '🎉 Social' },
                  meeting: { bg: 'bg-blue-50', border: 'border-blue-200', label: '📋 Meeting' },
                  training: { bg: 'bg-green-50', border: 'border-green-200', label: '🏃 Training' },
                  fundraiser: { bg: 'bg-yellow-50', border: 'border-yellow-200', label: '💰 Fundraiser' },
                  other: { bg: 'bg-purple-50', border: 'border-purple-200', label: '📅 Event' }
                };
                const typeStyle = eventTypeStyles[event.eventType] || eventTypeStyles.other;

                return (
                  <div key={event.id} className={`p-4 ${typeStyle.bg} border ${typeStyle.border} rounded-lg`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-xs font-medium text-gray-600">{typeStyle.label}</span>
                        <h6 className="font-semibold text-gray-800 mt-1">{event.name}</h6>
                        {event.venue && <p className="text-sm text-gray-600 flex items-center gap-1 mt-1"><MapPin size={14} />{event.venue}</p>}
                        {event.time && <p className="text-sm text-gray-600 flex items-center gap-1"><Clock size={14} />{event.time}{event.endTime ? ` - ${event.endTime}` : ''}</p>}
                        {event.description && <p className="text-sm text-gray-600 mt-2">{event.description}</p>}
                      </div>
                      <div className="text-right">
                        {myRsvp ? (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${myRsvp.status === 'yes' ? 'bg-green-100 text-green-700' : myRsvp.status === 'no' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {myRsvp.status === 'yes' ? '✓ Going' : myRsvp.status === 'no' ? '✗ Not Going' : '? Maybe'}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">No RSVP</span>
                        )}
                      </div>
                    </div>
                    {!myRsvp && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-purple-200">
                        <button onClick={() => handleEventRsvp(event.id, 'yes', 0)} className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700">✓ Going</button>
                        <button onClick={() => handleEventRsvp(event.id, 'no')} className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700">✗ Can't Go</button>
                        <button onClick={() => handleEventRsvp(event.id, 'maybe')} className="flex-1 px-3 py-1.5 bg-yellow-500 text-white rounded text-sm font-medium hover:bg-yellow-600">? Maybe</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <WaiverGuard user={user} clubInfo={clubInfo}>
        <RegistrationGuard
          user={user}
        clubInfo={clubInfo}
        isAdmin={isUserAdmin}
        onDataReload={() => {
          // FIX: Reload expenses after registration to show Initial Donation immediately
          console.log('🔄 Reloading data after registration...');
          loadPlayerData();
          // Most importantly, reload expenses to pick up the new Initial Donation
          if (clubInfo?.id && playerData?.id) {
            loadExpenses(clubInfo.id, playerData.id, false, playerData.oderId || playerData.oderedPlayerId);
          }
          // Also reload wallet transactions for balance
          if (clubInfo?.id) {
            loadWalletTransactions(playerData?.id || user.uid, [playerData?.id], false, clubInfo.id);
          }
        }}
        onRegistrationComplete={(formData) => {
          // Update state to show dashboard after registration
          console.log('✅ Registration completed, refreshing data');
          setRegistrationFormCompleted(true);
          setShowRegistrationForm(false);

          // Reload all data to show the new Initial Donation
          loadPlayerData();

          // Force reload expenses after a short delay to ensure Firestore has processed the write
          setTimeout(() => {
            if (clubInfo?.id && playerData?.id) {
              console.log('🔄 Delayed reload of expenses and wallet transactions...');
              loadExpenses(clubInfo.id, playerData.id, false, playerData.oderId || playerData.oderedPlayerId);
              loadWalletTransactions(playerData?.id || user.uid, [playerData?.id], false, clubInfo.id);
            }
          }, 1000);
        }}
      >
        <PullToRefresh
          onRefresh={handleRefresh}
          pullingContent={
            <div className="flex justify-center items-center py-4 bg-gradient-to-br from-green-50 via-white to-blue-50">
              <span className="text-gray-500">↓ Pull to refresh</span>
            </div>
          }
          refreshingContent={
            <div className="flex justify-center items-center py-4 bg-gradient-to-br from-green-50 via-white to-blue-50">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              <span className="ml-2 text-green-600">Refreshing...</span>
            </div>
          }
          resistance={2.5}
          maxPullDownDistance={120}
          pullDownThreshold={80}
        >
          <div className="min-h-screen bg-gradient-to-b from-[#FAE8E0] via-[#F1E6F3] to-[#E2E8F0] relative">
            {/* Club Logo Watermark Background */}
            {(clubInfo?.logoUrl || clubInfo?.logo) && (
              <div
                className="fixed inset-0 pointer-events-none z-0"
                style={{
                  backgroundImage: `url(${clubInfo.logoUrl || clubInfo.logo})`,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '70%',
                  opacity: 0.08
                }}
              />
            )}
            <div className="relative z-10">
              {/* PWA Install Prompt */}
              {showPWAPrompt && (
                <PWAInstallPrompt
                  userName={playerData?.name || user?.displayName}
                  onDismiss={() => setShowPWAPrompt(false)}
                />
              )}

              {!hideHeader && (
                <>
                  {/* Mobile Header (replaces standard header on small screens) */}
                  <div className="md:hidden flex items-center justify-between px-5 py-4 pt-5">
                    <div className="flex items-center gap-3">
                      {/* Profile dropdown trigger */}
                      <div className="relative">
                        <button
                          onClick={() => setShowMobileMenu(!showMobileMenu)}
                          className="w-10 h-10 bg-white shadow-sm rounded-full flex items-center justify-center border border-slate-200 overflow-hidden shrink-0 hover:bg-slate-50 transition-colors"
                        >
                           {playerData?.profilePicture || user?.profilePicture ? (
                             <img src={playerData?.profilePicture || user?.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                           ) : (
                             <div className="w-full h-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                               <span className="text-lg font-bold text-white">{(playerData?.name || user?.displayName || user?.email?.split('@')[0] || 'U')[0].toUpperCase()}</span>
                             </div>
                           )}
                        </button>

                        {/* Mobile Dropdown Menu */}
                        {showMobileMenu && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setShowMobileMenu(false)}
                            ></div>
                            <div className="absolute left-0 top-12 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
                              <div className="py-1">
                                {/* Profile */}
                                <button
                                  onClick={() => {
                                    setShowProfile(true);
                                    setShowMobileMenu(false);
                                  }}
                                  className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3 font-medium"
                                >
                                  <User size={16} className="text-slate-500" />
                                  View Profile
                                </button>

                                {/* Admin/Treasurer View */}
                                {(() => {
                                  if (!canSwitchToAdmin) return null;
                                  
                                  const btnLabel = isUserAdmin ? 'Admin View' : 'Treasurer View';

                                  return (
                                    <button
                                      onClick={() => {
                                        window.location.href = '/admin'; // Force reload to ensure clean handoff to AdminDashboard
                                        setShowMobileMenu(false);
                                      }}
                                      className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                                    >
                                      <Shield size={16} className="text-purple-500" />
                                      {btnLabel}
                                    </button>
                                  );
                                })()}

                                {/* Switch Club */}
                                {(() => {
                                  const actualClubIds = Array.from(new Set([
                                    ...(user?.joinedClubsIds || []),
                                    ...(user?.joinedClubs ? Object.keys(user.joinedClubs) : []),
                                    user?.clubId,
                                    user?.clubInfo?.clubId
                                  ].filter(Boolean)));

                                  if (actualClubIds.length <= 1) return null;

                                  return (
                                    <button
                                      onClick={() => {
                                        setShowMobileSwitchClub(true);
                                        setShowMobileMenu(false);
                                      }}
                                      className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                                    >
                                      <Building2 size={16} className="text-blue-500" />
                                      Switch Club
                                    </button>
                                  );
                                })()}

                                {/* Join New Club */}
                                <button
                                  onClick={() => {
                                    setShowMobileJoinClub(true);
                                    setShowMobileMenu(false);
                                  }}
                                  className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                                >
                                  <Trophy size={16} className="text-green-500" />
                                  Join New Club
                                </button>

                                {/* Logout */}
                                <button
                                  onClick={() => {
                                    setShowMobileMenu(false);
                                    onLogout();
                                  }}
                                  className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                                >
                                  <LogOut size={16} className="text-red-500" />
                                  Logout
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="flex flex-col text-left">
                        <h1 className="text-xl font-bold leading-none tracking-tight">
                          <span className="text-green-600">Club</span>
                          <span className="text-blue-600">Hudl</span>
                        </h1>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-slate-700">
                      <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
                        title="Notifications"
                      >
                        <svg className="w-6 h-6 text-slate-500 hover:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        {notifications && notifications.length > 0 && (
                          <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                            {notifications.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setMobileViewMode('list');
                        }}
                        className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${mobileViewMode === 'list' ? 'bg-slate-200 text-slate-900' : 'hover:bg-black/5'}`}
                      >
                        <LayoutGrid strokeWidth={2.5} size={24} className={mobileViewMode === 'list' ? 'text-slate-900' : 'text-slate-500'} />
                      </button>
                      <button
                        onClick={() => {
                          setMobileViewMode('calendar');
                        }}
                        className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${mobileViewMode === 'calendar' ? 'bg-slate-200 text-slate-900' : 'hover:bg-black/5'}`}
                      >
                        <CalendarDays strokeWidth={2.5} size={24} className={mobileViewMode === 'calendar' ? 'text-slate-900' : 'text-slate-500'} />
                      </button>
                    </div>
                  </div>

                  {/* Desktop Header */}
                  <div className="hidden md:block">
                    <Header
                      user={user}
                      role="player"
                      onLogout={onLogout}
                      clubName={clubInfo?.name}
                      clubLogo={clubInfo?.logoUrl || clubInfo?.logo}
                      teamName={playerData?.teamName}
                      playerData={playerData}
                      notifications={notifications}
                      onNotificationClick={() => setShowNotifications(!showNotifications)}
                      isUserAdmin={isUserAdmin}
                      canSwitchToAdmin={canSwitchToAdmin}
                    />
                  </div>

                  {/* Mobile Modals */}
                  {showMobileSwitchClub && (
                    <SwitchClubModal
                      user={user}
                      onClose={() => setShowMobileSwitchClub(false)}
                    />
                  )}

                  {showMobileJoinClub && (
                    <JoinAnotherClubModal
                      user={user}
                      onClose={() => setShowMobileJoinClub(false)}
                    />
                  )}

                  {/* Mobile Profile Modal */}
      {showProfile && (
        <ProfileModal
          user={user}
          profilePicture={playerData?.profilePicture || user?.profilePicture}
          userName={playerData?.name || user?.displayName || user?.email?.split('@')[0] || 'User'}
          userInitial={(playerData?.name || user?.displayName || user?.email?.split('@')[0] || 'U')[0].toUpperCase()}
          roleBadge={{
            bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
            text: 'text-white',
            label: 'Player'
          }}
          RoleIcon={User}
          clubName={clubInfo?.name}
          clubLogo={clubInfo?.logoUrl || clubInfo?.logo}
          teamName={playerData?.teamName}
          playerData={playerData}
          onClose={() => setShowProfile(false)}
        />
      )}

      {/* Legacy modals (Match recap, registration forms, etc.) */}

      {/* Mobile Switch Club Modal */}
      {showMobileSwitchClub && (
        <SwitchClubModal
          user={user}
          onClose={() => setShowMobileSwitchClub(false)}
        />
      )}

      {/* Mobile Join Club Modal */}
      {showMobileJoinClub && (
        <JoinAnotherClubModal
          user={user}
          onClose={() => setShowMobileJoinClub(false)}
        />
      )}
                </>
              )}

              {/* Payment QR Code Modal */}
              {showPaymentQR && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">How to Pay</h2>
                        <p className="text-gray-600 mt-1">Scan any QR code below to make a payment</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPaymentQR(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X size={24} />
                      </button>
                    </div>

                    <div className="p-6">
                      {/* Payment Amount Highlight */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-8 text-center">
                        <p className="text-gray-700 font-medium mb-2">Amount to Pay</p>
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-3xl font-bold text-blue-600">$</span>
                          <input
                            type="number"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            placeholder="0.00"
                            className="text-5xl font-bold text-blue-600 bg-transparent border-b-2 border-blue-200 focus:border-blue-600 focus:outline-none w-48 text-center"
                          />
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {clubInfo?.name || 'Seattle United Yorkas Club'}
                        </p>
                      </div>

                      {/* QR Codes Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {/* Zelle */}
                        {clubInfo?.paymentDetails?.zelleEmail && (
                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow flex flex-col h-full">
                            <div className="flex flex-col items-center flex-1">
                              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-2xl font-bold text-purple-600">Z</span>
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Zelle</h3>
                              <p className="text-sm text-gray-600 mb-4 text-center">
                                Scan with your banking app
                              </p>
                              {clubInfo.paymentDetails.zelleQrUrl ? (
                                <div className="bg-white p-3 rounded-lg border-2 border-gray-200 mb-4 w-48 h-48 flex items-center justify-center flex-shrink-0">
                                  <img
                                    src={clubInfo.paymentDetails.zelleQrUrl}
                                    alt="Zelle QR Code"
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div className="hidden w-full h-full flex-col items-center justify-center">
                                    <div className="text-6xl mb-2">📱</div>
                                    <p className="text-sm text-gray-600 text-center px-2">{clubInfo.paymentDetails.zelleEmail}</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4 flex-1 flex flex-col items-center justify-center text-center">
                                  <div className="text-4xl mb-2">📱</div>
                                  <p className="text-sm text-gray-500">Send money directly to:</p>
                                  <p className="font-mono text-purple-700 font-bold break-all mt-1">{clubInfo.paymentDetails.zelleEmail}</p>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 font-mono text-center break-all mt-auto">{clubInfo.paymentDetails.zelleEmail}</p>
                            </div>
                          </div>
                        )}

                        {/* PayPal */}
                        {clubInfo?.paymentDetails?.paypalLink && (
                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow flex flex-col h-full">
                            <div className="flex flex-col items-center flex-1">
                              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-2xl font-bold text-blue-600">P</span>
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">PayPal</h3>
                              <p className="text-sm text-gray-600 mb-4 text-center">
                                Secure online payment
                              </p>
                              {clubInfo.paymentDetails.paypalQrUrl ? (
                                <div className="bg-white p-3 rounded-lg border-2 border-gray-200 mb-4 w-48 h-48 flex items-center justify-center flex-shrink-0">
                                  <img
                                    src={clubInfo.paymentDetails.paypalQrUrl}
                                    alt="PayPal QR Code"
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div className="hidden w-full h-full flex-col items-center justify-center">
                                    <div className="text-6xl mb-2">💙</div>
                                    <p className="text-sm text-gray-600">Scan with PayPal</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4 flex-1 flex flex-col items-center justify-center text-center">
                                  <div className="text-4xl mb-2">💙</div>
                                  <p className="text-sm text-gray-600">Open link below</p>
                                </div>
                              )}
                              <a
                                href={clubInfo.paymentDetails.paypalLink.startsWith('http') ? clubInfo.paymentDetails.paypalLink : `https://${clubInfo.paymentDetails.paypalLink}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 underline text-center mt-auto"
                              >
                                {clubInfo?.name || 'PayPal Link'}
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Venmo */}
                        {clubInfo?.paymentDetails?.venmoLink && (
                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow flex flex-col h-full">
                            <div className="flex flex-col items-center flex-1">
                              <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-2xl font-bold text-sky-600">V</span>
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Venmo</h3>
                              <p className="text-sm text-gray-600 mb-4 text-center">
                                Quick mobile payment
                              </p>
                              {clubInfo.paymentDetails.venmoQrUrl ? (
                                <div className="bg-white p-3 rounded-lg border-2 border-gray-200 mb-4 w-48 h-48 flex items-center justify-center flex-shrink-0">
                                  <img
                                    src={clubInfo.paymentDetails.venmoQrUrl}
                                    alt="Venmo QR Code"
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div className="hidden w-full h-full flex-col items-center justify-center">
                                    <div className="text-6xl mb-2">💰</div>
                                    <p className="text-sm text-gray-600">Scan with Venmo</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-sky-50 border border-sky-200 rounded-lg p-6 mb-4 flex-1 flex flex-col items-center justify-center text-center">
                                  <div className="text-4xl mb-2">💰</div>
                                  <p className="text-sm text-gray-500">Send money to:</p>
                                  <p className="font-mono text-sky-700 font-bold break-all mt-1">{clubInfo.paymentDetails.venmoLink}</p>
                                </div>
                              )}
                              <a
                                href={clubInfo.paymentDetails.venmoLink.startsWith('http') ? clubInfo.paymentDetails.venmoLink : `https://venmo.com/${clubInfo.paymentDetails.venmoLink.replace('@', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-sky-600 hover:text-sky-800 underline font-mono text-center mt-auto truncate w-full"
                              >
                                {clubInfo.paymentDetails.venmoLink.startsWith('@') ? clubInfo.paymentDetails.venmoLink : `@${clubInfo.paymentDetails.venmoLink.split('/').pop()}`}
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Benevity (Hidden for now) */}
                        {/* clubInfo?.paymentDetails?.benevitySearch && (
                          <div className="bg-white border-2 border-orange-200 rounded-xl p-6 hover:shadow-lg transition-shadow flex flex-col h-full">
                            <div className="flex flex-col items-center flex-1">
                              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-2xl font-bold text-orange-600">B</span>
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Benevity</h3>
                              <p className="text-sm text-gray-600 mb-4 text-center">
                                Employer donation portal
                              </p>
                              <div className="bg-orange-50 p-3 rounded-lg border-2 border-orange-200 mb-4 w-48 flex-1 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="text-5xl mb-3">💼</div>
                                  <p className="text-sm font-semibold text-orange-800 mb-2">Use Benevity Portal</p>
                                  <p className="text-xs text-gray-600 px-2 line-clamp-3">
                                    Search: <span className="font-bold">{clubInfo.paymentDetails.benevitySearch}</span>
                                  </p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 text-center mt-auto">Via employer matching</p>
                            </div>
                          </div>
                        ) */}

                        {/* Fallback Display When Blank */}
                        {!clubInfo?.paymentDetails?.zelleEmail && !clubInfo?.paymentDetails?.venmoLink && !clubInfo?.paymentDetails?.paypalLink && (
                          <div className="col-span-full bg-yellow-50 border-2 border-yellow-200 rounded-xl p-8 flex flex-col items-center text-center">
                            <div className="bg-yellow-100 text-yellow-600 p-4 rounded-full mb-4">
                              <AlertCircle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-yellow-900 mb-2">No Payment Methods Configured</h3>
                            <p className="text-yellow-800">
                              Your club administrator has not set up any specific payment methods or QR codes yet. Please contact them for payment instructions!
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Instructions */}
                      <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="font-bold text-gray-900 mb-3">📱 How to Pay:</h3>

                        {/* Zelle, PayPal, Venmo Instructions */}
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-gray-700 mb-2">For Zelle, PayPal, or Venmo:</p>
                          <ol className="space-y-2 text-sm text-gray-700 pl-4">
                            <li className="flex gap-2">
                              <span className="font-bold">1.</span>
                              <span>Open your payment app depending on the options listed above.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold">2.</span>
                              <span>Use the direct link, or scan the QR code with your phone camera or app.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold">3.</span>
                              <span>Enter the amount</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold">4.</span>
                              <span>Add a note - Please include what you are paying for: "Eg: {playerData?.name} - {getRegistrationFeeLabel(clubInfo)}"</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold">5.</span>
                              <span>Complete the payment</span>
                            </li>
                          </ol>
                        </div>

                        {/* Benevity Instructions (Hidden for now) */}
                        {/* clubInfo?.paymentDetails?.benevitySearch && (
                          <div className="border-t pt-4">
                            <p className="text-sm font-semibold text-orange-700 mb-2">For Benevity:</p>
                            <ol className="space-y-2 text-sm text-gray-700 pl-4">
                              <li className="flex gap-2">
                                <span className="font-bold">1.</span>
                                <span>Access your employer's Benevity portal using your work login</span>
                              </li>
                              <li className="flex gap-2">
                                <span className="font-bold">2.</span>
                                <span>Search for: <span className="font-semibold">{clubInfo.paymentDetails.benevitySearch}</span></span>
                              </li>
                              <li className="flex gap-2">
                                <span className="font-bold">3.</span>
                                <span>Make your donation through the Benevity system</span>
                              </li>
                              <li className="flex gap-2">
                                <span className="font-bold">4.</span>
                                <span>Screenshot the final confirmation page</span>
                              </li>
                              <li className="flex gap-2">
                                <span className="font-bold">5.</span>
                                <span>Click "Record a Payment" below and attach the screenshot</span>
                              </li>
                            </ol>
                          </div>
                        ) */}

                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <span className="font-semibold">💡 Tip:</span> After paying, record your payment below so admin can verify and update your balance.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 border-t border-gray-200 p-6 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowPaymentQR(false)}
                        className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => {
                          setShowPaymentQR(false);
                          setShowRecordPayment(true);
                        }}
                        className="flex-1 py-3 rounded-lg font-semibold bg-green-600 text-white hover:bg-green-700"
                      >
                        Record a Payment
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Record Payment Modal */}
              {showRecordPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {netBalance <= 0.01 ? 'Record Donation' : 'Record Payment'}
                        </h2>
                        <p className="text-gray-600 mt-1">
                          {netBalance <= 0.01 ? 'Submit a voluntary contribution' : 'Submit payment for admin approval'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowRecordPayment(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X size={24} />
                      </button>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Amount */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {netBalance <= 0.01 ? 'Donation Amount *' : 'Payment Amount *'}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="250.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {netBalance <= 0.01
                            ? 'Your current balance is settled ($0.00). Thank you for your support!'
                            : `Your current balance: ${formatCurrency(balance)}`
                          }
                        </p>
                      </div>

                      {/* Tournament (optional) */}
                      {tournaments && tournaments.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tournament (if paying tournament fees)
                          </label>
                          <select
                            value={paymentTournamentId}
                            onChange={(e) => setPaymentTournamentId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                          >
                            <option value="">Not tournament-specific (e.g. Annual Membership)</option>
                            {tournaments.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Select the tournament if this payment is for tournament game fees.
                          </p>
                        </div>
                      )}

                      {/* Payment Method */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Method *
                        </label>
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                          autoComplete="off"
                        >
                          <option value="">Select method</option>
                          <option value="Zelle">Zelle</option>
                          <option value="PayPal">PayPal</option>
                          <option value="Venmo">Venmo</option>
                          <option value="Benevity">Benevity</option>
                        </select>
                      </div>

                      {/* Payment Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Transaction ID */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transaction ID / Reference (Optional)
                        </label>
                        <input
                          type="text"
                          value={paymentTransactionId}
                          onChange={(e) => setPaymentTransactionId(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Zelle confirmation number"
                        />
                      </div>

                      {/* Receipt Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Receipt / Screenshot {paymentMethod === 'Benevity' ? '*' : '(Optional)'}
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => setPaymentReceipt(e.target.files[0])}
                            className="hidden"
                            id="payment-receipt-upload"
                          />
                          <label htmlFor="payment-receipt-upload" className="cursor-pointer">
                            {paymentReceipt ? (
                              <div className="text-green-600">
                                <CheckCircle className="mx-auto mb-2" size={32} />
                                <p className="font-medium">{paymentReceipt.name}</p>
                                <p className="text-xs text-gray-500 mt-1">Click to change</p>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                <svg className="mx-auto mb-2 w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="font-medium">Click to upload receipt</p>
                                <p className="text-xs mt-1">Screenshot or photo of payment confirmation</p>
                              </div>
                            )}
                          </label>
                        </div>
                        {paymentMethod === 'Benevity' && (
                          <p className="text-xs text-orange-600 mt-1 font-semibold">
                            ⚠️ Receipt is required for Benevity donations
                          </p>
                        )}
                        {paymentMethod && paymentMethod !== 'Benevity' && (
                          <p className="text-xs text-gray-500 mt-1">
                            📄 Upload a screenshot from Zelle/PayPal/Venmo showing payment details
                          </p>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes *
                        </label>
                        <textarea
                          value={paymentNotes}
                          onChange={(e) => setPaymentNotes(e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={`Example: ${playerData?.name} - ${clubInfo?.name || 'Seattle United Yorkas Club'} - ${getRegistrationFeeLabel(clubInfo)} for Spring 2026`}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Include your name, club name, and what you're paying for (e.g., registration, game fees, etc.)
                        </p>
                      </div>

                      {/* Info Box */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <span className="font-semibold">ℹ️ What happens next:</span>
                          <br />
                          Your payment will be submitted for admin review. Once approved, your balance will be updated automatically.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 border-t border-gray-200 p-6 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowRecordPayment(false)}
                        disabled={uploadingPayment}
                        className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitPayment}
                        disabled={uploadingPayment}
                        className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {uploadingPayment ? (
                          <>
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          'Submit Payment'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {view === 'mobile-dues' && (
                <MobileDuesView
                  playerData={playerData}
                  expenses={expenses}
                  draftExpenses={draftExpenses}
                  walletTransactions={walletTransactions}
                  netBalance={netBalance}
                  amountPaid={amountPaid}
                  reimbursementsOwed={reimbursementsOwed}
                  getBalanceDueBreakdown={getBalanceDueBreakdown}
                  setShowPaymentQR={setShowPaymentQR}
                  setPaymentAmount={setPaymentAmount}
                  setView={setView}
                  onEditDraft={handleEditDraft}
                  pendingExpenses={getPendingExpenses()}
                  paymentSubmissions={paymentSubmissions}
                  setShowPendingExpensesModal={setShowPendingExpensesModal}
                />
              )}

              {view === 'captains-corner' && (
                <div className="max-w-7xl mx-auto px-4 py-8 md:hidden">
                  {renderCaptainsCorner()}
                </div>
              )}

              {view === 'dashboard' && (
                <div className="max-w-7xl mx-auto px-4 pt-2 pb-8">
                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="mb-6 bg-white rounded-xl shadow-lg p-4 max-w-md ml-auto">
                      <h3 className="font-bold mb-3 flex justify-between items-center">
                        <span>Notifications</span>
                        <div className="flex items-center gap-3">
                          {notifications.length > 0 && (
                            <button
                              onClick={handleDismissAllNotifications}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline"
                            >
                              Dismiss All
                            </button>
                          )}
                          <button onClick={() => setShowNotifications(false)} className="text-gray-500 hover:bg-gray-100 rounded-full p-1">
                            <X size={16} />
                          </button>
                        </div>
                      </h3>
                      <div className="space-y-4">
                        {/* Push Notification Toggle */}
                        {/* Push Notification Status */}
                        {!playerData?.fcmToken ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <div className="flex items-start gap-3">
                              <div className="bg-blue-100 p-2 rounded-full">
                                <span className="text-xl">🔔</span>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-blue-800 text-sm">Enable Push Notifications</h4>
                                <p className="text-xs text-blue-600 mb-2">
                                  Get instant updates for games, squad selections, and expenses.
                                </p>
                                <button
                                  onClick={handleEnableNotifications}
                                  className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                  Turn On Notifications
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 flex items-center gap-3">
                            <div className="bg-green-100 p-1.5 rounded-full">
                              <span className="text-sm">✅</span>
                            </div>
                            <div>
                              <h4 className="font-bold text-green-800 text-sm">Notifications Active</h4>
                              <p className="text-xs text-green-600">You are receiving push notifications.</p>
                            </div>
                          </div>
                        )}

                        {notifications.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <p>No new notifications</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {notifications.map(notification => {
                              const isDenied = notification.type === 'expense_denied';
                              const isGameReminder = notification.type === 'game_reminder';
                              const isAvailabilityReminder = notification.type === 'availability_reminder';
                              const isSquadSelected = notification.type === 'squad_selected';
                              const isDutyAssigned = notification.type === 'duty_assigned';
                              const isFeedbackResolved = notification.type === 'feedback_resolved';

                              // Determine styling based on notification type
                              const getNotificationStyle = () => {
                                if (isDenied) return 'bg-red-50 border-red-200';
                                if (isGameReminder) return 'bg-blue-50 border-blue-200';
                                if (isAvailabilityReminder) return 'bg-amber-50 border-amber-200';
                                if (isSquadSelected) return 'bg-purple-50 border-purple-200';
                                if (isDutyAssigned) return 'bg-orange-50 border-orange-200';
                                if (isFeedbackResolved) return 'bg-teal-50 border-teal-200';
                                return 'bg-green-50 border-green-200';
                              };

                              const getTextColor = () => {
                                if (isDenied) return 'text-red-800';
                                if (isGameReminder) return 'text-blue-800';
                                if (isAvailabilityReminder) return 'text-amber-800';
                                if (isSquadSelected) return 'text-purple-800';
                                if (isDutyAssigned) return 'text-orange-800';
                                if (isFeedbackResolved) return 'text-teal-800';
                                return 'text-green-800';
                              };

                              const getIcon = () => {
                                if (isDenied) return '❌';
                                if (isGameReminder) return '🏏';
                                if (isAvailabilityReminder) return '⏰';
                                if (isSquadSelected) return '✅';
                                if (isDutyAssigned) return '📋';
                                if (isFeedbackResolved) return '✨';
                                return '📢';
                              };

                              // Handle clicking on notification to navigate to game
                              const handleNotificationClick = async () => {
                                // First, mark as read and delete
                                await markNotificationAsRead(notification.id);

                                // Close notifications dropdown
                                setShowNotifications(false);

                                // Handle navigation based on notification type
                                if (notification.type === 'expense_added' || notification.expenseId) {
                                  // Navigate to expenses view
                                  setView('my-expenses');

                                  // Show amount owed if available
                                  if (notification.amountOwed) {
                                    setTimeout(() => {
                                      alert(`You owe $${notification.amountOwed.toFixed(2)} for "${notification.expenseDescription || 'Game Expense'}"`);
                                    }, 500);
                                  }
                                } else if (notification.type === 'payment_reminder') {
                                  // Navigate to payments view
                                  setView('payments');
                                } else if (notification.gameId) {
                                  // Navigate to game - existing logic
                                  setTimeout(() => {
                                    const gameElement = document.getElementById(`game-${notification.gameId}`);
                                    if (gameElement) {
                                      gameElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      // Highlight the game briefly
                                      gameElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
                                      setTimeout(() => {
                                        gameElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
                                      }, 3000);
                                    }
                                  }, 100);
                                }
                              };

                              return (
                                <div
                                  key={notification.id}
                                  className={`p-3 border rounded-lg ${getNotificationStyle()} cursor-pointer hover:shadow-md transition-shadow`}
                                  onClick={handleNotificationClick}
                                >
                                  <div className="flex items-start gap-2">
                                    <span className="text-lg">{getIcon()}</span>
                                    <div className="flex-1">
                                      <p className={`font-semibold ${getTextColor()}`}>
                                        {notification.message}
                                      </p>
                                      {notification.gameName && (
                                        <p className="text-sm text-gray-700 mt-1 font-medium">
                                          {notification.gameName}
                                        </p>
                                      )}
                                      {notification.gameDate && (
                                        <p className="text-sm text-gray-600 mt-1">
                                          📅 {notification.gameDate}
                                        </p>
                                      )}
                                      {notification.gameVenue && (
                                        <p className="text-sm text-gray-600">
                                          📍 {notification.gameVenue}
                                        </p>
                                      )}
                                      {/* Super Sub Badge */}
                                      {notification.isSuperSub && (
                                        <p className="text-sm text-purple-700 mt-2 font-medium">
                                          ⭐ Super Sub (50% cost)
                                        </p>
                                      )}
                                      {/* Assigned Duty */}
                                      {notification.assignedDuty && (
                                        <p className="text-sm text-orange-700 mt-1 font-medium bg-orange-100 inline-block px-2 py-0.5 rounded">
                                          Your duty: {notification.assignedDuty}
                                        </p>
                                      )}
                                      {/* Expense Amount Owed */}
                                      {notification.amountOwed && (
                                        <p className="text-sm text-red-700 mt-2 font-bold bg-red-50 inline-block px-2 py-1 rounded">
                                          💰 You owe: ${notification.amountOwed.toFixed(2)}
                                          {notification.isSuperSub && ' (Super Sub - 50% discount)'}
                                        </p>
                                      )}
                                      {notification.reason && (
                                        <p className="text-sm text-red-700 mt-2 font-medium">
                                          Reason: {notification.reason}
                                        </p>
                                      )}
                                      {notification.gameId && (
                                        <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                                          <Eye size={12} />
                                          Click to view game
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-end mt-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        markNotificationAsRead(notification.id);
                                      }}
                                      className={`text-xs hover:underline ${isDenied ? 'text-red-600' :
                                        isGameReminder ? 'text-blue-600' :
                                          isAvailabilityReminder ? 'text-amber-600' :
                                            isSquadSelected ? 'text-purple-600' :
                                              'text-green-600'
                                        }`}
                                    >
                                      Dismiss
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}




                  {/* Stats Cards - Updated Grid Container for Smaller Balance Card */}
                  <div className="max-w-4xl mb-8">
                    <div className="hidden md:flex gap-3 items-stretch">
                      {/* Quick Actions Tile - Contains Pay Now, File Expense and My Expenses buttons */}
                      <div className="bg-white rounded-xl shadow-sm p-3 w-80">
                        <div className="flex flex-col gap-2">
                          {/* Pay Now button - Full Width */}
                          {!isClubArchivedForUser && (
                            <button
                              onClick={() => {
                                setPaymentAmount(netBalance > 0 ? netBalance.toString() : '');
                                setShowPaymentQR(true);
                              }}
                              className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              <span className="font-semibold text-lg">Pay Now</span>
                            </button>
                          )}

                          <div className="grid grid-cols-2 gap-2">
                            {/* File Expense Button */}
                            {!isClubArchivedForUser && (
                              <button
                                onClick={() => setView('add-expense')}
                                className="flex flex-col items-center justify-center gap-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all shadow-sm"
                              >
                                <div className="bg-white/20 p-1.5 rounded-lg mb-1">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <span className="text-sm font-medium">File Expense</span>
                              </button>
                            )}

                            {/* My Expenses Button */}
                            <button
                              onClick={() => setView('my-expenses')}
                              className="flex flex-col items-center justify-center gap-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all shadow-sm"
                            >
                              <div className="bg-white/20 p-1.5 rounded-lg mb-1">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h2m0 0h2m-2 0v2m0-2V10M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <span className="text-sm font-medium">My Expenses</span>
                            </button>
                          </div>
                        </div>

                        {/* Show placeholder when no balance due */}


                      </div>

                      {/* Balance Due Tile - Smaller and more compact */}
                      <div
                        className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-lg transition-all w-80 ${netBalance > 0.01
                          ? 'ring-2 ring-red-500'      // Player owes club
                          : netBalance < -0.01
                            ? 'ring-2 ring-blue-500'   // Club owes player
                            : 'ring-2 ring-green-500'  // All settled
                          }`}
                        onClick={() => setShowBalanceDueModal(true)}
                      >
                        <div className="h-full flex flex-col justify-center text-center">
                          <p className="text-sm text-gray-600 mb-2">
                            {netBalance > 0.01 ? 'Your Dues to Club' : netBalance < -0.01 ? 'Club Owes You' : 'All Settled'}
                          </p>
                          <p className="text-2xl font-bold mb-2" style={{ color: netBalance > 0.01 ? '#dc2626' : netBalance < -0.01 ? '#2563eb' : '#16a34a' }}>
                            ${Math.abs(netBalance).toFixed(2)}
                          </p>

                          {/* Show pending payments if exists */}
                          {paymentSubmissions.filter(p => p.status === 'pending').length > 0 && (() => {
                            const pendingAmount = paymentSubmissions
                              .filter(p => p.status === 'pending')
                              .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

                            return (
                              <p className="text-xs text-gray-500 italic">
                                Payment pending approval ${pendingAmount.toFixed(2)}
                              </p>
                            );
                          })()}

                          {/* Show pending expenses count and amount */}
                          {getPendingExpenses().length > 0 && (() => {
                            const pendingExpenses = getPendingExpenses();
                            const pendingExpenseTotal = pendingExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

                            return (
                              <p
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowPendingExpensesModal(true);
                                }}
                                className="text-xs text-yellow-600 mt-1 hover:text-yellow-700 cursor-pointer"
                              >
                                {pendingExpenses.length} expense{pendingExpenses.length !== 1 ? 's' : ''} awaiting approval (${pendingExpenseTotal.toFixed(2)})
                              </p>
                            );
                          })()}
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Luma Event Banner Card - Second Row */}
                  {clubInfo.lumaBannerEnabled && clubInfo.lumaEventUrl && (
                    <div className="max-w-4xl mb-8">
                      <LumaBanner
                        eventUrl={clubInfo.lumaEventUrl}
                        title={clubInfo.lumaTitle}
                        badge={clubInfo.lumaBadge}
                        description={clubInfo.lumaDescription}
                        eventDate={clubInfo.lumaEventDate}
                      />
                    </div>
                  )}

                  {/* Umpiring Assignment Banner */}
                  {upcomingUmpiringAssignments.length > 0 && (() => {
                    // Group by tournament
                    const byTournament = upcomingUmpiringAssignments.reduce((acc, a) => {
                      const key = a.tournamentName || 'General';
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(a);
                      return acc;
                    }, {});
                    const tournamentGroups = Object.entries(byTournament);
                    return (
                      <div className="max-w-4xl mb-6">
                        {/* Collapsible header */}
                        <button
                          onClick={() => {
                            const next = !umpiringExpanded;
                            setUmpiringExpanded(next);
                            localStorage.setItem('umpiringExpanded', String(next));
                          }}
                          className="w-full flex items-center justify-between px-1 sm:px-0 mb-3 group"
                        >
                          <div className="flex items-center gap-2">
                            <h2 className="text-md sm:text-lg font-bold text-gray-800">Upcoming Umpiring Assignments</h2>
                            <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-0.5 rounded-full">
                              {upcomingUmpiringAssignments.length}
                            </span>
                          </div>
                          <span className={`text-gray-400 transition-transform duration-200 ${umpiringExpanded ? 'rotate-180' : 'rotate-0'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        </button>

                        {/* Collapsible content */}
                        {umpiringExpanded && (
                          <div className="space-y-4">
                            {tournamentGroups.map(([tournamentName, assignments]) => (
                              <div key={tournamentName}>
                                {tournamentGroups.length > 1 && (
                                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2 px-1">
                                    🏆 {tournamentName}
                                  </p>
                                )}
                                <div className="flex overflow-x-auto gap-4 pb-2 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
                                  {assignments.map((assignment, idx) => (
                                    <UmpiringBannerItem
                                      key={assignment.id || `${assignment.date}-${assignment.homeTeam}`}
                                      assignment={assignment}
                                      currentUserId={user?.uid}
                                      currentUserName={user?.displayName || user?.email || 'Player'}
                                      onVolunteer={handleVolunteerUmpire}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Upcoming Events (Top Placement) */}
                  {(() => {
                    const topCarouselEvents = urgentUpcomingEvents.filter(e => e.eventType !== 'training');
                    if (topCarouselEvents.length === 0) return null;

                    return (
                      <div id="events-section" className="mb-6">
                        <div className="relative">
                          <div
                            className="flex gap-4 px-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-2"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            onScroll={(e) => {
                              if (topCarouselEvents.length > 1) {
                                const index = Math.round(e.target.scrollLeft / e.target.offsetWidth);
                                const dots = document.querySelectorAll('.event-carousel-dot');
                                dots.forEach((dot, i) => {
                                  if (i === index) {
                                    dot.className = 'event-carousel-dot w-2 h-2 rounded-full bg-slate-800 transition-colors duration-200';
                                  } else {
                                    dot.className = 'event-carousel-dot w-2 h-2 rounded-full bg-transparent border border-slate-800 transition-colors duration-200';
                                  }
                                });
                              }
                            }}
                          >
                            {topCarouselEvents.map(event => (
                              <NextEventBanner
                                key={event.id}
                                event={event}
                                eventRsvps={eventRsvps}
                                handleEventRsvp={handleEventRsvp}
                                editingEventNote={editingEventNote}
                                setEditingEventNote={setEditingEventNote}
                                formatDate={formatDate}
                                allClubMembers={allClubMembers}
                              />
                            ))}
                          </div>

                          {topCarouselEvents.length > 1 && (
                            <div className="flex justify-center gap-1.5 mt-2">
                              {topCarouselEvents.map((_, idx) => (
                                <div
                                  key={idx}
                                  className={`event-carousel-dot w-2 h-2 rounded-full transition-colors duration-200 ${idx === 0 ? 'bg-slate-800' : 'bg-transparent border border-slate-800'}`}
                                ></div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}



                  {/* Desktop View Mode Toggle */}
                  <div className="hidden md:flex justify-end mb-4">
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                      <button
                        onClick={() => setMobileViewMode('list')}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${mobileViewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        <List size={16} /> List View
                      </button>
                      <button
                        onClick={() => setMobileViewMode('calendar')}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${mobileViewMode === 'calendar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        <CalendarDays size={16} /> Calendar
                      </button>
                    </div>
                  </div>

                  {/* Calendar View Container */}
                  {mobileViewMode === 'calendar' && (
                    <div className="px-5 md:px-0 mb-8 mt-4">
                      {/* Filter Pills for Calendar View */}
                      {renderFilterPills()}
                      <div id="calendar-section" className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                        {renderCalendarContent()}
                      </div>
                    </div>
                  )}

                  {/* Upcoming Games */}
                  {mobileViewMode === 'list' && (() => {
                    let baseGamesList = urgentUpcomingGames;
                    let baseEventsList = [];

                    if (!showAllEventsOnly) {
                      baseGamesList = baseGamesList.filter(g => !g.isNotRostered);
                    } else {
                      baseEventsList = urgentUpcomingEvents;
                    }

                    let upNextGames = [];
                    let upNextGameIds = new Set();
                    if (baseGamesList.length > 0) {
                      const todayDate = new Date();
                      todayDate.setHours(0, 0, 0, 0);
                      const nextWeekDate = new Date(todayDate);
                      nextWeekDate.setDate(nextWeekDate.getDate() + 7);
                      nextWeekDate.setHours(23, 59, 59, 999);

                      upNextGames = baseGamesList.filter(g => {
                        if (!g.date) return false;
                        const [y, m, d] = String(g.date).split('T')[0].split('-').map(Number);
                        const gDateObj = new Date(y, m - 1, d);
                        return gDateObj <= nextWeekDate;
                      });

                      if (upNextGames.length === 0) {
                        upNextGames = [baseGamesList[0]];
                      }
                      upNextGameIds = new Set(upNextGames.map(g => g.id));
                    }

                    let filteredGamesList = filteredAllGames.filter(g => baseGamesList.some(b => b.id === g.id));
                    let filteredEventsList = filteredAllEvents.filter(e => baseEventsList.some(b => b.id === e.id));

                    return (
                      <div id="games-section" className="px-5 md:px-0">
                        {upNextGames.length > 0 && (
                          <div className="mb-8">
                            <h2 className="text-xl font-extrabold text-slate-800 mb-4 tracking-tight">Up Next</h2>
                            <div className="flex flex-col md:flex-row gap-6 overflow-x-auto pb-4 custom-scrollbar">
                              {upNextGames.map(game => (
                                <div key={game.id} className="min-w-full md:min-w-[448px] xl:min-w-[512px] flex-shrink-0">
                                  {renderSingleGameCard(game)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mb-8 mt-2">
                          <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
                              Down the Road
                            </h2>
                            <div className="flex bg-gray-100 rounded-lg p-1 hidden sm:flex">
                              <button onClick={() => setEventsViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${eventsViewMode === 'grid' ? 'bg-white shadow-sm text-purple-600' : 'text-gray-500 hover:text-gray-700'}`} title="Grid View">
                                <LayoutGrid size={16} />
                              </button>
                              <button onClick={() => setEventsViewMode('list')} className={`p-1.5 rounded-md transition-colors ${eventsViewMode === 'list' ? 'bg-white shadow-sm text-purple-600' : 'text-gray-500 hover:text-gray-700'}`} title="List View">
                                <List size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Filter Pills for Down the Road */}
                          {renderFilterPills()}

                          {eventsViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                              {(() => {
                                if (filteredGamesList.length > 0 || filteredEventsList.length > 0) {
                                  return (
                                    <>
                                      {/* If Practice filter triggers events, render them here first */}
                                      {filteredEventsList.map(event => (
                                        <div key={event.id} className="mb-4">
                                          <NextEventBanner
                                            event={event}
                                            eventRsvps={eventRsvps}
                                            handleEventRsvp={handleEventRsvp}
                                            editingEventNote={editingEventNote}
                                            setEditingEventNote={setEditingEventNote}
                                            formatDate={formatDate}
                                            allClubMembers={allClubMembers}
                                          />
                                        </div>
                                      ))}
                                      {/* Followed by the remaining games */}
                                      {filteredGamesList.map((game, index) => {
                                        if (upNextGameIds.has(game.id)) {
                                          return null;
                                        }
                                        return renderSingleGameCard(game);
                                      })}
                                    </>
                                  );
                                } else {
                                  return (
                                    <div className="text-center py-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200 col-span-full">
                                      <p className="text-gray-500 font-medium text-sm">No remaining games or events match this filter</p>
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-5">
                              {(() => {
                                if (filteredGamesList.length > 0 || filteredEventsList.length > 0) {
                                  return (
                                    <>
                                      {filteredEventsList.map(event => (
                                        <div key={event.id} className="mb-4">
                                          <NextEventBanner
                                            event={event}
                                            eventRsvps={eventRsvps}
                                            handleEventRsvp={handleEventRsvp}
                                            editingEventNote={editingEventNote}
                                            setEditingEventNote={setEditingEventNote}
                                            formatDate={formatDate}
                                            allClubMembers={allClubMembers}
                                          />
                                        </div>
                                      ))}
                                      {filteredGamesList.length > 0 && (
                                        <div className="overflow-x-auto w-full border border-gray-200 rounded-xl shadow-sm bg-white">
                                          <table className="w-full text-left border-collapse min-w-[800px]">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                              <tr className="text-xs uppercase tracking-wider text-gray-500 font-semibold text-left">
                                                <th className="p-3 whitespace-nowrap">Date</th>
                                                <th className="p-3">Opponent</th>
                                                <th className="p-3 whitespace-nowrap">Time</th>
                                                <th className="p-3 whitespace-nowrap">Arrival</th>
                                                <th className="p-3">Address</th>
                                                <th className="p-3 text-center">Available</th>
                                                <th className="p-3 text-center">Team Responses</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                              {filteredGamesList.map((game, index) => {
                                                if (upNextGameIds.has(game.id)) return null;
                                                return renderSingleGameListRow(game);
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </>
                                  );
                                } else {
                                  return (
                                    <div className="text-center py-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                      <p className="text-gray-500 font-medium text-sm">No remaining games or events match this filter</p>
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}



                  {/* Captain's Corner - Desktop Only */}
                  <div className="hidden md:block">
                    {renderCaptainsCorner()}
                  </div>

                  {/* Visual Separator with Divider */}
                  <div className="my-12">
                    <div className="border-t border-gray-200"></div>
                  </div>

                  {/* Volunteer Hours Section */}
                  {
                    clubInfo?.id && playerData?.id && (
                      <div className="mb-8">
                        <PlayerVolunteerSection
                          playerId={playerData.id}
                          playerName={playerData.name}
                          clubId={clubInfo.id}
                          games={gamesPlayed}
                          events={events}
                        />
                      </div>
                    )
                  }



                  {/* Visual Separator with Divider */}
                  <div className="my-12">
                    <div className="border-t border-gray-200"></div>
                  </div>

                  {/* Merchandise Section - HIDDEN FOR NOW
                  {(() => {
                    const merchandiseOrders = expenses.filter(e =>
                      e.isMerchandise &&
                      e.userId === playerData.id
                    ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                    const handleCancelMerchandiseOrder = async (orderId) => {
                      if (!confirm('Are you sure you want to cancel this order? This will remove the charge from your balance.')) return;

                      try {
                        setLoading(true);
                        await deleteDoc(doc(db, 'expenses', orderId));
                        await handleRefresh(); // Update localized state and balance
                        alert('Order cancelled successfully.');
                      } catch (error) {
                        console.error('Error cancelling order:', error);
                        alert(`Failed to cancel order: ${error.message}`);
                      } finally {
                        setLoading(false);
                      }
                    };

                    return (
                      <MerchandiseSection
                        orders={merchandiseOrders}
                        onCancelOrder={handleCancelMerchandiseOrder}
                        onPurchase={async (item, details) => {
                          try {
                            setLoading(true);
                            // Create expense record
                            await addDoc(collection(db, 'expenses'), {
                              amount: item.price,
                              description: details ? `${item.name} (${details})` : item.name,
                              isMerchandise: true,
                              approvalStatus: 'approved', // Auto-approved as it's a purchase
                              userId: playerData.id,
                              splitAmong: [playerData.id], // Ensure admin dashboard sees this as owed by player
                              clubId: clubInfo.id,
                              createdAt: new Date().toISOString(),
                              createdBy: user.uid,
                              playerEmail: user.email,
                              playerName: playerData.name,
                              type: 'merchandise'
                            });

                            // Refresh data to update balance
                            await handleRefresh();
                            alert(`Successfully purchased ${item.name}! Your balance has been updated.`);
                          } catch (error) {
                            console.error('Error purchasing merchandise:', error);
                            alert('Failed to process purchase. Please try again.');
                          } finally {
                            setLoading(false);
                          }
                        }}
                      />
                    );
                  })()}
                  */}

                  {/* Tournaments Modal */}
                  {
                    showTournamentsModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                          <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
                            <div>
                              <h2 className="text-xl font-bold text-gray-900">Team Tournaments</h2>
                              <p className="text-sm text-gray-500">All tournaments for your teams</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowTournamentsModal(false)}
                              className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                              ×
                            </button>
                          </div>

                          <div className="p-6">
                            {tournaments.length === 0 ? (
                              <div className="text-center py-8">
                                <Trophy className="mx-auto text-gray-300 mb-3" size={48} />
                                <p className="text-gray-600">No tournaments yet</p>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {tournaments.map(tournament => {
                                  // Get games for this tournament where player's team is involved
                                  const tournamentGames = gamesPlayed.filter(g => {
                                    if (g.tournamentId !== tournament.id) return false;
                                    const playerTeams = playerData?.allTeams?.map(t => t.teamId) || (playerData?.teamId ? [playerData.teamId] : []);
                                    return playerTeams.length > 0 && (playerTeams.includes(g.homeTeamId) || playerTeams.includes(g.awayTeamId) || playerTeams.includes(g.teamId));
                                  });

                                  // Count games player actually played in
                                  const gamesPlayerPlayed = tournamentGames.filter(g =>
                                    g.playing11?.includes(playerData?.id)
                                  ).length;

                                  const participated = gamesPlayerPlayed > 0;

                                  return (
                                    <div
                                      key={tournament.id}
                                      className={`p-4 rounded-lg border-2 ${participated
                                        ? 'bg-purple-50 border-purple-200'
                                        : 'bg-gray-50 border-gray-200'
                                        }`}
                                    >
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-800">{tournament.name}</h3>
                                            {participated && (
                                              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">
                                                ✓ Participated
                                              </span>
                                            )}
                                          </div>
                                          {tournament.startDate && (
                                            <p className="text-sm text-gray-500 mt-1">
                                              {formatDate(tournament.startDate, { month: 'short', year: 'numeric' })}
                                              {tournament.endDate && ` - ${formatDate(tournament.endDate, { month: 'short', year: 'numeric' })}`}
                                            </p>
                                          )}
                                        </div>
                                        <div className="text-right">
                                          <p className={`text-lg font-bold ${participated ? 'text-purple-600' : 'text-gray-400'}`}>
                                            {gamesPlayerPlayed}/{tournamentGames.length}
                                          </p>
                                          <p className="text-xs text-gray-500">games played</p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Summary */}
                                <div className="border-t pt-4 mt-4">
                                  <div className="flex justify-between items-center p-4 bg-purple-100 rounded-lg">
                                    <span className="font-bold text-gray-800">Total Participation</span>
                                    <span className="text-xl font-bold text-purple-600">
                                      {tournamentsParticipated} of {totalTournaments} tournaments
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }

                  {/* Games Modal */}
                  {
                    showGamesModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                          <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
                            <div>
                              <h2 className="text-xl font-bold text-gray-900">All Games</h2>
                              <p className="text-sm text-gray-500">Games by tournament for your teams</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowGamesModal(false)}
                              className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                              ×
                            </button>
                          </div>

                          <div className="p-6">
                            {(() => {
                              // Group games by tournament
                              const gamesByTournamentForModal = {};

                              tournaments.forEach(tournament => {
                                const tournamentGames = gamesPlayed.filter(g => {
                                  if (g.tournamentId !== tournament.id) return false;
                                  const playerTeams = playerData?.allTeams?.map(t => t.teamId) || (playerData?.teamId ? [playerData.teamId] : []);
                                  return playerTeams.length > 0 && (playerTeams.includes(g.homeTeamId) || playerTeams.includes(g.awayTeamId) || playerTeams.includes(g.teamId));
                                });

                                if (tournamentGames.length > 0) {
                                  gamesByTournamentForModal[tournament.id] = {
                                    tournament,
                                    games: tournamentGames.sort((a, b) => new Date(a.date) - new Date(b.date))
                                  };
                                }
                              });

                              const tournamentIds = Object.keys(gamesByTournamentForModal);

                              if (tournamentIds.length === 0) {
                                return (
                                  <div className="text-center py-8">
                                    <Calendar className="mx-auto text-gray-300 mb-3" size={48} />
                                    <p className="text-gray-600">No games scheduled yet</p>
                                  </div>
                                );
                              }

                              return (
                                <div className="space-y-6">
                                  {tournamentIds.map(tournamentId => {
                                    const { tournament, games } = gamesByTournamentForModal[tournamentId];
                                    const gamesPlayed = games.filter(g => g.playing11?.includes(playerData?.id)).length;

                                    return (
                                      <div key={tournamentId}>
                                        <div className="flex items-center justify-between mb-3">
                                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                            <Trophy size={18} className="text-purple-600" />
                                            {tournament.name}
                                          </h3>
                                          <span className="text-sm text-gray-500">
                                            {gamesPlayed}/{games.length} played
                                          </span>
                                        </div>

                                        <div className="space-y-2">
                                          {games.map(game => {
                                            const played = game.playing11?.includes(playerData?.id);
                                            const gameDate = new Date(game.date);
                                            const isPast = gameDate < new Date();

                                            return (
                                              <div
                                                key={game.id}
                                                className={`p-3 rounded-lg border flex justify-between items-center ${played
                                                  ? 'bg-green-50 border-green-200'
                                                  : isPast
                                                    ? 'bg-gray-50 border-gray-200 opacity-60'
                                                    : 'bg-blue-50 border-blue-200'
                                                  }`}
                                              >
                                                <div className="flex items-center gap-3">
                                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${played ? 'bg-green-200 text-green-700' : isPast ? 'bg-gray-200 text-gray-500' : 'bg-blue-200 text-blue-700'
                                                    }`}>
                                                    {gameDate.getDate()}
                                                    <span className="text-xs ml-0.5">{formatDate(gameDate, { month: 'short' })}</span>
                                                  </div>
                                                  <div>
                                                    <p className="font-medium text-gray-800">{game.name}</p>
                                                    {game.opponent && (
                                                      <p className="text-sm text-gray-500">vs {game.opponent}</p>
                                                    )}
                                                  </div>
                                                </div>
                                                <div>
                                                  {played ? (
                                                    <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                                                      ✓ Played
                                                    </span>
                                                  ) : isPast ? (
                                                    <span className="text-xs bg-gray-400 text-white px-2 py-1 rounded-full">
                                                      Missed
                                                    </span>
                                                  ) : (
                                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                                      Upcoming
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Summary */}
                                  <div className="border-t pt-4 mt-4">
                                    <div className="flex justify-between items-center p-4 bg-blue-100 rounded-lg">
                                      <span className="font-bold text-gray-800">Total Games Played</span>
                                      <span className="text-xl font-bold text-blue-600">
                                        {totalGamesPlayed} of {totalGamesInClub} games
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )
                  }

                  {/* Squad Selection Modal */}
                  {
                    showSelectPlaying11 && selectedGameForSquad && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                          <SquadSelection
                            game={selectedGameForSquad}
                            tournament={tournaments.find(t => t.id === selectedGameForSquad.tournamentId)}
                            team={playerData ? { id: playerData.teamId, name: playerData.teamName, ballType: playerData.ballType } : null}
                            availablePlayers={teamPlayers}
                            expenses={expenses}
                            walletTransactions={walletTransactions}
                            canOverridePaymentBlock={playerData?.teamRole === 'captain' || playerData?.teamRole === 'vice-captain'}
                            onBack={() => setShowSelectPlaying11(false)}
                            onSave={handleSaveSquad}
                          />
                        </div>
                      </div>
                    )
                  }
                </div >
              )}

              {/* ADD EXPENSE VIEW */}
              {
                view === 'add-expense' && (
                  <div className="max-w-2xl mx-auto px-4 py-8">
                    <button
                      onClick={() => {
                        // Clear form when going back
                        setExpenseDescription('');
                        setExpenseAmount('');
                        setExpenseGameId('');
                        setExpenseTournamentId('');
                        setExpenseLevel('match');
                        setSelectedCategory('');
                        setCustomDescription('');
                        setExpenseReceipt(null);
                        setCapturedImage(null);
                        setEditingExpense(null);
                        setView(editingExpense?.isDraft ? 'my-expenses' : 'dashboard');
                      }}
                      className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
                    >
                      ← Back to {editingExpense?.isDraft ? 'My Expenses' : 'Dashboard'}
                    </button>

                    <div className="bg-white rounded-xl shadow-sm p-8">
                      <h2 className="text-2xl font-bold mb-6">
                        {editingExpense?.isDraft ? 'Edit Draft Expense' : 'Add Expense'}
                      </h2>

                      <div className="space-y-4">
                        {/* Level Selector */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expense Level
                          </label>
                          <div className="flex p-1 bg-gray-100 rounded-lg">
                            <button
                              onClick={() => {
                                setExpenseLevel('club');
                                setExpenseGameId('');
                                setExpenseTournamentId('');
                                setSelectedCategory('');
                                setCustomDescription('');
                                setExpenseDescription('');
                              }}
                              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${expenseLevel === 'club'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                              Club
                            </button>
                            <button
                              onClick={() => {
                                setExpenseLevel('tournament');
                                setExpenseGameId('');
                                setSelectedCategory('');
                                setCustomDescription('');
                                setExpenseDescription('');
                              }}
                              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${expenseLevel === 'tournament'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                              Tournament
                            </button>
                            <button
                              onClick={() => {
                                setExpenseLevel('match');
                                setExpenseTournamentId('');
                                setSelectedCategory('');
                                setCustomDescription('');
                                setExpenseDescription('');
                              }}
                              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${expenseLevel === 'match'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                              Match
                            </button>
                          </div>
                        </div>

                        {/* Context Selection (Game/Tournament) */}
                        {expenseLevel === 'match' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Game *
                            </label>
                            <select
                              value={expenseGameId}
                              onChange={(e) => setExpenseGameId(e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Choose a game...</option>
                              {gamesPlayed.map(game => (
                                <option key={game.id} value={game.id}>
                                  {game.name} - {game.date}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {expenseLevel === 'tournament' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Tournament *
                            </label>
                            <select
                              value={expenseTournamentId}
                              onChange={(e) => setExpenseTournamentId(e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Choose a tournament...</option>
                              {tournaments.map(t => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Expense Description / Category */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expense Description *
                          </label>

                          {expenseLevel === 'club' ? (
                            /* Club Level: Free Text */
                            <input
                              type="text"
                              value={expenseDescription}
                              onChange={(e) => setExpenseDescription(e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Annual Domain Renewal, Trophy Purchase"
                            />
                          ) : (
                            /* Tournament & Match Level: Dropdown */
                            <div className="space-y-3">
                              <select
                                value={selectedCategory}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setSelectedCategory(val);
                                  // Reset custom description if category changes
                                  if (val !== 'Other' && val !== 'Umpiring assignment') {
                                    setCustomDescription('');
                                    setExpenseDescription(val);
                                  } else {
                                    setExpenseDescription(''); // Will be constructed from custom
                                  }
                                }}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select category...</option>
                                {(expenseLevel === 'match' ? MATCH_EXPENSE_TYPES : TOURNAMENT_EXPENSE_TYPES).map(cat => (
                                  <option key={cat} value={cat}>{cat}</option>
                                ))}
                              </select>

                              {/* Mandatory Specification for "Other" or "Umpiring assignment" */}
                              {(selectedCategory === 'Other' || selectedCategory === 'Umpiring assignment') && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Please specify details *
                                  </label>
                                  <input
                                    type="text"
                                    value={customDescription}
                                    onChange={(e) => {
                                      setCustomDescription(e.target.value);
                                      // Combine category + custom for final description
                                      // e.g. "Umpiring assignment - vs Wildfire"
                                      const prefix = selectedCategory === 'Other' ? '' : `${selectedCategory} - `;
                                      setExpenseDescription(selectedCategory === 'Other' ? e.target.value : `${prefix}${e.target.value}`);
                                    }}
                                    placeholder={selectedCategory === 'Umpiring assignment' ? "e.g., vs Wildfire CC" : "Specify expense details..."}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50"
                                    autoFocus
                                  />
                                </div>
                              )}

                              {/* Assignment Date for Umpiring assignment */}
                              {selectedCategory === 'Umpiring assignment' && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1 mt-3">
                                    Assignment Date *
                                  </label>
                                  <input
                                    type="date"
                                    value={expenseDate}
                                    onChange={(e) => setExpenseDate(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount ($) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={expenseAmount}
                            onChange={(e) => setExpenseAmount(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                            placeholder="0.00"
                          />
                        </div>



                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Upload Receipt {['umpiring fee', 'umpiring assignment'].some(type => selectedCategory?.toLowerCase() === type || expenseDescription?.toLowerCase().startsWith(type)) ? '(Optional)' : '*'}
                          </label>
                          {!['umpiring fee', 'umpiring assignment'].some(type => selectedCategory?.toLowerCase() === type || expenseDescription?.toLowerCase().startsWith(type)) && (expenseDescription || selectedCategory) && (
                            <p className="text-xs text-orange-600 mb-2">⚠️ Receipt is required for {expenseDescription || selectedCategory}</p>
                          )}

                          {capturedImage || expenseReceipt ? (
                            <div className="border rounded-lg p-4 bg-green-50">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-green-700">✓ Receipt attached</p>
                                <button
                                  onClick={() => {
                                    setCapturedImage(null);
                                    setExpenseReceipt(null);
                                  }}
                                  className="text-red-600 text-sm hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                              {capturedImage && (
                                <img src={capturedImage} alt="Receipt" className="max-w-full h-auto rounded border" />
                              )}
                              {expenseReceipt && !capturedImage && (
                                <p className="text-sm text-gray-600">📄 {expenseReceipt.name}</p>
                              )}
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-4">
                              <button
                                type="button"
                                onClick={startCameraCapture}
                                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all hover:scale-[1.02]"
                              >
                                <Camera size={32} className="mb-2" />
                                <span className="text-sm font-semibold">Camera</span>
                              </button>

                              <div className="relative">
                                <input
                                  type="file"
                                  accept="image/*,.pdf"
                                  onChange={(e) => setExpenseReceipt(e.target.files[0])}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                  id="receipt-upload"
                                />
                                <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 transition-all h-full hover:scale-[1.02]">
                                  <div className="mb-2 p-1 bg-white rounded-lg shadow-sm">
                                    <FileText size={24} className="text-gray-500" />
                                  </div>
                                  <span className="text-sm font-semibold">Gallery/Files</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={handleSaveAsDraft}
                            disabled={savingDraft || !expenseDescription || !expenseAmount}
                            className={`flex-1 py-3 rounded-lg font-semibold border-2 ${savingDraft || !expenseDescription || !expenseAmount
                              ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'border-gray-400 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {savingDraft ? 'Saving...' : (editingExpense?.isDraft ? 'Update Draft' : 'Save as Draft')}
                          </button>
                          <button
                            onClick={editingExpense?.isDraft ?
                              async () => {
                                // Save draft changes first, then submit
                                await handleSaveAsDraft();
                                if (editingExpense?.id) {
                                  await handleSubmitFromDraft(editingExpense.id);
                                }
                              } :
                              handleAddExpense
                            }
                            disabled={uploadingReceipt || !expenseDescription || !expenseAmount}
                            className={`flex-1 py-3 rounded-lg font-semibold ${uploadingReceipt || !expenseDescription || !expenseAmount
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-orange-600 text-white hover:bg-orange-700'
                              }`}
                          >
                            {uploadingReceipt ? 'Submitting...' : 'Submit Expense'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              {/* EDIT EXPENSE VIEW */}
              {
                view === 'edit-expense' && (
                  <div className="max-w-2xl mx-auto px-4 py-8">
                    <button
                      onClick={() => {
                        setView('dashboard');
                        setEditingExpense(null);
                      }}
                      className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
                    >
                      ← Back to Dashboard
                    </button>

                    <div className="bg-white rounded-xl shadow-sm p-8">
                      <h2 className="text-2xl font-bold mb-6">Edit Expense</h2>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expense Description *
                          </label>
                          <input
                            type="text"
                            value={expenseDescription}
                            onChange={(e) => setExpenseDescription(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                            placeholder="e.g., Ground Rental, Lunch, Umpiring Fee, etc."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount ($) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={expenseAmount}
                            onChange={(e) => setExpenseAmount(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Game (Optional)
                          </label>
                          <select
                            value={expenseGameId}
                            onChange={(e) => setExpenseGameId(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                          >
                            <option value="">Choose a game (optional)...</option>
                            {gamesPlayed.map(game => (
                              <option key={game.id} value={game.id}>
                                {game.name} - {game.date}
                              </option>
                            ))}
                          </select>
                          
                          {expenseGameId === 'other' && (
                            <div className="mt-4 border-t pt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Match Details (Teams, Date, etc.) *
                              </label>
                              <input
                                type="text"
                                value={otherGameDetails}
                                onChange={(e) => setOtherGameDetails(e.target.value)}
                                placeholder="e.g., SUY vs Falcons - Oct 12"
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
                                required
                              />
                            </div>
                          )}
                        </div>

                        {editingExpense?.receiptURL && !removeOldReceipt && !capturedImage && (
                          <div className="border rounded-lg p-4 bg-blue-50">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium">Current Receipt</p>
                                <a href={editingExpense.receiptURL} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600">
                                  View receipt
                                </a>
                              </div>
                              <button
                                onClick={() => setRemoveOldReceipt(true)}
                                className="text-red-600 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}

                        {(!editingExpense?.receiptURL || removeOldReceipt) && !capturedImage && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Upload Receipt {['umpiring fee', 'umpiring assignment'].some(type => selectedCategory?.toLowerCase() === type || expenseDescription?.toLowerCase().startsWith(type)) ? '(Optional)' : '*'}
                            </label>
                            {!['umpiring fee', 'umpiring assignment'].some(type => selectedCategory?.toLowerCase() === type || expenseDescription?.toLowerCase().startsWith(type)) && (expenseDescription || selectedCategory) && (
                              <p className="text-xs text-orange-600 mb-2">⚠️ Receipt is required for {expenseDescription || selectedCategory}</p>
                            )}
                            <div className="space-y-3">
                              <button
                                type="button"
                                onClick={startCameraCapture}
                                className="w-full py-3 border-2 border-dashed border-blue-400 rounded-lg bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Take Photo with Camera
                              </button>

                              <div className="text-center text-sm text-gray-500">or</div>

                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                <input
                                  type="file"
                                  accept="image/*,.pdf"
                                  onChange={(e) => setExpenseReceipt(e.target.files[0])}
                                  className="hidden"
                                  id="receipt-upload-edit"
                                />
                                <label htmlFor="receipt-upload-edit" className="cursor-pointer">
                                  <div className="text-gray-600">
                                    {expenseReceipt ? (
                                      <p className="font-medium text-green-600">✓ {expenseReceipt.name}</p>
                                    ) : (
                                      <p className="font-medium">Click to upload from gallery</p>
                                    )}
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                        {capturedImage && (
                          <div className="border rounded-lg p-4 bg-green-50">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-green-700">✓ New photo captured</p>
                              <button
                                onClick={() => {
                                  setCapturedImage(null);
                                  setExpenseReceipt(null);
                                }}
                                className="text-red-600 text-sm hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                            <img src={capturedImage} alt="Receipt" className="max-w-full h-auto rounded border" />
                          </div>
                        )}

                        {editingExpense?.isDraft ? (
                          <div className="flex gap-3">
                            <button
                              onClick={handleSaveAsDraft}
                              disabled={savingDraft}
                              className={`flex-1 py-3 rounded-lg font-semibold border-2 ${savingDraft
                                ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'border-gray-400 bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              {savingDraft ? 'Saving...' : 'Update Draft'}
                            </button>
                            <button
                              onClick={async () => {
                                // Convert draft to submitted expense
                                await handleSaveAsDraft(); // Save changes first
                                await handleSubmitFromDraft(editingExpense.id);
                              }}
                              disabled={uploadingReceipt}
                              className="flex-1 py-3 rounded-lg font-semibold bg-orange-600 text-white hover:bg-orange-700"
                            >
                              {uploadingReceipt ? 'Submitting...' : 'Submit Expense'}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={handleEditExpense}
                            disabled={uploadingReceipt}
                            className="w-full py-3 rounded-lg font-semibold bg-orange-600 text-white hover:bg-orange-700"
                          >
                            {uploadingReceipt ? 'Updating...' : 'Update Expense'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }

              {/* MY EXPENSES VIEW */}
              {
                view === 'my-expenses' && (
                  <div className="max-w-4xl mx-auto px-4 py-8">
                    <button
                      onClick={() => setView('dashboard')}
                      className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
                    >
                      ← Back to Dashboard
                    </button>

                    <div className="bg-white rounded-xl shadow-sm p-8">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">My Expenses</h2>
                        {!isClubArchivedForUser && (
                          <button
                            onClick={() => setView('add-expense')}
                            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 text-sm font-semibold flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Expense
                          </button>
                        )}
                      </div>

                      {/* Summary Stats */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="bg-orange-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-orange-700">
                            {draftExpenses.length}
                          </p>
                          <p className="text-sm text-gray-600">Drafts</p>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-yellow-700">
                            {submittedExpenses.filter(e => e.approvalStatus === 'pending').length}
                          </p>
                          <p className="text-sm text-gray-600">Pending</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-green-700">
                            {submittedExpenses.filter(e => e.approvalStatus === 'approved').length}
                          </p>
                          <p className="text-sm text-gray-600">Approved</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-red-700">
                            {submittedExpenses.filter(e => e.approvalStatus === 'denied').length}
                          </p>
                          <p className="text-sm text-gray-600">Denied</p>
                        </div>
                      </div>

                      {/* Draft Expenses Section */}
                      {draftExpenses.length > 0 && (
                        <div className="mb-8">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-orange-700 flex items-center gap-2">
                              <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                              </span>
                              Pending Submissions ({draftExpenses.length})
                            </h3>
                            <span className="text-sm text-orange-600 font-medium">
                              Action Required
                            </span>
                          </div>
                          <div className="space-y-3">
                            {draftExpenses
                              .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                              .map(draft => (
                                <div key={draft.id} className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 hover:shadow-md transition-shadow">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-2">
                                        <h4 className="font-semibold text-gray-800">{draft.description}</h4>
                                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                                          DRAFT
                                        </span>
                                      </div>
                                      <div className="space-y-1">
                                        <p className="text-sm text-gray-600">
                                          💰 Amount: <span className="font-semibold">{formatCurrency(draft.amount)}</span>
                                        </p>
                                        <p className="text-sm text-gray-600">🏏 Game: {draft.gameName || 'No game selected'}</p>
                                        {draft.tournamentId && (
                                          <p className="text-sm text-gray-600">
                                            🏆 Tournament: <span className="font-medium">{draft.tournamentName || tournaments.find(t => t.id === draft.tournamentId)?.name || 'Tournament'}</span>
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500">
                                          📅 Last updated: {formatDateTime(draft.updatedAt, {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-2 ml-4">
                                      {draft.receiptURL && (
                                        <a
                                          href={draft.receiptURL}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs font-semibold border border-blue-600 text-center"
                                        >
                                          📄 Receipt
                                        </a>
                                      )}
                                      <button
                                        onClick={() => handleSubmitFromDraft(draft.id)}
                                        className="px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded text-xs font-semibold"
                                      >
                                        Submit
                                      </button>
                                      <button
                                        onClick={() => handleEditDraft(draft)}
                                        className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs font-semibold border border-blue-600"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteDraft(draft.id)}
                                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded text-xs font-semibold"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Submitted Expenses Section */}
                      <div>
                        <h3 className="text-lg font-bold mb-4 text-gray-700">Submitted Expenses</h3>
                        {submittedExpenses.length === 0 ? (
                          <div className="text-center py-12 text-gray-500">
                            <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg mb-2">You haven't submitted any expenses yet</p>
                            <button
                              onClick={() => setView('add-expense')}
                              className="mt-4 text-orange-600 font-semibold hover:underline"
                            >
                              Submit your first expense →
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {submittedExpenses
                              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                              .map(expense => (
                                <div key={expense.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-2">
                                        <h3 className="font-bold text-lg">{expense.description}</h3>
                                        {/* Status Badge */}
                                        {expense.approvalStatus === 'pending' && (
                                          <span className="text-xs bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-semibold">
                                            ⏳ Pending Review
                                          </span>
                                        )}
                                        {expense.approvalStatus === 'approved' && (
                                          <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                                            ✓ Approved
                                          </span>
                                        )}
                                        {expense.approvalStatus === 'denied' && (
                                          <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold">
                                            ✗ Denied
                                          </span>
                                        )}
                                      </div>

                                      <div className="space-y-1">
                                        <p className="text-sm text-gray-600">
                                          💰 Amount: <span className="font-semibold">{formatCurrency(expense.amount)}</span>
                                        </p>
                                        <p className="text-sm text-gray-600">🏏 Game: {expense.gameName || 'No game selected'}</p>
                                        {expense.tournamentId && (
                                          <p className="text-sm text-gray-600">
                                            🏆 Tournament: <span className="font-medium">{expense.tournamentName || tournaments.find(t => t.id === expense.tournamentId)?.name || 'Tournament'}</span>
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-2">
                                          📅 Submitted: {formatDate(expense.createdAt, {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </p>

                                        {/* Approval/Denial Info */}
                                        {expense.approvalStatus === 'approved' && expense.approvedAt && (
                                          <>
                                            <p className="text-xs text-green-600 mt-1">
                                              ✓ Approved by {expense.approvedByName || expense.approvedBy} on {formatDate(expense.approvedAt)}
                                            </p>
                                            {expense.approvalNote && (
                                              <p className="text-xs text-gray-500 italic mt-1">
                                                Note: "{expense.approvalNote}"
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {expense.approvalStatus === 'denied' && (expense.rejectionReason || expense.denialReason) && (
                                          <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                                            <p className="text-xs font-semibold text-red-700">Denial Reason:</p>
                                            <p className="text-xs text-red-600">{expense.rejectionReason || expense.denialReason}</p>
                                            {expense.deniedByName && (
                                              <p className="text-xs text-red-500 mt-1">Denied by: {expense.deniedByName}</p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex flex-col gap-2 ml-4">
                                      {expense.receiptURL && (
                                        <a
                                          href={expense.receiptURL}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm font-semibold border border-blue-600 text-center"
                                        >
                                          📄 View Receipt
                                        </a>
                                      )}
                                      {(expense.approvalStatus === 'pending' || expense.approvalStatus === 'denied') && (
                                        <>
                                          <button
                                            onClick={() => {
                                              setEditingExpense(expense);
                                              setExpenseDescription(expense.description);
                                              setExpenseAmount(expense.amount.toString());
                                              
                                              if (!expense.gameId && expense.gameName) {
                                                setExpenseGameId('other');
                                                setOtherGameDetails(expense.gameName);
                                              } else {
                                                setExpenseGameId(expense.gameId || '');
                                                setOtherGameDetails('');
                                              }

                                              // Set level and tournament
                                              setExpenseLevel(expense.gameId ? 'match' : (expense.tournamentId ? 'tournament' : 'club'));
                                              setExpenseTournamentId(expense.tournamentId || '');
                                              setExpenseDate(expense.expenseDate || '');

                                              // Set category if it's tournament level
                                              if (expense.tournamentId) {
                                                const cat = TOURNAMENT_EXPENSE_TYPES.find(t => expense.description.startsWith(t)) || 'Other';
                                                setSelectedCategory(cat);
                                                if (cat === 'Other' || cat === 'Umpiring assignment') {
                                                  const prefix = cat === 'Other' ? '' : `${cat} - `;
                                                  setCustomDescription(expense.description.replace(prefix, ''));
                                                }
                                              }

                                              setView('edit-expense');
                                            }}
                                            className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm font-semibold border border-blue-600"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (confirm('Delete this expense? This cannot be undone.')) {
                                                handleDeleteExpense(expense.id);
                                              }
                                            }}
                                            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded text-sm font-semibold"
                                          >
                                            Delete
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>{/* End of Submitted Expenses Section */}
                    </div>
                  </div>
                )
              }

              {/* MY PAYMENTS VIEW */}
              {
                view === 'my-payments' && (
                  <div className="max-w-4xl mx-auto px-4 py-8">
                    <button
                      onClick={() => setView('dashboard')}
                      className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
                    >
                      ← Back to Dashboard
                    </button>

                    <div className="bg-white rounded-xl shadow-sm p-8">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">My Payment Submissions</h2>
                        <button
                          onClick={() => {
                            setShowRecordPayment(true);
                            setPaymentAmount(balance.toString());
                            setPaymentDate(new Date().toISOString().split('T')[0]);
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm font-semibold"
                        >
                          + Record Payment
                        </button>
                      </div>

                      {/* Summary Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-yellow-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-yellow-700">
                            {paymentSubmissions.filter(p => p.status === 'pending').length}
                          </p>
                          <p className="text-sm text-gray-600">Pending</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-green-700">
                            {paymentSubmissions.filter(p => p.status === 'approved').length}
                          </p>
                          <p className="text-sm text-gray-600">Approved</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-red-700">
                            {paymentSubmissions.filter(p => p.status === 'denied').length}
                          </p>
                          <p className="text-sm text-gray-600">Denied</p>
                        </div>
                      </div>

                      {paymentSubmissions.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <Wallet className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg mb-2">No payment submissions yet</p>
                          <button
                            onClick={() => {
                              setShowRecordPayment(true);
                              setPaymentAmount(balance.toString());
                              setPaymentDate(new Date().toISOString().split('T')[0]);
                            }}
                            className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-semibold"
                          >
                            Record Your First Payment
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {paymentSubmissions
                            .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                            .map(payment => (
                              <div key={payment.id} className={`border-2 rounded-lg p-4 ${payment.status === 'approved' ? 'border-green-200 bg-green-50' :
                                payment.status === 'denied' ? 'border-red-200 bg-red-50' :
                                  'border-yellow-200 bg-yellow-50'
                                }`}>
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</h4>
                                      <span className={`px-2 py-1 text-xs font-semibold rounded ${payment.status === 'approved' ? 'bg-green-100 text-green-700' :
                                        payment.status === 'denied' ? 'bg-red-100 text-red-700' :
                                          'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {payment.status === 'approved' && '✓ Approved'}
                                        {payment.status === 'denied' && '✗ Denied'}
                                        {payment.status === 'pending' && '⏳ Pending'}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                      <p><span className="font-medium">Method:</span> {payment.paymentMethod}</p>
                                      <p><span className="font-medium">Date:</span> {formatDate(payment.paymentDate)}</p>
                                      {payment.transactionId && (
                                        <p><span className="font-medium">Transaction ID:</span> {payment.transactionId}</p>
                                      )}
                                      {payment.notes && (
                                        <p><span className="font-medium">Notes:</span> {payment.notes}</p>
                                      )}
                                      <p className="text-xs text-gray-500">
                                        Submitted: {formatDate(payment.submittedAt)} at {formatTimeFromDate(payment.submittedAt)}
                                      </p>
                                      {payment.status === 'approved' && payment.approvedAt && (
                                        <p className="text-xs text-green-600">
                                          ✓ Approved on {formatDate(payment.approvedAt)}
                                        </p>
                                      )}
                                      {payment.status === 'denied' && payment.denialReason && (
                                        <div className="mt-2 p-2 bg-red-100 rounded border border-red-200">
                                          <p className="text-xs font-semibold text-red-700">Denial Reason:</p>
                                          <p className="text-xs text-red-600">{payment.denialReason}</p>
                                          {payment.deniedAt && (
                                            <p className="text-xs text-red-500 mt-1">
                                              Denied on {formatDate(payment.deniedAt)}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex flex-col gap-2 ml-4">
                                    {payment.receiptURL && (
                                      <a
                                        href={payment.receiptURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm font-semibold border border-blue-600 text-center"
                                      >
                                        📄 View Receipt
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              {/* PROFILE VIEW */}
              {
                view === 'profile' && (
                  <div className="max-w-4xl mx-auto px-4 py-8">
                    <button
                      onClick={() => setView('dashboard')}
                      className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
                    >
                      ← Back to Dashboard
                    </button>

                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      {/* Profile Header */}
                      <div className="bg-gradient-to-r from-green-400 to-blue-500 h-32"></div>

                      <div className="px-8 pb-8">
                        {/* Profile Picture */}
                        <div className="relative -mt-16 mb-4">
                          <div className="w-32 h-32 rounded-full bg-white p-2 shadow-lg">
                            <div className="w-full h-full rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white text-4xl font-bold overflow-hidden">
                              {playerData.profilePicture ? (
                                <img
                                  src={playerData.profilePicture}
                                  alt={playerData.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                playerData.name.charAt(0).toUpperCase()
                              )}
                            </div>
                          </div>

                          {/* Upload Button */}
                          <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 shadow-lg">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleUploadProfilePicture(e.target.files[0])}
                              className="hidden"
                            />
                            {uploadingProfilePic ? (
                              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            )}
                          </label>
                        </div>

                        {/* Profile Info */}
                        <div className="mb-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h2 className="text-3xl font-bold">{playerData.name}</h2>
                              <p className="text-gray-600">{playerData.email}</p>
                            </div>
                            <button
                              onClick={() => {
                                setProfileName(playerData.name);
                                setProfileEmail(playerData.email);
                                setProfilePhone(playerData.phone || '');
                                setProfileBio(playerData.bio || '');
                                setView('edit-profile');
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              Edit Profile
                            </button>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-purple-600">{tournamentsParticipated}</p>
                              <p className="text-sm text-gray-600">Tournaments</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-600">{totalGamesPlayed}</p>
                              <p className="text-sm text-gray-600">Games Played</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg text-center">
                              <p className="text-lg font-bold text-green-600">{clubInfo.name}</p>
                              <p className="text-sm text-gray-600">Club</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg text-center">
                              <p className="text-lg font-bold text-orange-600">
                                {playerData.teamName || 'No Team'}
                              </p>
                              <p className="text-sm text-gray-600">Team</p>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="space-y-4">
                            {playerData.teamRole && (
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700">Role:</span>
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${playerData.teamRole === 'captain' ? 'bg-yellow-100 text-yellow-700' :
                                  playerData.teamRole === 'vice-captain' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                  {playerData.teamRole === 'captain' ? 'Captain' :
                                    playerData.teamRole === 'vice-captain' ? 'Vice Captain' :
                                      'Player'}
                                </span>
                              </div>
                            )}

                            {playerData.phone && (
                              <div>
                                <span className="font-semibold text-gray-700">Phone:</span>
                                <span className="ml-2 text-gray-600">{playerData.phone}</span>
                              </div>
                            )}

                            {playerData.bio && (
                              <div>
                                <span className="font-semibold text-gray-700 block mb-2">Bio:</span>
                                <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{playerData.bio}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              {/* EDIT PROFILE VIEW */}
              {
                view === 'edit-profile' && (
                  <div className="max-w-2xl mx-auto px-4 py-8">
                    <button
                      onClick={() => setView('profile')}
                      className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
                    >
                      ← Back to Profile
                    </button>

                    <div className="bg-white rounded-xl shadow-sm p-8">
                      <h2 className="text-2xl font-bold mb-6">Edit Profile</h2>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Name *
                          </label>
                          <input
                            type="text"
                            value={profileName}
                            onChange={(e) => setProfileName(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            value={profileEmail}
                            onChange={(e) => setProfileEmail(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                            disabled
                            title="Email cannot be changed"
                          />
                          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone (Optional)
                          </label>
                          <input
                            type="tel"
                            value={profilePhone}
                            onChange={(e) => setProfilePhone(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                            placeholder="+1 234 567 8900"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bio (Optional)
                          </label>
                          <textarea
                            value={profileBio}
                            onChange={(e) => setProfileBio(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg"
                            rows="4"
                            placeholder="Tell us about yourself..."
                          />
                        </div>

                        <button
                          onClick={handleUpdateProfile}
                          disabled={!profileName}
                          className={`w-full py-3 rounded-lg font-semibold ${!profileName
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                )
              }

              {/* Add Player Modal */}
              {showAddPlayerModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                    <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                        <UserPlus size={18} /> Add Player to {playerData?.teamName}
                      </h3>
                      <button
                        onClick={() => setShowAddPlayerModal(false)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    
                    <div className="p-4 max-h-96 overflow-y-auto">
                      {unassignedPlayers.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No unassigned players available.</p>
                      ) : (
                        <div className="space-y-2">
                          {unassignedPlayers.map(player => (
                            <div key={player.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-yellow-300 transition-colors">
                              <div>
                                <p className="font-medium text-gray-800">{player.name}</p>
                                <p className="text-sm text-gray-500">{player.email}</p>
                              </div>
                              <button
                                onClick={() => handleAddPlayerToTeam(player.id)}
                                disabled={addingPlayerId === player.id}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                  addingPlayerId === player.id
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                }`}
                              >
                                {addingPlayerId === player.id ? 'Adding...' : 'Add'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Feedback Button */}
              <FeedbackButton user={user} currentPage="player-dashboard" clubId={clubInfo?.id} clubName={clubInfo?.name} />

              {/* WhatsApp Group Messenger Modal */}
              {
                showWhatsAppMessenger && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                      <WhatsAppGroupMessenger
                        team={{
                          id: playerData.teamId,
                          name: playerData.teamName,
                          whatsappGroupLink: playerData.teamWhatsAppLink,
                          players: [{
                            userId: user.uid,
                            id: user.uid,
                            teamRole: playerData.teamRole,
                            name: playerData.name
                          }]
                        }}
                        teams={playerData.allTeams?.map(t => ({
                          id: t.teamId,
                          name: t.teamName,
                          whatsappGroupLink: t.whatsappGroupLink || t.whatsAppLink || playerData.teamWhatsAppLink,
                          players: [{
                            userId: user.uid,
                            id: user.uid,
                            teamRole: t.teamRole || (t.teamId === playerData.teamId ? playerData.teamRole : 'player'),
                            name: playerData.name
                          }]
                        })) || []}
                        players={Object.entries(allClubMembers).map(([id, data]) => ({ id, ...data }))}
                        games={allClubGames}
                        selectedPlayers={[]}
                        onBack={() => setShowWhatsAppMessenger(false)}
                        userRole="captain"
                        userId={user.uid}
                        tournaments={tournaments}
                        expenses={expenses}
                        walletTransactions={walletTransactions}
                        clubWhatsAppLink={clubInfo?.whatsappGroupLink || ''}
                        onUpdateGroupLink={async (teamId, link) => {
                          try {
                            const teamRef = doc(db, 'teams', teamId);
                            await updateDoc(teamRef, {
                              whatsappGroupLink: link,
                              updatedAt: new Date().toISOString()
                            });

                            // Update local state if it's the primary team
                            if (teamId === playerData?.teamId) {
                              setPlayerData(prev => ({ ...prev, teamWhatsAppLink: link }));
                              setWhatsAppLink(link);
                            }

                            // Also update within allTeams array
                            if (playerData?.allTeams) {
                              setPlayerData(prev => ({
                                ...prev,
                                allTeams: prev.allTeams.map(t =>
                                  t.teamId === teamId ? { ...t, whatsappGroupLink: link } : t
                                )
                              }));
                            }
                          } catch (error) {
                            console.error('Error saving WhatsApp link:', error);
                            throw error;
                          }
                        }}
                      />
                    </div>
                  </div>
                )
              }

              {/* Camera Capture Modal */}
              {
                showCameraCapture && (
                  <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center p-4">
                    <div className="w-full max-w-2xl">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-white text-xl font-bold">Capture Receipt</h2>
                        <button
                          onClick={stopCameraCapture}
                          className="text-white hover:text-gray-300"
                        >
                          <X className="w-8 h-8" />
                        </button>
                      </div>

                      <div className="relative bg-black rounded-lg overflow-hidden">
                        <video
                          id="camera-video"
                          autoPlay
                          playsInline
                          ref={(video) => {
                            if (video && cameraStream) {
                              video.srcObject = cameraStream;
                            }
                          }}
                          className="w-full h-auto"
                        />
                      </div>

                      <div className="mt-6 flex justify-center gap-4">
                        <button
                          onClick={stopCameraCapture}
                          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={capturePhoto}
                          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Capture Photo
                        </button>
                      </div>
                    </div>
                  </div>
                )
              }

                  {/* Balance Due Modal */}
                  {
                    showBalanceDueModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                          <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
                            <div>
                              <h2 className="text-xl font-bold text-gray-900">My Balance</h2>
                              <p className="text-sm text-gray-500">Complete financial breakdown</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowBalanceDueModal(false)}
                              className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                              ×
                            </button>
                          </div>

                          <div className="p-6">
                            {(() => {
                              const breakdown = getBalanceDueBreakdown();
                              const payments = walletTransactions.filter(t =>
                                t.type === 'payment' && t.status === 'approved' && t.amount < 0 && !t.isDonation // Exclude donations
                              );
                              const waivers = walletTransactions.filter(t =>
                                t.type === 'waiver' && t.status === 'approved' && t.amount < 0
                              );
                              const displayWaivers = waivers.filter(t =>
                                !t.description?.toLowerCase().includes('credit offset') &&
                                !t.waiverReason?.toLowerCase().includes('credit offset')
                              );
                              const {
                                approvedExpenses,
                                reimbursementsReceived
                              } = getPlayerReimbursementDetails();

                              // Group by type
                              const registrationItems = breakdown.items.filter(i => i.type === 'registration');
                              const gameItems = breakdown.items.filter(i => i.type === 'game_expense');
                              const tournamentFeeItems = breakdown.items.filter(i => i.type === 'tournament_fee');

                              return (
                                <div className="space-y-6">
                                  {/* Fees Owed Section */}
                                  <div>
                                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                      Fees Owed
                                    </h3>

                                    {breakdown.items.length === 0 ? (
                                      <p className="text-gray-500 text-sm italic ml-5">No fees owed</p>
                                    ) : (
                                      <div className="space-y-2 ml-5">
                                        {/* Initial Donations */}
                                        {registrationItems.map(item => (
                                          <div key={item.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                            <div>
                                              <p className="font-medium text-gray-800">{item.description}</p>
                                              {item.date && (
                                                <p className="text-xs text-gray-500">
                                                  {formatDate(item.date)}
                                                </p>
                                              )}
                                            </div>
                                            <span className="font-bold text-red-700">
                                              {item.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
                                            </span>
                                          </div>
                                        ))}

                                        {/* Tournament Fees */}
                                        {tournamentFeeItems.map(item => (
                                          <div key={item.id} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                                            <div>
                                              <p className="font-medium text-gray-800 flex items-center gap-2">
                                                🏆 {item.description}
                                              </p>
                                              <p className="text-xs text-gray-500">
                                                {item.gameName ? item.gameName : item.gamesPlayed && `${item.gamesPlayed} game${item.gamesPlayed !== 1 ? 's' : ''} played`}
                                                {(item.gameName || item.gamesPlayed) && item.date && ' · '}
                                                {item.date && formatDate(item.date)}
                                              </p>
                                            </div>
                                            <span className="font-bold text-purple-700">
                                              {item.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
                                            </span>
                                          </div>
                                        ))}

                                        {/* Game Expenses */}
                                        {gameItems.map(item => (
                                          <div key={item.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                            <div>
                                              <p className="font-medium text-gray-800">{item.description}</p>
                                              <p className="text-sm text-gray-600">{item.gameName}</p>
                                              {item.isSuperSub && (
                                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                                  ⭐ Super Sub (50% rate)
                                                </span>
                                              )}
                                            </div>
                                            <span className="font-bold text-red-700">
                                              {item.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
                                            </span>
                                          </div>
                                        ))}

                                        {/* Total */}
                                        <div className="flex justify-between items-center p-2 border-t mt-2">
                                          <span className="text-sm text-gray-600">Total Owed</span>
                                          <span className="font-semibold text-red-600">{formatCurrency(breakdown.total)}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Payments Made Section */}
                                  <div>
                                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                                      Payments Made
                                    </h3>

                                    {payments.length === 0 ? (
                                      <p className="text-gray-500 text-sm italic ml-5">No payments recorded</p>
                                    ) : (
                                      <div className="space-y-2 ml-5">
                                        {payments.map(payment => (
                                          <div key={payment.id} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                            <div>
                                              <p className="font-medium text-gray-800">{payment.description || 'Payment'}</p>
                                              <p className="text-xs text-gray-500">
                                                {payment.paymentDate
                                                  ? formatDate(payment.paymentDate)
                                                  : payment.createdAt
                                                    ? formatDate(payment.createdAt)
                                                    : ''}
                                                {payment.paymentMethod && ` • via ${payment.paymentMethod}`}
                                              </p>
                                            </div>
                                            <span className="font-bold text-green-700">-{formatCurrency(Math.abs(payment.amount))}</span>
                                          </div>
                                        ))}

                                        {/* Subtotal */}
                                        <div className="flex justify-between items-center p-2 border-t mt-2">
                                          <span className="text-sm text-gray-600">Total Paid</span>
                                          <span className="font-semibold text-green-600">-{formatCurrency(amountPaid)}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Waivers Section (if any) */}
                                  {displayWaivers.length > 0 && (
                                    <div>
                                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                                        Fee Waivers
                                      </h3>

                                      <div className="space-y-2 ml-5">
                                        {displayWaivers.map(waiver => (
                                          <div key={waiver.id} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                                            <div>
                                              <p className="font-medium text-gray-800">{waiver.description || 'Fee Waived'}</p>
                                              <p className="text-xs text-gray-500">
                                                {waiver.createdAt ? formatDate(waiver.createdAt) : ''}
                                                {waiver.waiverReason && ` - ${waiver.waiverReason}`}
                                              </p>
                                            </div>
                                            <span className="font-bold text-purple-700">-{formatCurrency(Math.abs(waiver.amount))}</span>
                                          </div>
                                        ))}

                                        {/* Subtotal */}
                                        <div className="flex justify-between items-center p-2 border-t mt-2">
                                          <span className="text-sm text-gray-600">Total Waived</span>
                                          <span className="font-semibold text-purple-600">
                                            -{formatCurrency(displayWaivers.reduce((sum, w) => sum + Math.abs(w.amount), 0))}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Approved Expenses Section (if any) */}
                                  {approvedExpenses.length > 0 && (
                                    <div>
                                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                                        Approved Expenses (Paid Upfront)
                                      </h3>

                                      <div className="space-y-2 ml-5">
                                        {approvedExpenses.map(item => (
                                          <div key={item.id} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                            <div>
                                              <p className="font-medium text-gray-800">{item.description || 'Approved Expense'}</p>
                                              {item.gameName && (
                                                <p className="text-xs text-gray-600">
                                                  {item.gameName}
                                                </p>
                                              )}
                                              {item.createdAt && (
                                                <p className="text-xs text-gray-500">
                                                  {formatDate(item.createdAt)}
                                                </p>
                                              )}
                                            </div>
                                            <span className="font-bold text-blue-700">+{formatCurrency(Math.abs(item.amount))}</span>
                                          </div>
                                        ))}

                                        {/* Subtotal */}
                                        <div className="flex justify-between items-center p-2 border-t mt-2">
                                          <span className="text-sm text-gray-600">Total Approved Expenses</span>
                                          <span className="font-semibold text-blue-600">
                                            +{formatCurrency(approvedExpenses.reduce((sum, t) => sum + (t.amount || 0), 0))}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Reimbursements Received Section (if any) */}
                                  {reimbursementsReceived.length > 0 && (
                                    <div>
                                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                                        Reimbursements Received (Payouts)
                                      </h3>

                                      <div className="space-y-2 ml-5">
                                        {reimbursementsReceived.map(item => (
                                          <div key={item.id} className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                                            <div>
                                              <p className="font-medium text-gray-800">{item.description || 'Reimbursement Payout'}</p>
                                              {item.createdAt && (
                                                <p className="text-xs text-gray-500">
                                                  {formatDate(item.createdAt)}
                                                </p>
                                              )}
                                            </div>
                                            <span className="font-bold text-amber-700">-{formatCurrency(Math.abs(item.amount))}</span>
                                          </div>
                                        ))}

                                        {/* Subtotal */}
                                        <div className="flex justify-between items-center p-2 border-t mt-2">
                                          <span className="text-sm text-gray-600">Total Payouts</span>
                                          <span className="font-semibold text-amber-700">
                                            -{formatCurrency(reimbursementsReceived.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0))}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Net Balance Summary */}
                                  <div className="border-t pt-4">
                                    <div className={`flex justify-between items-center p-4 rounded-lg ${netBalance > 0
                                      ? 'bg-red-100'
                                      : netBalance < 0
                                        ? 'bg-blue-100'
                                        : 'bg-green-100'
                                      }`}>
                                      <span className="font-bold text-gray-800">
                                        {netBalance > 0
                                          ? 'Total Balance Due'
                                          : netBalance < 0
                                            ? 'Credit Balance (Club Owes You)'
                                            : 'Balance'}
                                      </span>
                                      <span className={`text-2xl font-bold ${netBalance > 0
                                        ? 'text-red-600'
                                        : netBalance < 0
                                          ? 'text-blue-600'
                                          : 'text-green-600'
                                        }`}>
                                        {formatCurrency(Math.abs(netBalance))}
                                      </span>
                                    </div>

                                    {Math.abs(netBalance) <= 0.01 && (
                                      <div className="text-center mt-4">
                                        <CheckCircle className="mx-auto text-green-500 mb-2" size={40} />
                                        <p className="text-green-600 font-semibold">All Settled! ✓</p>
                                      </div>
                                    )}

                                    <div className="mt-4 text-center">
                                      {!isClubArchivedForUser && (
                                        <button
                                          onClick={() => {
                                            setShowBalanceDueModal(false);
                                            setPaymentAmount(netBalance > 0 ? netBalance.toString() : '');
                                            setShowPaymentQR(true);
                                          }}
                                          className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                                        >
                                          Pay Now
                                        </button>
                                      )}
                                    </div>

                                    <p className="text-xs text-gray-500 mt-3 text-center">
                                      * Balance updates automatically when payments are approved
                                    </p>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )
                  }

                  {/* Wallet Balance Modal - Approved Expenses */}
                  {
                    showWalletModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                          <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
                            <div>
                              <h2 className="text-xl font-bold text-gray-900">Approved Expenses</h2>
                              <p className="text-sm text-gray-500">Expenses you submitted that were approved</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowWalletModal(false)}
                              className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                              ×
                            </button>
                          </div>

                          <div className="p-6">
                            {(() => {
                              const approvedExpenses = getApprovedExpenses();

                              if (approvedExpenses.length === 0) {
                                return (
                                  <div className="text-center py-8">
                                    <Wallet className="mx-auto text-gray-300 mb-3" size={48} />
                                    <p className="text-gray-600">No approved expenses yet</p>
                                    <p className="text-sm text-gray-400 mt-1">Expenses you submit will appear here once approved</p>
                                  </div>
                                );
                              }

                              const totalApproved = approvedExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

                              return (
                                <div className="space-y-4">
                                  {approvedExpenses.map(expense => (
                                    <div key={expense.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <p className="font-semibold text-gray-800">{expense.description}</p>
                                          <p className="text-sm text-gray-600">
                                            {expense.gameName || 'General Expense'}
                                          </p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            Submitted: {formatDate(expense.createdAt)}
                                          </p>
                                          {expense.approvedAt && (
                                            <p className="text-xs text-green-600">
                                              Approved: {formatDate(expense.approvedAt)}
                                            </p>
                                          )}
                                          {expense.approvalNote && (
                                            <p className="text-xs text-gray-500 italic mt-1">
                                              Note: "{expense.approvalNote}"
                                            </p>
                                          )}
                                        </div>
                                        <div className="text-right">
                                          <span className="font-bold text-green-700 text-lg">{formatCurrency(expense.amount)}</span>
                                          <div className="mt-1">
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                              ✓ Approved
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      {expense.receiptURL && (
                                        <a
                                          href={expense.receiptURL}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                                        >
                                          View Receipt →
                                        </a>
                                      )}
                                    </div>
                                  ))}

                                  {/* Total */}
                                  <div className="border-t pt-4">
                                    <div className="flex justify-between items-center p-4 bg-green-100 rounded-lg">
                                      <span className="font-bold text-gray-800">Total Approved</span>
                                      <span className="text-2xl font-bold text-green-600">{formatCurrency(totalApproved)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )
                  }
                  {/* Pending Expenses Modal */}
                  {
                    showPendingExpensesModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                          <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
                            <div>
                              <h2 className="text-xl font-bold text-gray-900">Pending Expenses</h2>
                              <p className="text-sm text-gray-500">Expenses awaiting admin approval</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowPendingExpensesModal(false)}
                              className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                              ×
                            </button>
                          </div>

                          <div className="p-6">
                            {(() => {
                              const pendingExpenses = getPendingExpenses();

                              if (pendingExpenses.length === 0) {
                                return (
                                  <div className="text-center py-8">
                                    <CheckCircle className="mx-auto text-green-500 mb-3" size={48} />
                                    <p className="text-gray-600">No pending expenses</p>
                                    <p className="text-sm text-gray-400 mt-1">All your submitted expenses have been reviewed</p>
                                  </div>
                                );
                              }

                              const totalPending = pendingExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

                              return (
                                <div className="space-y-4">
                                  {pendingExpenses.map(expense => (
                                    <div key={expense.id} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <p className="font-semibold text-gray-800">{expense.description}</p>
                                          <p className="text-sm text-gray-600">
                                            {expense.gameName || 'General Expense'}
                                          </p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            Submitted: {formatDate(expense.createdAt)}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <span className="font-bold text-yellow-700 text-lg">{formatCurrency(expense.amount)}</span>
                                          <div className="mt-1">
                                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1">
                                              <Clock size={12} /> Pending
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      {expense.receiptURL && (
                                        <a
                                          href={expense.receiptURL}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                                        >
                                          View Receipt →
                                        </a>
                                      )}
                                    </div>
                                  ))}

                                  {/* Total */}
                                  <div className="border-t pt-4">
                                    <div className="flex justify-between items-center p-4 bg-yellow-100 rounded-lg">
                                      <span className="font-bold text-gray-800">Total Pending</span>
                                      <span className="text-2xl font-bold text-yellow-600">{formatCurrency(totalPending)}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2 text-center">
                                      Your balance will update once these are approved
                                    </p>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )
                  }

              {/* Footer */}
              <footer className="mt-12 py-6 border-t border-gray-200 text-center pb-20 md:pb-6">
                <p className="text-sm text-gray-600">
                  Copyright ©️ 2026 ClubHudl, All rights reserved
                </p>
              </footer>
            </div >
          </div >
        </PullToRefresh >
      </RegistrationGuard >
      </WaiverGuard>
      <PlayerMobileBottomNav
        view={view}
        setView={setView}
        playerData={playerData}
        isAdmin={isUserAdmin}
        isTreasurer={isUserTreasurer}
        onLogout={onLogout}
        setShowBalanceDueModal={setShowBalanceDueModal}
      />

      {/* Removed old modal */}
    </>
  );
};

export default PlayerDashboard;